import '@/modules/utility/ds-wms/styles/wmsTable.css';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ContractorProfile from './ContractorProfile';
import WorkProfileOverview from './WorkProfileOverview';
import WorkLifecycleTab from './WorkLifecycleTab';
import ContractorLink from '../ContractorLink';
import ContractorSelector from '../ContractorSelector';
import EmployeeSelector, { type SelectedEmployee } from '../EmployeeSelector';
import EngineerProfile from './EngineerProfile';
import TenderProfile from './TenderProfile';
import { worksApi, workGisApi, designsApi, estimatesApi, workScheduleApi, fundingPatternApi, documentConfigApi, attachmentApi, cstmMleApi, qcApi, inspectionsApi, ncrApi, contractsApi, type FundingPattern, type WorkDocGroup } from '../../services/wmsService';
import InspectionHistory from '../inspections/InspectionHistory';
import DocumentUploader from '../DocumentUploader';
import axiosInstance from '@/services/interceptor';
import DataGrid from '@/modules/utility/ds-wms/components/WmsDataGrid';
import SachivalayamSelector from '../SachivalayamSelector';
import GISParcelSelector from '../GISParcelSelector';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { BASE_LAYERS } from '../dashboard/MapSettings';
import { useWorkMasterList } from '../../hooks/useWorkMasterList';
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';
import { todayYmd, ymdLocal, toYmd } from '../../lib/dateUtils';
import { ListSkeleton, Skeleton, StatsSkeleton, TableSkeleton } from '@/components/base/Skeleton';
import { TILE_URLS } from '@/config/mapTiles';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';

// Fix for default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface WorkProfileProps {
  isOpen: boolean;
  onClose: () => void;
  workId?: string;
}

export default function WorkProfile({ isOpen, onClose, workId }: WorkProfileProps) {
  const [activeTab, setActiveTab] = useState('funding');
  // Landing view: the premium "Overview" dashboard (default) vs. the full
  // editable record (legacy dossier sheet + functional tabs).
  const [mainView, setMainView] = useState<'overview' | 'lifecycle' | 'record'>('overview');
  const [showContractorModal, setShowContractorModal] = useState(false);
  const [showContractorSelector, setShowContractorSelector] = useState(false);
  const [showEngineerModal, setShowEngineerModal] = useState(false);
  const [showEngineerSelector, setShowEngineerSelector] = useState(false);
  const [assigningEngineer, setAssigningEngineer] = useState(false);
  const [groundingWork, setGroundingWork] = useState(false);
  const [groundMsg, setGroundMsg] = useState<string | null>(null);
  const [showTenderProfile, setShowTenderProfile] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [apiData, setApiData] = useState<any>(null);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  // Wraps the premium Overview so its Download PDF can snapshot it to a file.
  const overviewRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  // Sachivalayam mapping state
  const [sachivalayams, setSachivalayams] = useState<{ svm_id: number; svm_cd: string; svm_nm: string }[]>([]);
  const [showSvmSelector, setShowSvmSelector] = useState(false);
  const [savingSvm, setSavingSvm] = useState(false);
  // Active section in the profile's left menu (must sit with the other hooks,
  // above the early `if (!isOpen) return null` — moving it below would break
  // the Rules of Hooks and blank the page).
  const [activeProfileSection, setActiveProfileSection] = useState('sec-location');

  // Header work-name "View more": long names clamp to 2 lines on screen with a
  // toggle to reveal the full text. nameClamped is measured (in the clamped state)
  // after render. The PDF/print exports always render the full name regardless.
  const [nameExpanded, setNameExpanded] = useState(false);
  const [nameClamped, setNameClamped] = useState(false);
  const nameRef = useRef<HTMLHeadingElement>(null);

  // Master dropdowns from centralized store
  const masterData = useWorkMasterList();
  const dropdowns: Record<string, any[]> = {
    workTypes: masterData.workTypes,
    statuses: masterData.workStatuses,
    schemes: masterData.schemes,
    wards: masterData.wards as any[],
    departments: masterData.departments,
    designations: masterData.designations,
  };

  // Fetch work data from API
  useEffect(() => {
    if (!isOpen || !workId) return;
    let cancelled = false;
    setIsLoading(true);
    worksApi.getById(workId).then((data) => {
      if (!cancelled) {
        setApiData(data);
        setIsLoading(false);
      }
    }).catch((err) => {
      console.error('Failed to fetch work details:', err);
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [isOpen, workId]);

  // Measure whether the (clamped) header work-name overflows 2 lines, so the
  // "View more" toggle only appears when it's actually needed.
  useEffect(() => {
    setNameExpanded(false);
    const id = requestAnimationFrame(() => {
      const el = nameRef.current;
      if (el) setNameClamped(el.scrollHeight > el.clientHeight + 2);
    });
    return () => cancelAnimationFrame(id);
  }, [apiData?.workName, isOpen]);

  // Fetch sachivalayam mappings
  useEffect(() => {
    if (!isOpen || !workId) return;
    setSachivalayams([]);
    axiosInstance.get(`ds-wms/sachivalayam-mapping/work/${workId}`)
      .then(res => setSachivalayams(res.data.data || []))
      .catch(() => setSachivalayams([]));
  }, [isOpen, workId]);

  // Fetch schedule milestones and finance schedule for Current Status
  const [scheduleMilestones, setScheduleMilestones] = useState<any[]>([]);
  const [scheduleId, setScheduleId] = useState<number | null>(null);
  const [financeEntries, setFinanceEntries] = useState<any[]>([]);
  useEffect(() => {
    if (!isOpen || !workId) return;
    setScheduleMilestones([]);
    setScheduleId(null);
    setFinanceEntries([]);
    // Load schedule with milestones
    axiosInstance.get(`ds-wms/works/${workId}/schedule`)
      .then(res => {
        const sched = res.data?.data;
        if (sched?.milestones) setScheduleMilestones(sched.milestones);
        if (sched?.wrk_schd_id) {
          setScheduleId(sched.wrk_schd_id);
          // Load finance schedule
          axiosInstance.get(`ds-wms/work-schedules/${sched.wrk_schd_id}/finance-schedule`)
            .then(fres => setFinanceEntries(fres.data?.data || []))
            .catch(() => {});
          // Sub-component custom field defs/values are loaded inside
          // ProgressUpdatesTab (keeps state in scope for the JSX that uses it).
        }
      })
      .catch(() => {});
  }, [isOpen, workId]);

  // ===== Site Photos gallery =====
  // One unified feed of every progress photo for the work, regardless of which
  // screen captured it. The backend (/works/:id/photos) unions the three upload
  // paths — milestone Progress Updates (wrk_prgrs_dtl), BOQ/abstract progress
  // (wrk_boq_media) and work component images (WRK_CMPNT_IMG) — so nothing is
  // missed just because it wasn't recorded through the milestone tab. Keyed on
  // workId (not scheduleId) so it also works for works without a schedule.
  type GalleryPhoto = { id: number; dt: string | null; milestone: string; remark: string; source: string };
  const [galleryPhotos, setGalleryPhotos] = useState<GalleryPhoto[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryPreview, setGalleryPreview] = useState<{ url: string; caption: string } | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  // Reusable so the Progress Updates tab can refresh the gallery after a new
  // upload (the effect alone only fires on workId change → would otherwise stay
  // stale until the modal is reopened).
  const SOURCE_LABEL: Record<string, string> = { milestone: 'Milestone', boq: 'BOQ Progress', component: 'Site Photo' };
  const loadGallery = useCallback(async () => {
    if (!workId) { setGalleryPhotos([]); return; }
    setGalleryLoading(true);
    try {
      const rows = await attachmentApi.listWorkPhotos(workId);
      const all: GalleryPhoto[] = (Array.isArray(rows) ? rows : []).map((a) => ({
        id: a.attchmnt_id,
        dt: a.captured_dt || a.i_ts || null,
        milestone: a.source_label || SOURCE_LABEL[a.source] || 'Progress',
        remark: '',
        source: a.source,
      }));
      // Server already orders newest-first; keep a defensive client sort.
      all.sort((a, b) => new Date(b.dt || 0).getTime() - new Date(a.dt || 0).getTime());
      setGalleryPhotos(all);
    } catch {
      setGalleryPhotos([]);
    } finally {
      setGalleryLoading(false);
    }
  }, [workId]);
  useEffect(() => { loadGallery(); }, [loadGallery]);

  const handleSvmSave = async (svmIds: number[]) => {
    if (!workId) return;
    setSavingSvm(true);
    try {
      await axiosInstance.put(`ds-wms/sachivalayam-mapping/work/${workId}`, { svmIds });
      // Refresh mappings
      const res = await axiosInstance.get(`ds-wms/sachivalayam-mapping/work/${workId}`);
      setSachivalayams(res.data.data || []);
      setShowSvmSelector(false);
    } catch (e) {
      console.error('Failed to save sachivalayam mapping:', e);
    } finally {
      setSavingSvm(false);
    }
  };

  const updateIndicator = useCallback(() => {
    if (!tabsContainerRef.current) return;
    const activeButton = tabsContainerRef.current.querySelector(`[data-tab="${activeTab}"]`) as HTMLButtonElement;
    if (activeButton) {
      const containerRect = tabsContainerRef.current.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      setIndicatorStyle({
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width,
      });
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(updateIndicator, 50);
    window.addEventListener('resize', updateIndicator);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateIndicator);
    };
  }, [updateIndicator, isOpen, isLoading]);

  if (!isOpen) return null;

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const enterEditMode = () => {
    const cf = apiData?.customFields || {};
    setEditData({
      wrk_nm: apiData?.workName || '',
      dscn_tx: apiData?.description || '',
      wrk_type_id: apiData?.type?.id || '',
      wrk_sts_id: apiData?.status?.id || '',
      schme_id: apiData?.scheme?.id || '',
      lctn_tx: apiData?.location || '',
      ward_nm: apiData?.wardName || '',
      orgnl_estmt_at: apiData?.originalEstimate || 0,
      snctn_at: apiData?.sanctionAmount || 0,
      rvsd_estmt_at: apiData?.revisedEstimate || 0,
      snctn_dt: apiData?.sanctionDate || '',
      agrmnt_dt: apiData?.agreementDate || '',
      strt_dt: apiData?.startDate || '',
      orgnl_cmpltn_dt: apiData?.originalCompletionDate || '',
      rvsd_cmpltn_dt: apiData?.revisedCompletionDate || '',
      cstm_flds_jn: {
        executing_agency: cf.executing_agency || '',
        contractor_contact_person: cf.contractor_contact_person || '',
        contractor_phone: cf.contractor_phone || '',
        contractor_email: cf.contractor_email || '',
        contractor_gst: cf.contractor_gst || '',
        assigned_engineer: cf.assigned_engineer || '',
        engineer_designation: cf.engineer_designation || '',
        engineer_department: cf.engineer_department || '',
        engineer_phone: cf.engineer_phone || '',
        engineer_email: cf.engineer_email || '',
        expenditure: cf.expenditure || 0,
        pending_bills: cf.pending_bills || 0,
        last_inspection_date: cf.last_inspection_date || '',
      },
    });
    setIsEditMode(true);
  };

  const cancelEditMode = () => {
    setEditData({});
    setIsEditMode(false);
  };

  const handleSave = async () => {
    // Use the numeric DB id (wrk_id) from apiData, not the display code (workCode)
    const numericId = apiData?.workId;
    if (!numericId) return;
    setIsSaving(true);
    try {
      const updated = await worksApi.update(numericId, editData);
      setApiData(updated);
      setIsEditMode(false);
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Assign an engineer directly from the read-only view (without entering full
  // edit mode). Engineer details live in the work's custom-fields JSON, so we
  // merge the picked employee into the existing cstm_flds_jn and persist.
  const handleAssignEngineer = async (emp: SelectedEmployee) => {
    const numericId = apiData?.workId;
    if (!numericId) return;
    setAssigningEngineer(true);
    try {
      const existingCf = apiData?.customFields || {};
      const updated = await worksApi.update(numericId, {
        cstm_flds_jn: {
          ...existingCf,
          assigned_engineer: emp.employee_name,
          engineer_designation: emp.designation_name || '',
          engineer_phone: emp.phone || '',
          engineer_email: emp.email || '',
        },
      } as any);
      setApiData(updated);
      setShowEngineerSelector(false);
    } catch (err) {
      console.error('Failed to assign engineer:', err);
    } finally {
      setAssigningEngineer(false);
    }
  };

  /**
   * Ground this work: record that it has physically started on site.
   *
   * Only offered from AWARDED, which is the one state the WORK workflow allows
   * GROUNDED from — the server refuses anything else with a 409 rather than trusting
   * the button. Grounding also moves the work's APPROVED designs to GROUNDED in the
   * same transaction, which is what fills the Grounded column on the Designs screen.
   */
  const handleGroundWork = async () => {
    const numericId = apiData?.workId;
    if (!numericId) return;
    setGroundingWork(true);
    try {
      const res = await worksApi.ground(numericId);
      const grounded = res?.data?.designsGrounded ?? 0;
      const fresh = await worksApi.getById(numericId);
      setApiData(fresh);
      setGroundMsg(grounded > 0
        ? `Work grounded. ${grounded} approved design(s) grounded with it.`
        : 'Work grounded.');
    } catch (err: any) {
      const msg = err?.response?.data?.message || '';
      // Already grounded is information, not an error — re-read so the button goes.
      if (/already/i.test(msg) && /grounded/i.test(msg)) {
        try { setApiData(await worksApi.getById(numericId)); } catch { /* keep what we have */ }
        setGroundMsg('This work is already grounded.');
      } else {
        // The server's refusal is the useful message — e.g. the work is not AWARDED.
        setGroundMsg(msg || 'Could not ground this work');
      }
    } finally {
      setGroundingWork(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const updateCustomField = (field: string, value: any) => {
    setEditData(prev => ({
      ...prev,
      cstm_flds_jn: { ...prev.cstm_flds_jn, [field]: value },
    }));
  };

  // Shared tile styling for the unified work-profile sheet — a dense grid of
  // label-on-top / value-below tiles that fills the full width (no label→value gaps,
  // no empty columns).
  // One field row: LABEL on the left (fixed width) and VALUE beside it, sharing
  // the same baseline. Two of these sit per grid row, so each section reads as a
  // clean, aligned two-column sheet. `wide` spans the full row (titles, address,
  // description). The fixed label column + the grid's column gap keep adjacent
  // fields from ever colliding.
  const TILE = "flex items-baseline gap-3.5 py-2.5 border-b border-slate-100 dark:border-gray-700/60";
  const TILE_LABEL = "w-28 sm:w-36 flex-shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 leading-tight pt-0.5";

  // Read-only value tile (used for non-editable fields).
  const tile = (label: string, children: React.ReactNode, opts?: { wide?: boolean; valueClass?: string }) => (
    <div className={`${TILE} ${opts?.wide ? 'sm:col-span-2' : ''}`}>
      <span className={TILE_LABEL}>{label}:</span>
      <div className={`flex-1 min-w-0 text-sm font-semibold break-words ${opts?.valueClass || 'text-slate-900 dark:text-slate-100'}`}>{children}</div>
    </div>
  );

  // Editable field helper — same label-left / value-right row, so the layout
  // never shifts between view and edit mode.
  const EditableField = ({ label, value, field, type = 'text', options, isCustom, colorClass = 'text-slate-800 dark:text-slate-100', optionKey, optionLabel, wide = false }: {
    label: string; value: string | number; field: string; type?: 'text' | 'number' | 'date' | 'select' | 'textarea';
    options?: any[]; isCustom?: boolean; colorClass?: string; optionKey?: string; optionLabel?: string; wide?: boolean;
  }) => {
    if (!isEditMode) {
      let display: React.ReactNode;
      if (type === 'date') display = formatDate(value as string) || '—';
      else if (type === 'number') display = formatCurrency(value as number);
      else display = (value as any) || '—';
      return tile(label, <span className={colorClass}>{display}</span>, { wide });
    }
    let currentValue = isCustom ? editData.cstm_flds_jn?.[field] : editData[field];
    const onChange = (v: any) => isCustom ? updateCustomField(field, v) : updateField(field, v);
    // Convert date strings to YYYY-MM-DD for input[type=date]
    if (type === 'date' && currentValue) {
      const d = new Date(currentValue);
      if (!isNaN(d.getTime())) currentValue = ymdLocal(d);
    }
    const inputClass = "w-full text-sm font-semibold border border-slate-300 dark:border-gray-600 rounded-lg px-2 py-1.5 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none bg-white dark:bg-gray-700 text-slate-900 dark:text-gray-100";

    let control: React.ReactNode;
    if (type === 'select') {
      const opts = options || [];
      const getKey = (opt: any) => optionKey ? opt[optionKey] : (
        opt.id ?? opt.workTypeId ?? opt.schemeId ?? opt.admn_unt_id
        ?? opt.wrk_type_id ?? opt.wrk_sts_id ?? opt.wrk_ctgry_id ?? opt.schme_id
        ?? opt.fy_id ?? opt.dpmt_id ?? opt.wrk_ntr_id
      );
      const getLabel = (opt: any) => optionLabel ? opt[optionLabel] : (
        opt.name ?? opt.workTypeName ?? opt.schemeName ?? opt.admn_unt_nm
        ?? opt.wrk_type_nm ?? opt.wrk_sts_nm ?? opt.wrk_ctgry_nm ?? opt.schme_nm
        ?? opt.fy_nm ?? opt.dpmt_nm ?? opt.wrk_ntr_nm
      );
      control = (
        <select value={currentValue || ''} onChange={e => onChange(e.target.value)} className={inputClass}>
          <option value="">{opts.length === 0 ? 'Loading...' : 'Select...'}</option>
          {opts.map((opt: any) => (
            <option key={getKey(opt)} value={getKey(opt)}>{getLabel(opt)}</option>
          ))}
        </select>
      );
    } else if (type === 'textarea') {
      control = <textarea value={currentValue || ''} onChange={e => onChange(e.target.value)} rows={2} className={inputClass} />;
    } else {
      control = <input type={type} value={currentValue ?? ''} onChange={e => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)} className={inputClass} />;
    }
    return (
      <div className={`${TILE} ${wide ? 'sm:col-span-2' : ''}`}>
        <span className={TILE_LABEL}>{label}:</span>
        <div className="flex-1 min-w-0">{control}</div>
      </div>
    );
  };

  // Section-header band for the unified work-profile sheet (Official Dossier look).
  // Plain function (not a component) so it never remounts on re-render.
  const sheetHead = ({ icon, label, right }: { icon: string; label: string; tint?: string; right?: React.ReactNode }) => {
    return (
      <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 border-b border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/60">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 ring-1 ring-indigo-100 dark:ring-indigo-800">
            <i className={`${icon} text-base`}></i>
          </span>
          <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-slate-700 dark:text-slate-200 truncate">{label}</h3>
        </div>
        {right}
      </div>
    );
  };

  // Left section-menu config for the profile sheet. Hovering/clicking an item
  // brings its section into focus; all sections stay rendered so the full
  // record is always visible.
  const profileSections = [
    { id: 'sec-location', label: 'Work Location', icon: 'ri-map-pin-2-fill' },
    { id: 'sec-details', label: 'Work Details', icon: 'ri-file-text-fill' },
    { id: 'sec-finance', label: 'Financials & Dates', icon: 'ri-money-rupee-circle-fill' },
    { id: 'sec-status', label: 'Current Status', icon: 'ri-pie-chart-2-fill' },
  ];
  // Smooth-scroll a section to the top of the scroll area and mark it active.
  const goToProfileSection = (id: string) => {
    setActiveProfileSection(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Map API response to component data structure
  const cf = apiData?.customFields || {};
  const workData = {
    workId: apiData?.workCode || workId || '',
    workName: apiData?.workName || '',
    workType: apiData?.type?.name || '',
    status: apiData?.status?.name || '',

    location: {
      district: apiData?.district || cf.district || '',
      municipality: apiData?.ulb || apiData?.zone || '',
      ward: apiData?.wardName || '',
      address: apiData?.location || '',
      gpsCoordinates: apiData?.lat && apiData?.lng ? `${apiData.lat}° N, ${apiData.lng}° E` : '',
    },

    contractor: {
      contractorId: apiData?.contractor?.contractorId || null,
      contractorCode: apiData?.contractor?.contractorCode || '',
      // contractor_from_ledger is set on works reconstructed from an accounting ledger,
      // where the firm that was paid is known but no agreement record exists to join
      // through. Last in the chain so a real contract always wins.
      name: apiData?.contractor?.name || cf.executing_agency || cf.contractor_name || cf.contractor_from_ledger || '',
      contactPerson: apiData?.contractor?.contactPerson || cf.contractor_contact_person || '',
      phone: apiData?.contractor?.phone || cf.contractor_phone || '',
      email: apiData?.contractor?.email || cf.contractor_email || '',
      registrationNo: apiData?.contractor?.registrationNo || cf.contractor_reg_no || '',
      gstNo: apiData?.contractor?.gstNo || cf.contractor_gst || '',
      address: apiData?.contractor?.address || '',
      city: apiData?.contractor?.city || '',
      msme: apiData?.contractor?.msme || false,
    },

    engineer: {
      name: cf.assigned_engineer || cf.engineer_name || '',
      designation: cf.engineer_designation || '',
      department: cf.engineer_department || '',
      phone: cf.engineer_phone || '',
      email: cf.engineer_email || '',
    },

    financial: (() => {
      // Some columns (snctn_at, cntrct_at, expenditure, paid_to_contractor) are stored in raw rupees,
      // while others (orgnl_estmt_at, rvsd_estmt_at) are stored in crores. Normalize everything to
      // crores at the source so downstream arithmetic and display stay consistent. The 1e5 threshold
      // separates the two regimes safely for WMS-scale projects.
      // Rupees -> crores, one divisor. See WorkProfileOverview.tsx for the full
      // account of why the previous `num >= 1e5 ? num / 1e7 : num` heuristic
      // rendered a Rs 90,000 BOQ line as "Rs 90,000.00 Cr". Every caller below
      // passes rupees; the crore-denominated paid_to_contractor is not read.
      const toCr = (v: any): number => (parseFloat(v) || 0) / 1e7;
      const tcvBase = apiData?.contractAmount || apiData?.sanctionAmount || 0;
      // Roll up milestone "Earned" values: each = Planned Amount × progress%.
      // Mirrors the per-milestone Earned card and stays consistent without
      // waiting on a separate progress-history fetch.
      const visibleMlstns = scheduleMilestones.filter(m => m.mlstn_cd !== 'WORK_SCHEDULE' && m.mlstn_cd !== 'WORK SCHEDULE');
      const sumPlanned = visibleMlstns.reduce((acc, m) => {
        const stored = m.plnd_amt_at;
        const planned = stored !== null && stored !== undefined
          ? Number(stored)
          : tcvBase > 0 ? Math.round(tcvBase * (Number(m.wghtg_pct_nu) || 0) / 100) : 0;
        return acc + (planned > 0 ? planned : 0);
      }, 0);
      const sumEarned = visibleMlstns.reduce((acc, m) => {
        const stored = m.plnd_amt_at;
        const planned = stored !== null && stored !== undefined
          ? Number(stored)
          : tcvBase > 0 ? Math.round(tcvBase * (Number(m.wghtg_pct_nu) || 0) / 100) : 0;
        return acc + Math.round(planned * (Number(m.prgrs_pct_nu) || 0) / 100);
      }, 0);
      // TCV: when milestones carry explicit Planned Amount overrides, those
      // are the authoritative project value (they may exceed the work's
      // sanctioned amount). Otherwise fall back to contract/sanction.
      const tcv = sumPlanned > 0 ? sumPlanned : tcvBase;
      // Prefer the milestone-rolled Work Completed when milestones carry a
      // planned amount; otherwise use the legacy tcv * physical% formula.
      const workCompleted = sumPlanned > 0
        ? sumEarned
        : tcv * ((apiData?.physicalProgress || 0) / 100);
      return {
        estimatedCost: toCr(apiData?.originalEstimate),
        sanctionedAmount: toCr(apiData?.sanctionAmount),
        releasedAmount: toCr(apiData?.revisedEstimate),
        /* From the bill register, not the Tally custom fields.
         *
         * cstm_flds_jn.expenditure / .pending_bills only exist on ledger-reconstructed
         * works, so on a real WMS work both read 0 and the profile showed "-" for
         * Expenditure while BILL-WRK/2026-27/0008-1 had been paid at Rs 9,890.16.
         * The server now resolves both from the bills themselves (paid vs unpaid) and
         * keeps the Tally figure as its own fallback, so ledger works are unaffected.
         *
         * `??` not `||`: a sanctioned work with no bills genuinely has 0 spent, and 0
         * must survive as an answer rather than falling through to the custom field. */
        expenditure: toCr(apiData?.expenditureAmount ?? cf.expenditure),
        pendingBills: toCr(apiData?.pendingBillsAmount ?? cf.pending_bills),
        tcv: toCr(tcv),
        workCompleted: toCr(workCompleted),
        /* apiData.paidAmount — the sum of this work's PAID bills, resolved server-side
         * (see the CASE in works.service.js getById).
         *
         * Reading cf.paid_rupees alone reported Rs 0 for every real WMS work, because
         * that field only exists on Tally-reconstructed rows: WRK/2026-27/0008 showed
         * Rs 0 against a settled bill of Rs 9,890.16.
         *
         * `??` and not `||`: for a sanctioned work with no bills yet the server sends
         * 0, and 0 is the correct answer — "nothing paid so far" is a fact. `||` would
         * treat it as missing and fall through to the ledger field, which for a
         * sanctioned work is absent anyway, so the figure would silently become 0 again
         * by a route that hides whether it was ever computed. Only a genuine null (no
         * bills AND no ledger amount) falls through, and toCr(undefined) is 0.
         *
         * The Tally fallback lives on the server, so paid_rupees is no longer read
         * here at all — one place decides what "paid" means. */
        paidToContractor: toCr(apiData?.paidAmount ?? cf.paid_rupees),
        pendingPayment: 0,
        pendingWorkdone: 0,
      };
    })(),

    dates: {
      sanctionDate: apiData?.sanctionDate || '',
      /* The CONTRACT's agreement date, with the work's own column only as a fallback.
       *
       * The two disagree on every contract in the system, and the contract is the
       * authoritative one — an agreement date is a property of the agreement:
       *
       *   WRK/2026-27/0004   work NULL          contract 10 Aug 2026
       *   WRK/2026-27/0008   work 18 Aug 2026   contract 17 Aug 2026
       *   WRK/2026-27/0012   work NULL          contract 20 Aug 2026
       *
       * For 0008 the evidence is conclusive: the LOA is dated 17 Aug and the contract
       * START date is 18 Aug, so w.agrmnt_dt was populated with the COMMENCEMENT date
       * and labelled "Agreement". The other two works simply showed a blank Agreement
       * Date although their contracts carried one.
       *
       * SCOPE — this is the profile's display only. WorkScheduleProfile keeps its own
       * source chain (workData.agrmnt_dt || ... || schedule.agrmnt_dt) untouched,
       * because that value anchors every milestone month in MonthlyMilestoneDrillDown
       * and is deliberately LATCHED there after a documented data-loss bug ("Save
       * reverts month + grouping + % + break"). Re-sourcing that chain to fix a
       * one-day display discrepancy would risk reopening it. Consequence, accepted
       * knowingly: on 0008 this card shows 17 Aug while the schedule stays anchored on
       * 18 Aug until the underlying columns are reconciled in the data.
       *
       * Nothing about SAVING changes — the edit payload above still seeds and writes
       * w.agrmnt_dt exactly as before. */
      agreementDate: apiData?.contract?.agreementDate || apiData?.agreementDate || '',
      commencementDate: apiData?.startDate || '',
      stipulatedCompletion: apiData?.originalCompletionDate || '',
      revisedCompletion: apiData?.revisedCompletionDate || '',
      lastInspection: cf.last_inspection_date || '',
    },

    physicalProgress: apiData?.physicalProgress || 0,
    financialProgress: apiData?.financialProgress || 0,

    landmarks: scheduleMilestones.filter(m => m.mlstn_cd !== 'WORK_SCHEDULE' && m.mlstn_cd !== 'WORK SCHEDULE').map(m => ({
      id: m.wrk_schd_mlstn_id,
      name: m.mlstn_nm,
      status: (m.prgrs_pct_nu || 0) >= 100 ? 'completed' : (m.prgrs_pct_nu || 0) > 0 ? 'in-progress' : 'pending',
      progress: m.prgrs_pct_nu || 0,
      coordinates: '',
      // Weightage drives the auto-derived Planned Amount shown in the UI.
      wghtg_pct_nu: Number(m.wghtg_pct_nu) || 0,
      // Stored Planned Amount override, if any (otherwise derived from weightage × TPC).
      plnd_amt_at: m.plnd_amt_at === null || m.plnd_amt_at === undefined ? null : Number(m.plnd_amt_at),
      // Designer-opt-in flag controlling Planned Amount visibility here.
      shw_plnd_amt_in_prgrs_in: !!m.shw_plnd_amt_in_prgrs_in,
      // Pass through the user-defined custom fields so the Progress Updates
      // form can render them per milestone.
      cstm_flds_jn: Array.isArray(m.cstm_flds_jn) ? m.cstm_flds_jn : [],
      // Sub-component instances picked for this milestone — needed so the
      // Progress Updates form can render per-sub custom fields below.
      sub_milestones: Array.isArray(m.sub_milestones) ? m.sub_milestones : [],
    })),
  };

  // Compute derived financial fields
  workData.financial.pendingPayment = Math.max(workData.financial.workCompleted - workData.financial.paidToContractor, 0);
  workData.financial.pendingWorkdone = Math.max(workData.financial.tcv - workData.financial.workCompleted, 0);

  const tabs = [
    { id: 'funding', label: 'Funding', icon: 'ri-money-rupee-circle-line' },
    { id: 'documents', label: 'Documents', icon: 'ri-file-list-3-line' },
    { id: 'design_boq', label: 'Design + BOQ', icon: 'ri-draft-line' },
    { id: 'progress', label: 'Progress Updates', icon: 'ri-line-chart-line' },
    { id: 'inspections', label: 'Inspections', icon: 'ri-search-eye-line' },
    // Quality Control lived only on its own screen (/ds-wms/progress/qc): a test
    // recorded there was invisible from the work it was recorded against, so an
    // engineer opening a work could not see whether its cubes had passed.
    { id: 'quality', label: 'Quality (QC/NCR)', icon: 'ri-flask-line' },
    { id: 'map', label: 'Map', icon: 'ri-map-pin-line' },
    // { id: 'workflows', label: 'Workflows', icon: 'ri-flow-chart' },
  ];

  // workData.financial.* values are already normalized to crores at the source (see `toCr` above).
  const formatCurrency = (amount: number) => {
    if (amount === null || amount === undefined) return '—';
    const num = Number(amount);
    if (!Number.isFinite(num) || num === 0) return '-';
    return `${num.toFixed(2)} Cr`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const safePercent = (a: number, b: number) => b > 0 ? Math.round((a / b) * 100) : 0;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in progress': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'delayed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'on hold': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Print/PDF Export function
  const handlePrintPDF = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    // Escape user-supplied strings before injecting into the print HTML.
    const esc = (v: any) => String(v ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
    // Embed the most recent site photos as data URLs (object URLs from this
    // window aren't reachable from the new print document, and the backend
    // needs the JWT — so fetch the bytes here and inline them). Cap the count
    // to keep the file size sane.
    const PDF_PHOTO_CAP = 12;
    const pdfPhotos = (await Promise.all(
      galleryPhotos.slice(0, PDF_PHOTO_CAP).map(async (p) => {
        try {
          const res = await axiosInstance.get(`ds-wms/attachments/${p.id}`, { responseType: 'blob' });
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result as string);
            fr.onerror = reject;
            fr.readAsDataURL(res.data as Blob);
          });
          return { ...p, dataUrl };
        } catch { return null; }
      })
    )).filter(Boolean) as Array<GalleryPhoto & { dataUrl: string }>;

    // Funding pattern (CAPEX/OPEX breakdown) — the default Funding tab, shown as
    // the last section of the PDF so the export carries the full work profile.
    const funding = apiData?.workId
      ? await fundingPatternApi.getByWorkId(apiData.workId).catch(() => null)
      : null;
    const fmtFund = (v: any) => { const n = parseFloat(v) || 0; return n > 0 ? `${n.toFixed(2)} Cr` : '-'; };
    const fcol = (cx: any, ox: any) => (parseFloat(cx) || 0) + (parseFloat(ox) || 0);

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Work Profile - ${workData.workId}</title>
        <style>
          @page { size: A4; margin: 15mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #333; font-size: 11px; line-height: 1.4; }
          .header { background: linear-gradient(135deg, #6d28d9, #7c3aed); color: white; padding: 15px 20px; margin-bottom: 15px; }
          .header h1 { font-size: 18px; margin-bottom: 3px; }
          .header p { font-size: 12px; opacity: 0.9; }
          .header .status { display: inline-block; background: white; color: #6d28d9; padding: 3px 10px; border-radius: 12px; font-size: 10px; font-weight: 600; margin-left: 10px; }
          .section { margin-bottom: 15px; page-break-inside: avoid; }
          .section-title { font-size: 13px; font-weight: 600; color: #1f2937; border-bottom: 2px solid #7c3aed; padding-bottom: 5px; margin-bottom: 10px; }
          .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
          .grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
          .card { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; background: #fafafa; }
          .card-title { font-size: 11px; font-weight: 600; color: #374151; margin-bottom: 8px; padding-bottom: 5px; border-bottom: 1px solid #e5e7eb; }
          .card-row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px dotted #e5e7eb; }
          .card-row:last-child { border-bottom: none; }
          .card-row .label { color: #6b7280; }
          .card-row .value { font-weight: 500; color: #1f2937; text-align: right; max-width: 60%; }
          .financial-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
          .financial-item { background: #f3f4f6; padding: 8px; border-radius: 4px; text-align: center; }
          .financial-item .label { font-size: 9px; color: #6b7280; }
          .financial-item .value { font-size: 12px; font-weight: 600; color: #1f2937; margin-top: 2px; }
          .financial-item .value.green { color: #059669; }
          .financial-item .value.blue { color: #2563eb; }
          .financial-item .value.orange { color: #d97706; }
          .financial-item .value.red { color: #dc2626; }
          .progress-section { margin-top: 15px; }
          .progress-bar-container { background: #e5e7eb; height: 16px; border-radius: 8px; overflow: hidden; margin: 5px 0; }
          .progress-bar { height: 100%; background: linear-gradient(90deg, #3b82f6, #2563eb); border-radius: 8px; }
          .progress-bar.green { background: linear-gradient(90deg, #22c55e, #16a34a); }
          .landmarks-table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 10px; }
          .landmarks-table th, .landmarks-table td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
          .landmarks-table th { background: #f3f4f6; font-weight: 600; }
          .landmarks-table tr:nth-child(even) { background: #fafafa; }
          .photo-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 10px; }
          .photo-cell { border: 1px solid #e5e7eb; border-radius: 4px; overflow: hidden; page-break-inside: avoid; }
          .photo-cell img { width: 100%; height: 95px; object-fit: cover; display: block; }
          .photo-cap { font-size: 8px; color: #6b7280; padding: 3px 5px; background: #fafafa; line-height: 1.3; }
          .photo-cap .pm { font-weight: 600; color: #374151; }
          .status-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 9px; font-weight: 500; }
          .status-completed { background: #dcfce7; color: #166534; }
          .status-in-progress { background: #fef3c7; color: #92400e; }
          .status-pending { background: #f3f4f6; color: #6b7280; }
          .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #e5e7eb; font-size: 9px; color: #6b7280; text-align: center; }
          .print-date { text-align: right; font-size: 9px; color: #6b7280; margin-bottom: 10px; }
          @media print {
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            .header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="print-date">Generated on: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>

        <div class="header">
          <h1>${workData.workName || workData.workId} <span class="status">${workData.status}</span></h1>
          <p>${workData.workId}</p>
        </div>

        <div class="section">
          <div class="card" style="margin-bottom: 12px;">
            <div class="card-title">📋 Work Details</div>
            <div class="card-row"><span class="label">Work Title</span><span class="value">${workData.workName}</span></div>
            ${apiData?.description ? `<div class="card-row"><span class="label">Description</span><span class="value">${apiData.description}</span></div>` : ''}
            ${workData.workType ? `<div class="card-row"><span class="label">Work Type/Sector</span><span class="value">${workData.workType}</span></div>` : ''}
            ${apiData?.scheme?.name ? `<div class="card-row"><span class="label">Scheme</span><span class="value">${apiData.scheme.name}</span></div>` : ''}
            ${apiData?.adminSanction?.goNumber ? `<div class="card-row"><span class="label">Admin Sanction GO</span><span class="value">${apiData.adminSanction.goNumber}</span></div>` : ''}
          </div>
        </div>

        <div class="section">
          <div class="grid-3">
            <div class="card">
              <div class="card-title">📍 Work Location</div>
              <div class="card-row"><span class="label">District</span><span class="value">${workData.location.district}</span></div>
              <div class="card-row"><span class="label">Municipality</span><span class="value">${workData.location.municipality}</span></div>
              <div class="card-row"><span class="label">Ward</span><span class="value">${workData.location.ward}</span></div>
              <div class="card-row"><span class="label">Address</span><span class="value">${workData.location.address}</span></div>
              <div class="card-row"><span class="label">GPS</span><span class="value">${workData.location.gpsCoordinates}</span></div>
              ${sachivalayams.length > 0 ? `<div class="card-row"><span class="label">Sachivalayams</span><span class="value">${esc(sachivalayams.map(s => `${s.svm_nm} (${s.svm_cd})`).join(', '))}</span></div>` : ''}
            </div>
            <div class="card">
              <div class="card-title">🏢 Contractor Details</div>
              <div class="card-row"><span class="label">Contractor</span><span class="value">${workData.contractor.name || '—'}</span></div>
              <div class="card-row"><span class="label">Phone</span><span class="value">${workData.contractor.phone || '—'}</span></div>
            </div>
            <div class="card">
              <div class="card-title">👷 Assigned Engineer</div>
              <div class="card-row"><span class="label">Name</span><span class="value">${workData.engineer.name || '—'}</span></div>
              <div class="card-row"><span class="label">Designation</span><span class="value">${workData.engineer.designation || '—'}</span></div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="grid-2">
            <div class="card">
              <div class="card-title">💰 Financial Details</div>
              <div class="financial-grid">
                <div class="financial-item">
                  <div class="label">Estimated Cost</div>
                  <div class="value">${formatCurrency(workData.financial.estimatedCost)}</div>
                </div>
                <div class="financial-item">
                  <div class="label">Sanctioned Amount</div>
                  <div class="value green">${formatCurrency(workData.financial.sanctionedAmount)}</div>
                </div>
                <div class="financial-item">
                  <div class="label">Released Amount</div>
                  <div class="value blue">${formatCurrency(workData.financial.releasedAmount)}</div>
                </div>
                <div class="financial-item">
                  <div class="label">Expenditure</div>
                  <div class="value orange">${formatCurrency(workData.financial.expenditure)}</div>
                </div>
                <div class="financial-item">
                  <div class="label">Pending Bills</div>
                  <div class="value red">${formatCurrency(workData.financial.pendingBills)}</div>
                </div>
                <div class="financial-item">
                  <div class="label">Balance</div>
                  <div class="value">${formatCurrency(workData.financial.releasedAmount - workData.financial.expenditure)}</div>
                </div>
              </div>
            </div>
            <div class="card">
              <div class="card-title">📅 Key Dates</div>
              <div class="financial-grid">
                <div class="financial-item">
                  <div class="label">Sanction Date</div>
                  <div class="value">${formatDate(workData.dates.sanctionDate)}</div>
                </div>
                <div class="financial-item">
                  <div class="label">Agreement Date</div>
                  <div class="value">${formatDate(workData.dates.agreementDate)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="section progress-section">
          <div class="section-title">Current Status</div>
          <div class="grid-2">
            <div class="card">
              <div class="card-title">Physical Progress: ${workData.physicalProgress}%</div>
              <div class="progress-bar-container">
                <div class="progress-bar" style="width: ${workData.physicalProgress}%"></div>
              </div>
              <p style="font-size: 9px; color: #6b7280; margin-top: 5px;">Started: ${formatDate(workData.dates.commencementDate)} | Target: ${formatDate(workData.dates.revisedCompletion)}</p>
            </div>
            <div class="card">
              <div class="card-title">Financial Progress</div>
              <div style="margin-bottom: 8px;">
                <div style="font-size: 9px; color: #6b7280;">Work Completed vs TCV: ${safePercent(workData.financial.workCompleted, workData.financial.tcv)}%</div>
                <div class="progress-bar-container" style="height: 10px;">
                  <div class="progress-bar" style="width: ${safePercent(workData.financial.workCompleted, workData.financial.tcv)}%"></div>
                </div>
              </div>
              <div>
                <div style="font-size: 9px; color: #6b7280;">Payment vs Work Completed: ${safePercent(workData.financial.paidToContractor, workData.financial.workCompleted)}%</div>
                <div class="progress-bar-container" style="height: 10px;">
                  <div class="progress-bar green" style="width: ${safePercent(workData.financial.paidToContractor, workData.financial.workCompleted)}%"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="card">
            <div class="card-title">💰 Contract Value & Payments</div>
            <div class="card-row"><span class="label">Total Contract Value (TCV)</span><span class="value">${formatCurrency(workData.financial.tcv)}</span></div>
            <div class="financial-grid" style="margin-top: 8px;">
              <div class="financial-item"><div class="label">Work Completed</div><div class="value">${formatCurrency(workData.financial.workCompleted)}</div></div>
              <div class="financial-item"><div class="label">Pending Workdone</div><div class="value red">${formatCurrency(workData.financial.pendingWorkdone)}</div></div>
              <div class="financial-item"><div class="label">Paid to Contractor</div><div class="value green">${formatCurrency(workData.financial.paidToContractor)}</div></div>
              <div class="financial-item"><div class="label">Pending Payment</div><div class="value orange">${formatCurrency(workData.financial.pendingPayment)}</div></div>
            </div>
            <div style="margin-top: 6px; font-size: 9px; color: #6b7280;">Paid: ${safePercent(workData.financial.paidToContractor, workData.financial.tcv)}% &nbsp;|&nbsp; Pending: ${safePercent(workData.financial.pendingPayment, workData.financial.tcv)}% of TCV</div>
          </div>
        </div>

        ${workData.landmarks.length > 0 ? `
        <div class="section">
          <div class="section-title">Landmark Progress</div>
          <table data-wms-table class="landmarks-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Landmark</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Coordinates</th>
              </tr>
            </thead>
            <tbody>
              ${workData.landmarks.map((landmark, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${landmark.name}</td>
                  <td><span class="status-badge status-${landmark.status}">${landmark.status.charAt(0).toUpperCase() + landmark.status.slice(1).replace('-', ' ')}</span></td>
                  <td>${landmark.progress}%</td>
                  <td>${landmark.coordinates}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div style="margin-top: 8px; font-size: 9px; color: #6b7280;">
            Summary: Completed: ${workData.landmarks.filter(l => l.status === 'completed').length} |
            In Progress: ${workData.landmarks.filter(l => l.status === 'in-progress').length} |
            Pending: ${workData.landmarks.filter(l => l.status === 'pending').length} |
            Total: ${workData.landmarks.length} landmarks
          </div>
        </div>` : ''}

        ${financeEntries.length > 0 ? `
        <div class="section">
          <div class="section-title">Finance Schedule</div>
          <table data-wms-table class="landmarks-table">
            <thead>
              <tr><th>S.No</th><th>Target Date</th><th>Planned Work %</th><th>Actual %</th><th>Planned Amount</th></tr>
            </thead>
            <tbody>
              ${financeEntries.map((fe, fi) => `
                <tr>
                  <td>${fi + 1}</td>
                  <td>${fe.target_dt ? formatDate(fe.target_dt) : '—'}</td>
                  <td>${fe.plnd_pct_nu || 0}%</td>
                  <td>${fe.actl_pct_nu || 0}%</td>
                  <td>₹${Number(fe.plnd_at || 0).toLocaleString('en-IN')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>` : ''}

        ${pdfPhotos.length > 0 ? `
        <div class="section">
          <div class="section-title">Site Photos${galleryPhotos.length > pdfPhotos.length ? ` — showing latest ${pdfPhotos.length} of ${galleryPhotos.length}` : ` (${pdfPhotos.length})`}</div>
          <div class="photo-grid">
            ${pdfPhotos.map(p => `
              <div class="photo-cell">
                <img src="${p.dataUrl}" alt="Site photo" />
                <div class="photo-cap"><span class="pm">${esc(p.milestone) || 'Progress'}</span><br/>${p.dt ? esc(formatDate(p.dt)) : ''}</div>
              </div>
            `).join('')}
          </div>
        </div>` : ''}

        ${funding ? `
        <div class="section">
          <div class="section-title">Funding Pattern</div>
          <table data-wms-table class="landmarks-table">
            <thead>
              <tr><th>Source</th><th>GoI</th><th>GoAP</th><th>UDA Share</th><th>15th FC</th><th>PPP</th><th>Total</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>CAPEX</td>
                <td>${fmtFund(funding.capex_goi_at)}</td><td>${fmtFund(funding.capex_goap_at)}</td>
                <td>${fmtFund(funding.capex_ulb_at)}</td><td>${fmtFund(funding.capex_15fc_at)}</td>
                <td>${fmtFund(funding.capex_ppp_at)}</td><td>${fmtFund(funding.capex_totl_at)}</td>
              </tr>
              <tr>
                <td>OPEX</td>
                <td>${fmtFund(funding.opex_goi_at)}</td><td>${fmtFund(funding.opex_goap_at)}</td>
                <td>${fmtFund(funding.opex_ulb_at)}</td><td>${fmtFund(funding.opex_15fc_at)}</td>
                <td>${fmtFund(funding.opex_ppp_at)}</td><td>${fmtFund(funding.opex_totl_at)}</td>
              </tr>
              <tr style="font-weight:600;background:#f3f4f6;">
                <td>Total</td>
                <td>${fmtFund(fcol(funding.capex_goi_at, funding.opex_goi_at))}</td>
                <td>${fmtFund(fcol(funding.capex_goap_at, funding.opex_goap_at))}</td>
                <td>${fmtFund(fcol(funding.capex_ulb_at, funding.opex_ulb_at))}</td>
                <td>${fmtFund(fcol(funding.capex_15fc_at, funding.opex_15fc_at))}</td>
                <td>${fmtFund(fcol(funding.capex_ppp_at, funding.opex_ppp_at))}</td>
                <td>${fmtFund(funding.totl_prjct_cst_at)}</td>
              </tr>
            </tbody>
          </table>
          <div style="margin-top: 4px; font-size: 9px; color: #6b7280;">All values in Cr.</div>
        </div>` : ''}

        <div class="footer">
          <p>WMS - Works Management System</p>
          <p>This is a computer-generated document. No signature required.</p>
        </div>
      </body>
      </html>
    `;

    try {
      // Print via a hidden iframe so the browser's PRINT dialog opens in-place
      // (choose "Save as PDF" there) instead of opening a whole new tab/file.
      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
      document.body.appendChild(iframe);
      const cleanup = () => { try { document.body.removeChild(iframe); } catch { /* already gone */ } };
      const doc = iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(printContent);
        doc.close();
        const fire = () => {
          try {
            iframe.contentWindow?.focus();
            if (iframe.contentWindow) iframe.contentWindow.onafterprint = () => setTimeout(cleanup, 200);
            iframe.contentWindow?.print();
          } catch { cleanup(); }
          // Safety net so the iframe never leaks if onafterprint doesn't fire.
          setTimeout(cleanup, 120000);
        };
        // Give inlined images a moment to lay out before the dialog opens.
        setTimeout(fire, 400);
      } else {
        cleanup();
      }
    } finally {
      setExportingPdf(false);
    }
  };

  // Download a real .pdf of the premium Overview (no print dialog). Rasterises
  // the live dashboard DOM with html2canvas, then slices the tall image into A4
  // pages with jsPDF and saves the file. Because it captures the rendered pixels
  // there is zero text overlap. The live Leaflet map is snapshotted separately and
  // embedded as an image; cross-origin thumbnails are swapped for placeholders;
  // and interactive buttons are dropped so the static PDF reads cleanly.
  const handleDownloadOverviewPdf = async () => {
    if (exportingPdf) return;
    const node = overviewRef.current;
    if (!node) { handlePrintPDF(); return; }
    setExportingPdf(true);

    // Snapshot the LIVE GIS map first (it's fully rendered on screen). html2canvas
    // with useCORS pulls OpenStreetMap tiles with CORS so the result isn't tainted;
    // we embed this PNG into the clone in place of the (un-cloneable) Leaflet canvas.
    let mapImg: string | null = null;
    const liveMap = node.querySelector<HTMLElement>('.leaflet-container');
    if (liveMap) {
      try {
        const mc = await html2canvas(liveMap, { useCORS: true, backgroundColor: '#e5e7eb', logging: false, scale: 2 });
        mapImg = mc.toDataURL('image/png');
      } catch { mapImg = null; }
    }

    // Render the clone at the SAME width as the on-screen overview so the PDF
    // reproduces the exact layout the user is viewing — identical responsive
    // breakpoints (column counts), card widths and (with the equal-height rows
    // kept intact below) card heights. Clamp to a sane desktop range so a very
    // narrow or ultra-wide modal still exports a readable page. Same document, so
    // the app stylesheets already apply.
    const measuredW = Math.round(node.getBoundingClientRect().width);
    const RENDER_W = Math.min(2000, Math.max(1024, measuredW || 1480));
    const host = document.createElement('div');
    host.style.cssText = `position:fixed;left:-10000px;top:0;width:${RENDER_W}px;background:#F8FAFC;z-index:-1;`;
    const clone = node.cloneNode(true) as HTMLElement;

    // Un-clip: drop fade transforms, expand scroll panes, stop truncation — so
    // the FULL layout is captured with nothing cut off.
    clone.querySelectorAll<HTMLElement>('*').forEach((el) => {
      el.style.transform = 'none';
      el.style.animation = 'none';
      el.style.transition = 'none';
      el.style.opacity = '1';
      const cl = el.classList;
      if (cl.contains('truncate')) { el.style.whiteSpace = 'normal'; el.style.overflow = 'visible'; el.style.textOverflow = 'clip'; }
      if (cl.contains('overflow-x-auto') || cl.contains('overflow-x-scroll')) {
        el.style.overflowX = 'visible';
        // A horizontal flex strip (the KPI row) must stay ONE row in the PDF. Drop the
        // fixed min-widths and let the cards share the page width equally so all fit.
        if (cl.contains('flex')) {
          el.style.flexWrap = 'nowrap';
          el.querySelectorAll<HTMLElement>('*').forEach((d) => { d.style.minWidth = '0'; });
          Array.from(el.children).forEach((c) => { (c as HTMLElement).style.flex = '1 1 0'; (c as HTMLElement).style.minWidth = '0'; });
        }
      }
      // Expand ONLY genuine vertical scroll panes (e.g. the Description table) so
      // their full content prints. Equal-height card rows (h-full + items-stretch)
      // and overflow-hidden are left untouched so the PDF reproduces the web view's
      // card widths and heights exactly instead of collapsing each card to its own
      // content height.
      if (cl.contains('overflow-y-auto') || cl.contains('overflow-y-scroll')) { el.style.overflowY = 'visible'; el.style.maxHeight = 'none'; }
      // html2canvas ignores -webkit-line-clamp, so clamped text overflows and overlaps
      // the rows below. Clear the clamp (inline OR Tailwind line-clamp-* class) so the
      // FULL text shows and its box grows to fit — nothing is cut in the PDF.
      const st = el.style as any;
      if (Array.from(cl).some((c) => c.startsWith('line-clamp-')) || st.webkitLineClamp || st.display === '-webkit-box') {
        st.display = 'block'; st.webkitLineClamp = ''; st.webkitBoxOrient = '';
        el.style.overflow = 'visible'; el.style.maxHeight = 'none'; el.style.whiteSpace = 'normal';
      }
      // html2canvas renders tight line-heights too high → text/pills overlap their
      // neighbours (header work code, KPI numbers, % badges). Relax to a normal box.
      if (cl.contains('leading-none') || el.style.lineHeight === '1' || el.style.lineHeight === '1px') {
        el.style.lineHeight = '1.25';
      }
      // Pill badges (rounded-full) overlap when their line box is shorter than the
      // text; give them a normal line-height so the label sits inside the pill.
      if (cl.contains('rounded-full')) el.style.lineHeight = '1.4';
    });

    // Cap long lists so the PDF doesn't sprawl (matches what the scrollable card shows).
    clone.querySelectorAll<HTMLElement>('[data-print-limit]').forEach((box) => {
      const lim = parseInt(box.getAttribute('data-print-limit') || '0', 10);
      const kids = Array.from(box.children) as HTMLElement[];
      if (lim > 0 && kids.length > lim) {
        kids.slice(lim).forEach((k) => k.remove());
        const note = document.createElement('div');
        note.textContent = `+ ${kids.length - lim} more — view the full list in the dashboard`;
        note.style.cssText = 'grid-column:1/-1;padding:8px 2px;font-size:11px;color:#6B7280;font-style:italic;';
        box.appendChild(note);
      }
    });

    // Put the snapshotted map image in place of the Leaflet container (or a neutral
    // placeholder if the snapshot failed). Keeps any overlay badge (e.g. "approximate").
    clone.querySelectorAll<HTMLElement>('.leaflet-container').forEach((m) => {
      m.querySelectorAll('.leaflet-pane,.leaflet-control-container,.leaflet-tile-container').forEach((n) => n.remove());
      const inner = mapImg
        ? `<img src="${mapImg}" style="width:100%;height:100%;object-fit:cover;display:block"/>`
        : '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#E5E7EB;color:#6B7280;font:600 12px sans-serif">Map view — open the dashboard for the live map</div>';
      m.insertAdjacentHTML('afterbegin', inner);
    });
    // Swap cross-origin <img> (loremflickr etc.) for a soft gradient; keep same-origin blob/data photos.
    clone.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
      const src = img.getAttribute('src') || '';
      if (src && !src.startsWith('blob:') && !src.startsWith('data:') && !src.startsWith(location.origin)) {
        const ph = document.createElement('div');
        ph.style.cssText = `width:100%;height:${img.height || 96}px;background:linear-gradient(135deg,#E0E7FF,#EDE9FE);border-radius:8px;`;
        img.replaceWith(ph);
      }
    });

    // Reveal print-only blocks (e.g. the "Work Name:" title), which are hidden on
    // screen but should appear in the PDF.
    clone.querySelectorAll<HTMLElement>('[data-print-only]').forEach((n) => { n.style.display = 'block'; });

    // Remove elements explicitly excluded from the PDF (e.g. the description
    // table's "Images" column — both its header cell and the per-row View cells).
    clone.querySelectorAll('[data-print-hide]').forEach((n) => n.remove());

    // Drop interactive controls from the static PDF (Download/Print/Open-Map header
    // buttons, "View Images", "View Larger Map", "view more", funding "Edit", etc.).
    // Photo tiles are <button>s wrapping an <img> — keep those; remove the rest.
    clone.querySelectorAll('button').forEach((b) => { if (!b.querySelector('img')) b.remove(); });
    clone.querySelectorAll('a').forEach((a) => { if (!a.querySelector('img')) a.remove(); });

    host.appendChild(clone);
    document.body.appendChild(host);
    const cleanup = () => { try { document.body.removeChild(host); } catch { /* gone */ } };

    try {
      // Let same-origin photos (blob URLs) finish decoding before the snapshot.
      await Promise.all(
        Array.from(clone.querySelectorAll('img')).map((img) =>
          img.complete ? Promise.resolve() : new Promise<void>((res) => { img.onload = () => res(); img.onerror = () => res(); setTimeout(res, 4000); })
        )
      );
      await new Promise((r) => setTimeout(r, 350));

      // Render at 3x for crisp text (192dpi → ~288dpi), capped so very tall
      // reports don't blow the canvas memory limit.
      const renderScale = Math.min(3, Math.max(2, (window.devicePixelRatio || 1) * 1.5));
      const canvas = await html2canvas(clone, {
        scale: renderScale,
        useCORS: true,
        backgroundColor: '#F8FAFC',
        windowWidth: RENDER_W,
        width: RENDER_W,
        logging: false,
        onclone: (doc) => {
          // Sharpen text in the snapshot: antialiased smoothing + precise glyph rendering.
          doc.querySelectorAll<HTMLElement>('*').forEach((el) => {
            el.style.setProperty('-webkit-font-smoothing', 'antialiased');
            el.style.setProperty('text-rendering', 'geometricPrecision');
          });
        },
      });

      // Slice the tall canvas into A4 LANDSCAPE pages — the dashboard is wide, so a
      // landscape page downscales it far less, keeping text large and print-readable.
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = 297, pageH = 210, margin = 8;
      const usableW = pageW - margin * 2;
      const pxPerMm = canvas.width / usableW;
      const pageHpx = Math.floor((pageH - margin * 2) * pxPerMm);

      // For neat page breaks: instead of cutting at the hard boundary (which slices
      // through cards/text), scan upward to the nearest "gap" — a row of uniform
      // colour (the page background or an empty band between cards) — and cut there.
      const src = canvas.getContext('2d')!;
      const W = canvas.width;
      const rowIsGap = (r: number) => {
        const data = src.getImageData(0, r, W, 1).data;
        let minR = 255, maxR = 0, minG = 255, maxG = 0, minB = 255, maxB = 0;
        for (let x = 0; x < W; x += 24) {
          const i = x * 4;
          minR = Math.min(minR, data[i]); maxR = Math.max(maxR, data[i]);
          minG = Math.min(minG, data[i + 1]); maxG = Math.max(maxG, data[i + 1]);
          minB = Math.min(minB, data[i + 2]); maxB = Math.max(maxB, data[i + 2]);
        }
        // Uniform row → safe to cut (no text/graphics crossing it).
        return (maxR - minR) <= 6 && (maxG - minG) <= 6 && (maxB - minB) <= 6;
      };
      const findBreak = (ideal: number) => {
        const lo = Math.max(ideal - Math.floor(pageHpx * 0.22), 1);
        for (let r = ideal; r >= lo; r--) { if (rowIsGap(r)) return r; }
        return ideal; // no gap found — fall back to the hard boundary
      };

      let y = 0, page = 0;
      while (y < canvas.height) {
        let sliceH = Math.min(pageHpx, canvas.height - y);
        if (y + sliceH < canvas.height) sliceH = findBreak(y + sliceH) - y; // trim to a clean gap
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = sliceH;
        const ctx = slice.getContext('2d')!;
        ctx.fillStyle = '#F8FAFC';
        ctx.fillRect(0, 0, slice.width, sliceH);
        ctx.drawImage(canvas, 0, y, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        if (page > 0) pdf.addPage();
        // PNG (lossless) keeps thin text strokes crisp — JPEG smeared them.
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', margin, margin, usableW, sliceH / pxPerMm, undefined, 'FAST');
        y += sliceH;
        page++;
      }
      pdf.save(`Work-Profile-${(workData.workId || 'work').toString().replace(/[^\w-]+/g, '_')}.pdf`);
    } catch (err) {
      console.error('Overview PDF export failed:', err);
      cleanup();
      setExportingPdf(false);
      handlePrintPDF(); // fall back to the print-based export
      return;
    }
    cleanup();
    setExportingPdf(false);
  };

  // Exact-look Print / Save-as-PDF of the premium Overview. Renders the REAL
  // dashboard DOM with the browser's own engine (in a hidden iframe with the
  // app's stylesheets copied in) and opens the print dialog — so the output is
  // pixel-identical to the on-screen view with zero text overlap. From the
  // dialog the user can print or choose "Save as PDF". (The Leaflet map needs
  // JS to draw tiles, so its area shows as a plain panel in print.)
  const openOverviewPrint = () => {
    if (exportingPdf) return;
    const node = overviewRef.current;
    if (!node) { handlePrintPDF(); return; }
    setExportingPdf(true);
    try {
      // Copy stylesheets (links by absolute href for prod, <style> tags for dev).
      const sheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .map((l) => `<link rel="stylesheet" href="${(l as HTMLLinkElement).href}">`);
      const styles = Array.from(document.querySelectorAll('style'))
        .map((s) => `<style>${s.textContent || ''}</style>`);
      const head = [...sheets, ...styles].join('\n');

      // Clone the dashboard and un-clip it for print: drop the fade transforms,
      // let horizontal strips (KPI row, funding table) wrap instead of scroll,
      // expand inner vertical scroll panes, and stop text truncation cutting
      // labels. This makes the FULL layout print with nothing clipped/cut.
      const clone = node.cloneNode(true) as HTMLElement;
      clone.querySelectorAll<HTMLElement>('*').forEach((el) => {
        el.style.transform = 'none';
        el.style.animation = 'none';
        el.style.transition = 'none';
        el.style.opacity = '1';
        const cl = el.classList;
        if (cl.contains('truncate')) { el.style.whiteSpace = 'normal'; el.style.overflow = 'visible'; el.style.textOverflow = 'clip'; }
        if (cl.contains('overflow-x-auto') || cl.contains('overflow-x-scroll')) { el.style.overflowX = 'visible'; el.style.flexWrap = 'wrap'; }
        if (cl.contains('overflow-y-auto') || cl.contains('overflow-y-scroll')) { el.style.overflowY = 'visible'; el.style.maxHeight = 'none'; }
        // Let equal-height cards (Funding card etc.) grow to their full content so
        // internally-scrolled tables (Funding Pattern + Releases) aren't clipped.
        if (cl.contains('overflow-hidden')) el.style.overflow = 'visible';
        if (cl.contains('h-full')) el.style.height = 'auto';
        if (cl.contains('items-stretch')) el.style.alignItems = 'flex-start';
        // Drop any line-clamp so multi-line text (long work names, descriptions,
        // remarks) prints in FULL rather than being cut to N lines with an ellipsis.
        const st2 = el.style as any;
        if (Array.from(cl).some((c) => c.startsWith('line-clamp-')) || st2.webkitLineClamp || st2.display === '-webkit-box') {
          st2.display = 'block'; st2.webkitLineClamp = ''; st2.webkitBoxOrient = '';
          el.style.overflow = 'visible'; el.style.maxHeight = 'none'; el.style.whiteSpace = 'normal';
        }
      });

      // Cap long lists in the PDF so they don't sprawl across many pages: keep
      // only the first N items (matches what the scrollable card shows) and add
      // a "+N more" note pointing back to the live dashboard.
      clone.querySelectorAll<HTMLElement>('[data-print-limit]').forEach((box) => {
        const lim = parseInt(box.getAttribute('data-print-limit') || '0', 10);
        const kids = Array.from(box.children) as HTMLElement[];
        if (lim > 0 && kids.length > lim) {
          kids.slice(lim).forEach((k) => k.remove());
          const note = clone.ownerDocument!.createElement('div');
          note.textContent = `+ ${kids.length - lim} more — view the full list in the dashboard`;
          note.style.cssText = 'grid-column:1/-1;padding:8px 2px;font-size:11px;color:#6B7280;font-style:italic;';
          box.appendChild(note);
        }
      });

      // Reveal print-only blocks (e.g. the "Work Name:" title) hidden on screen.
      clone.querySelectorAll<HTMLElement>('[data-print-only]').forEach((n) => { n.style.display = 'block'; });

      // Drop the description table's "Images" column (header + per-row cells) from print.
      clone.querySelectorAll('[data-print-hide]').forEach((n) => n.remove());

      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      // Off-screen but laid out at desktop width so the multi-column dashboard
      // renders exactly like the full-screen web view (not the mobile stack).
      iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:1366px;height:1200px;border:0;background:#fff;';
      document.body.appendChild(iframe);
      const cleanup = () => { try { document.body.removeChild(iframe); } catch { /* gone */ } };
      const idoc = iframe.contentWindow?.document;
      if (!idoc) { cleanup(); setExportingPdf(false); handlePrintPDF(); return; }
      idoc.open();
      idoc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><base href="${location.origin}/"><title>${workData.workId || 'Work Profile'}</title>${head}<style>
        html,body{margin:0;padding:0;background:#F8FAFC;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
        *{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
        .leaflet-container{background:#e5e7eb !important;}
        /* keep cards intact across page breaks */
        .rounded-2xl,.rounded-xl{break-inside:avoid;}
        @page{size:A4 portrait;margin:8mm;}
      </style></head><body>${clone.outerHTML}</body></html>`);
      idoc.close();

      const fire = () => {
        try {
          iframe.contentWindow?.focus();
          if (iframe.contentWindow) iframe.contentWindow.onafterprint = () => setTimeout(cleanup, 300);
          iframe.contentWindow?.print();
        } catch { cleanup(); }
        setExportingPdf(false);
        setTimeout(cleanup, 120000);
      };
      // Give the copied stylesheets + fonts time to apply before printing.
      setTimeout(fire, 900);
    } catch (err) {
      console.error('Overview print failed:', err);
      setExportingPdf(false);
      handlePrintPDF();
    }
  };

  return (
    <div className={`fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[9999] ${isFullscreen ? 'p-0' : 'p-4'}`}>
      <div className={`relative bg-slate-100 dark:bg-gray-900 shadow-2xl overflow-hidden flex flex-col ${isFullscreen ? 'w-full h-full rounded-none' : 'rounded-2xl w-full max-w-7xl max-h-[95vh]'}`}>
      {/* Header */}
      <div className="module-header shadow-md flex-shrink-0 relative border-b border-white/10">
        <div className="px-4 sm:px-5 py-1.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
                <i className="ri-building-4-fill text-white text-base"></i>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h1 ref={nameRef} className={`text-sm font-semibold text-white leading-tight break-words min-w-0 flex-1 ${nameExpanded ? '' : 'line-clamp-1'}`} title={workData.workName}>{workData.workName || workData.workId}</h1>
                  <span className={`shrink-0 whitespace-nowrap px-2 py-px rounded-full text-[10px] font-semibold ${getStatusColor(workData.status)}`}>
                    {workData.status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-[10px] leading-tight text-white/80 truncate">
                    <span className="font-mono">{workData.workId}</span>
                    <span className="text-white/40 ml-2">· ID {workId}</span>
                  </p>
                  {nameClamped && (
                    <button
                      type="button"
                      onClick={() => setNameExpanded((v) => !v)}
                      className="shrink-0 text-[10px] leading-tight font-semibold text-cyan-100 hover:text-white underline decoration-dotted"
                    >
                      {nameExpanded ? 'Show less' : 'View more'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Overview ⇄ Full Record switch */}
              <div className="hidden sm:flex items-center bg-white/15 rounded-lg p-0.5 mr-1">
                <button
                  onClick={() => setMainView('overview')}
                  className={`h-6 px-2.5 rounded-md text-[11px] font-semibold transition-colors ${mainView === 'overview' ? 'bg-white text-teal-700 shadow-sm' : 'text-white/80 hover:text-white'}`}
                  title="Premium overview dashboard"
                >
                  <i className="ri-dashboard-3-line mr-1"></i>Overview
                </button>
                <button
                  onClick={() => setMainView('lifecycle')}
                  className={`h-6 px-2.5 rounded-md text-[11px] font-semibold transition-colors ${mainView === 'lifecycle' ? 'bg-white text-teal-700 shadow-sm' : 'text-white/80 hover:text-white'}`}
                  title="Every stage from proposal to final payment"
                >
                  <i className="ri-git-commit-line mr-1"></i>Lifecycle
                </button>
                <button
                  onClick={() => setMainView('record')}
                  className={`h-6 px-2.5 rounded-md text-[11px] font-semibold transition-colors ${mainView === 'record' ? 'bg-white text-teal-700 shadow-sm' : 'text-white/80 hover:text-white'}`}
                  title="Full editable record"
                >
                  <i className="ri-file-list-3-line mr-1"></i>Full Record
                </button>
              </div>
              {/* Overview actions — Download PDF / Open GIS Map / Print sit beside the
                  Overview ⇄ Full Record switch (moved out of the overview body). */}
              <div className="flex items-center gap-2 mr-1">
                {mainView !== 'lifecycle' && (
                <button
                  onClick={mainView === 'overview' ? handleDownloadOverviewPdf : handlePrintPDF}
                  disabled={exportingPdf}
                  className="flex items-center gap-1.5 h-7 px-2.5 bg-white/15 hover:bg-white/25 text-white rounded-lg transition-colors text-[11px] font-semibold disabled:opacity-60 disabled:cursor-wait"
                  title={exportingPdf ? 'Preparing PDF…' : 'Download PDF'}
                >
                  <i className={`${exportingPdf ? 'ri-loader-4-line animate-spin' : 'ri-file-pdf-line'} text-sm`}></i>
                  <span className="hidden md:inline">Download PDF</span>
                </button>
                )}
                {mainView === 'overview' && (
                  <>
                    <button
                      onClick={() => { setMainView('record'); setActiveTab('map'); }}
                      className="flex items-center gap-1.5 h-7 px-2.5 bg-white/15 hover:bg-white/25 text-white rounded-lg transition-colors text-[11px] font-semibold"
                      title="Open GIS Map"
                    >
                      <i className="ri-map-2-line text-sm"></i>
                      <span className="hidden md:inline">Open GIS Map</span>
                    </button>
                    <button
                      onClick={openOverviewPrint}
                      disabled={exportingPdf}
                      className="flex items-center gap-1.5 h-7 px-2.5 bg-white/15 hover:bg-white/25 text-white rounded-lg transition-colors text-[11px] font-semibold disabled:opacity-60"
                      title="Print"
                    >
                      <i className="ri-printer-line text-sm"></i>
                      <span className="hidden md:inline">Print</span>
                    </button>
                  </>
                )}
              </div>
              <button
                onClick={toggleFullscreen}
                className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              >
                <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-white text-base`}></i>
              </button>
              {isEditMode ? (
                <>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-1.5 h-7 px-2.5 bg-green-500 hover:bg-green-600 disabled:bg-green-400 text-white rounded-lg transition-colors text-[11px] font-semibold"
                  >
                    {isSaving ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <i className="ri-save-line"></i>
                    )}
                    Save
                  </button>
                  <button
                    onClick={cancelEditMode}
                    disabled={isSaving}
                    className="flex items-center gap-1.5 h-7 px-2.5 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-[11px] font-semibold"
                  >
                    <i className="ri-close-line"></i>
                    Cancel
                  </button>
                </>
              ) : (
                <button onClick={enterEditMode} className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg hover:bg-white/30 transition-colors" title="Edit">
                  <i className="ri-edit-line text-white text-base"></i>
                </button>
              )}
              <button
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors"
                title="Close"
              >
                <i className="ri-close-line text-white text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`flex-1 overflow-auto ${(mainView === 'overview' || mainView === 'lifecycle') && !isLoading && apiData ? '' : 'px-4 sm:px-6 py-6 space-y-6'}`}>
        {isLoading ? (
          // Skeleton that mirrors the real card layout so content fades in place
          // instead of a spinner "circle" abruptly turning into cards.
          <div className="flex flex-col gap-6">
            {/* Top row — 3 info cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[0, 1, 2].map(i => (
                <div key={i} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-5 ring-1 ring-black/[0.03] dark:ring-gray-700">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="h-9 w-9 rounded-xl bg-gray-200 dark:bg-gray-700 animate-pulse" />
                    <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
                  </div>
                  <div className="space-y-3">
                    {[0, 1, 2, 3].map(r => (
                      <div key={r} className="flex justify-between gap-3">
                        <div className="h-3 w-20 rounded bg-gray-100 dark:bg-gray-700/60 animate-pulse" />
                        <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {/* Work Details — wide card */}
            <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-5 ring-1 ring-black/[0.03] dark:ring-gray-700">
              <div className="h-4 w-40 rounded bg-gray-200 dark:bg-gray-700 animate-pulse mb-4" />
              <div className="space-y-3">
                {[0, 1, 2, 3].map(r => <div key={r} className="h-3 w-full rounded bg-gray-100 dark:bg-gray-700/60 animate-pulse" />)}
              </div>
            </div>
            {/* Financial + Dates — two columns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[0, 1].map(i => (
                <div key={i} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-5 ring-1 ring-black/[0.03] dark:ring-gray-700">
                  <div className="h-4 w-36 rounded bg-gray-200 dark:bg-gray-700 animate-pulse mb-4" />
                  <div className="space-y-3">
                    {[0, 1, 2, 3, 4].map(r => (
                      <div key={r} className="flex justify-between gap-3">
                        <div className="h-3 w-24 rounded bg-gray-100 dark:bg-gray-700/60 animate-pulse" />
                        <div className="h-3 w-16 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !apiData ? (
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
              <i className="ri-error-warning-line text-4xl"></i>
              <span className="text-sm">Work details not found</span>
            </div>
          </div>
        ) : mainView === 'lifecycle' ? (
          <WorkLifecycleTab workId={apiData?.workId ?? workId} />
        ) : mainView === 'overview' ? (
          (() => {
            // Components = real schedule milestones (excluding the synthetic WORK_SCHEDULE row).
            const realMs = scheduleMilestones.filter(m => m.mlstn_cd !== 'WORK_SCHEDULE' && m.mlstn_cd !== 'WORK SCHEDULE');
            const components = realMs.map(m => ({
              name: m.mlstn_nm,
              progress: Number(m.prgrs_pct_nu) || 0,
              subCount: Array.isArray(m.sub_milestones) ? m.sub_milestones.length : 0,
              started: !!m.actl_strt_dt || (Number(m.prgrs_pct_nu) || 0) > 0 || (m.sts_cd && m.sts_cd !== 'NOT_STARTED'),
              startDate: m.plnd_strt_dt || m.actl_strt_dt || '',
              endDate: m.plnd_end_dt || '',
              actualStart: m.actl_strt_dt || '',
              status: m.sts_cd || '',
              weightage: Number(m.wghtg_pct_nu) || 0,
              plndAmtRupees: m.plnd_amt_at === null || m.plnd_amt_at === undefined ? null : Number(m.plnd_amt_at),
            }));
            // Work start: explicit commencement, else earliest milestone (actual/planned).
            const msStarts = realMs.map(m => m.actl_strt_dt || m.plnd_strt_dt).filter(Boolean).sort();
            const startDate = workData.dates.commencementDate || msStarts[0] || '';
            const expectedCompletion = workData.dates.revisedCompletion || workData.dates.stipulatedCompletion
              || realMs.map(m => m.plnd_end_dt).filter(Boolean).sort().slice(-1)[0] || '';
            return (
              <div ref={overviewRef}>
              <WorkProfileOverview
                workId={apiData?.workId ?? (workId ? Number(workId) : undefined)}
                exportingPdf={exportingPdf}
                onDownloadPdf={handleDownloadOverviewPdf}
                onPrint={openOverviewPrint}
                onOpenFullMap={() => { setMainView('record'); setActiveTab('map'); }}
                onEditFunding={() => { setMainView('record'); setActiveTab('funding'); }}
                data={{
                  workCode: workData.workId,
                  workName: workData.workName,
                  status: workData.status,
                  workType: workData.workType,
                  scheme: apiData?.scheme?.name || '',
                  description: apiData?.description || '',
                  executingAgency: apiData?.executingAgency || '',
                  owningBody: apiData?.owningBody || '',
                  udaCentre: apiData?.udaCentre || null,
                  location: {
                    district: workData.location.district,
                    municipality: workData.location.municipality,
                    ward: workData.location.ward,
                    village: apiData?.villageName || '',
                    address: workData.location.address,
                    lat: apiData?.lat,
                    lng: apiData?.lng,
                    nearestHabitation: apiData?.ulb || '',
                  },
                  engineer: {
                    name: workData.engineer.name || cf.engineer_in_charge || cf.engineer_incharge || cf.ae_name || cf.assistant_engineer || '',
                    designation: workData.engineer.designation,
                    department: workData.engineer.department,
                    phone: workData.engineer.phone,
                  },
                  ee: { name: cf.ee_name || cf.executive_engineer || cf.executive_engineer_name || cf.ee || '' },
                  se: { name: cf.se_name || cf.superintending_engineer || cf.superintending_engineer_name || cf.se || '' },
                  agreement: {
                    no: cf.agreement_no || cf.agrmnt_no || cf.agreement_number || apiData?.agreementNo || '',
                    date: workData.dates.agreementDate,
                    endDate: workData.dates.revisedCompletion || workData.dates.stipulatedCompletion,
                  },
                  contractor: {
                    id: workData.contractor.contractorId,
                    name: workData.contractor.name,
                    code: workData.contractor.contractorCode,
                    phone: workData.contractor.phone,
                  },
                  // workData.financial.* is coalesced to 0 for arithmetic everywhere
                  // else in this file. The overview additionally needs to tell "never
                  // recorded" from "zero" so it can show a dash, so the three figures
                  // it displays are passed as null when the API returned null. Works
                  // reconstructed from an accounting ledger have no sanction or
                  // contract value at all, and "₹0.00 Cr" would misstate that as nil.
                  projectCostCr: apiData?.contractAmount == null && apiData?.sanctionAmount == null
                    ? (workData.financial.tcv > 0 ? workData.financial.tcv : null)
                    : workData.financial.tcv,
                  financial: {
                    contractValueCr: apiData?.contractAmount == null && workData.financial.tcv === 0
                      ? null
                      : workData.financial.tcv,
                    // Work Done is derived — tcv x physical%, or the milestone roll-up.
                    // With no contract/sanction value and no milestone planned amounts,
                    // tcv is 0 and the product is 0 for arithmetic reasons, not because
                    // nothing was built. Pass null so it reads as a dash rather than
                    // claiming "no work done" on a completed work.
                    completedValueCr: workData.financial.tcv === 0
                      ? null
                      : workData.financial.workCompleted,
                    paidCr: workData.financial.paidToContractor,
                    pendingBillsCr: workData.financial.pendingBills,
                    sanctionedCr: apiData?.sanctionAmount == null ? null : workData.financial.sanctionedAmount,
                  },
                  dates: { startDate, expectedCompletion },
                  physicalProgress: workData.physicalProgress,
                  financialProgress: workData.financialProgress,
                  components,
                  counts: { issues: 0, criticalIssues: 0 },
                }}
              />
              </div>
            );
          })()
        ) : (
        // Auto-height flex column so order-first can reorder sections
        // without the height-constrained scroll container squishing them.
        <div className="flex flex-col gap-6">
        {/* ===== Work Profile — left section menu + focused content sheet ===== */}
        <div className="flex gap-4 items-start">
          {/* Left section menu — hover/click brings a section into focus; all
              sections stay rendered so the full record is always available. */}
          <nav className="hidden md:flex sticky top-0 self-start w-56 flex-shrink-0 flex-col gap-1.5">
            {profileSections.map(s => {
              const active = activeProfileSection === s.id;
              return (
                <button key={s.id} type="button"
                  onClick={() => goToProfileSection(s.id)}
                  onMouseEnter={() => goToProfileSection(s.id)}
                  className={`group flex items-center gap-3 border-l-2 px-3.5 py-2.5 text-left text-sm transition-colors ${active ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 font-semibold' : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white font-medium'}`}>
                  <i className={`${s.icon} text-lg flex-shrink-0 ${active ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 group-hover:text-indigo-500'}`}></i>
                  <span className="whitespace-nowrap">{s.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Content column — each section is its OWN separate card */}
          <div className="flex-1 min-w-0 flex flex-col gap-4">

            {/* Work Location */}
            <div id="sec-location" className="scroll-mt-4 flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)] ring-1 ring-slate-200 dark:ring-gray-700 overflow-hidden">
              {sheetHead({ icon: 'ri-map-pin-2-fill', label: 'Work Location', tint: 'violet' })}
              <div className="px-4 sm:px-5 py-3.5 flex-1 flex flex-col">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                  {tile('District', workData.location.district || '—')}
                  {tile('Municipality', workData.location.municipality || '—')}
                  {EditableField({ label: "Ward", value: workData.location.ward, field: "ward_nm", type: "select", options: dropdowns.wards, optionKey: "admn_unt_nm", optionLabel: "admn_unt_nm" })}
                  {tile('GPS Coordinates', workData.location.gpsCoordinates || '—')}
                  {EditableField({ label: "Address", value: workData.location.address, field: "lctn_tx", wide: true })}
                </div>
                {/* Sachivalayam mapping — grows to keep both columns the same height */}
                <div className="mt-2.5 flex-1 rounded-xl border border-slate-200/70 dark:border-gray-700/70 bg-slate-50/70 dark:bg-gray-700/30 px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Sachivalayams</span>
                    <button
                      onClick={() => setShowSvmSelector(true)}
                      className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    >
                      <i className={`${sachivalayams.length > 0 ? 'ri-edit-line' : 'ri-map-pin-add-line'} text-xs`} />
                      {sachivalayams.length > 0 ? 'Edit' : 'Map'}
                    </button>
                  </div>
                  {sachivalayams.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {sachivalayams.map(s => (
                        <span key={s.svm_id} className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-[11px] font-medium">
                          {s.svm_nm}<span className="text-indigo-400 dark:text-indigo-500 font-normal ml-1">{s.svm_cd}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[11px] text-orange-500 italic flex items-center gap-1 mt-1.5">
                      <i className="ri-error-warning-line text-xs" /> Not mapped — click Map to assign
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* Work Details */}
            <div id="sec-details" className="scroll-mt-4 bg-white dark:bg-gray-800 rounded-2xl shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)] ring-1 ring-slate-200 dark:ring-gray-700 overflow-hidden">
              {sheetHead({ icon: 'ri-file-text-fill', label: 'Work Details', tint: 'violet' })}
              <div className="px-4 sm:px-5 py-3.5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                  {EditableField({ label: "Work Title", value: workData.workName, field: "wrk_nm", wide: true })}
                  {EditableField({ label: "Work Type/Sector", value: workData.workType, field: "wrk_type_id", type: "select", options: dropdowns.workTypes })}
                  {EditableField({ label: "Scheme", value: apiData?.scheme?.name || '', field: "schme_id", type: "select", options: dropdowns.schemes })}
                  {EditableField({ label: "Status", value: workData.status, field: "wrk_sts_id", type: "select", options: dropdowns.statuses })}
                  {EditableField({ label: "Description", value: apiData?.description || '', field: "dscn_tx", type: "textarea" })}
                  {apiData?.adminSanction?.goNumber && tile('Admin Sanction GO',
                    <>{apiData.adminSanction.goNumber}{apiData.adminSanction.goDate && <span className="text-slate-400 text-xs ml-1 font-normal">({formatDate(apiData.adminSanction.goDate)})</span>}</>,
                    { wide: true }
                  )}
                  {/* Contractor & Engineer — folded into Work Details */}
                  {isEditMode ? (
                    <>
                      {EditableField({ label: "Contractor", value: workData.contractor.name, field: "executing_agency", isCustom: true })}
                      {EditableField({ label: "Contractor Phone", value: workData.contractor.phone, field: "contractor_phone", isCustom: true })}
                      {EditableField({ label: "Engineer", value: workData.engineer.name, field: "assigned_engineer", isCustom: true })}
                      {EditableField({ label: "Designation", value: workData.engineer.designation, field: "engineer_designation", isCustom: true, type: "select", options: dropdowns.designations, optionKey: "name", optionLabel: "name" })}
                    </>
                  ) : (
                    <>
                      {/* Contractor — name + phone in the value column */}
                      <div className={TILE}>
                        <span className={TILE_LABEL}>Contractor:</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold break-words text-slate-800 dark:text-slate-100">
                            {workData.contractor.name ? (
                              <button onClick={() => setShowContractorModal(true)} className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 text-left" title="View contractor profile">
                                {workData.contractor.name}<i className="ri-external-link-line text-xs"></i>
                              </button>
                            ) : (
                              <button onClick={() => setShowContractorSelector(true)} className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1">
                                Not Assigned<i className="ri-add-circle-line text-xs"></i>
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500 dark:text-slate-400">
                            <i className="ri-phone-line text-slate-400"></i>
                            {workData.contractor.phone
                              ? <a href={`tel:${workData.contractor.phone}`} className="text-blue-600 hover:underline font-medium">{workData.contractor.phone}</a>
                              : <span>No phone</span>}
                          </div>
                        </div>
                      </div>
                      {/* Engineer — name + designation in the value column */}
                      <div className={TILE}>
                        <span className={TILE_LABEL}>Engineer:</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold break-words text-slate-800 dark:text-slate-100">
                            {workData.engineer.name ? (
                              <button onClick={() => setShowEngineerModal(true)} className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 text-left" title="View all works assigned to this engineer">
                                {workData.engineer.name}<i className="ri-external-link-line text-xs"></i>
                              </button>
                            ) : (
                              <button onClick={() => setShowEngineerSelector(true)} className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1" title="Assign engineer">
                                Not Assigned<i className="ri-add-circle-line text-xs"></i>
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500 dark:text-slate-400">
                            <i className="ri-briefcase-4-line text-slate-400"></i>
                            <span>{workData.engineer.designation || 'No designation'}</span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

          {/* Financials & Key Dates */}
          <div id="sec-finance" className="scroll-mt-4 bg-white dark:bg-gray-800 rounded-2xl shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)] ring-1 ring-slate-200 dark:ring-gray-700 overflow-hidden">
          {sheetHead({ icon: 'ri-money-rupee-circle-fill', label: 'Financials & Key Dates', tint: 'purple' })}
          <div className="px-4 sm:px-5 py-3.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
              {EditableField({ label: "Estimated Cost", value: workData.financial.estimatedCost, field: "orgnl_estmt_at", type: "number" })}
              {EditableField({ label: "Sanctioned Amount", value: workData.financial.sanctionedAmount, field: "snctn_at", type: "number", colorClass: "text-emerald-600 dark:text-emerald-400" })}
              {EditableField({ label: "Released Amount", value: workData.financial.releasedAmount, field: "rvsd_estmt_at", type: "number", colorClass: "text-blue-600 dark:text-blue-400" })}
              {EditableField({ label: "Expenditure", value: workData.financial.expenditure, field: "expenditure", type: "number", isCustom: true, colorClass: "text-orange-600 dark:text-orange-400" })}
              {EditableField({ label: "Pending Bills", value: workData.financial.pendingBills, field: "pending_bills", type: "number", isCustom: true, colorClass: "text-red-600 dark:text-red-400" })}
              {/* Balance measured against whatever funding figure the work actually has.
                  Released is the right basis when funds have been released, but it is 0
                  on works that were sanctioned and are being executed without a separate
                  release recorded — and `0 - expenditure` then reports a negative or
                  empty balance on a work with half a crore sanctioned. Falls back to the
                  sanctioned amount so the figure means "sanctioned funds not yet spent"
                  rather than nothing at all. */}
              {tile('Balance', formatCurrency(
                (workData.financial.releasedAmount > 0
                  ? workData.financial.releasedAmount
                  : workData.financial.sanctionedAmount) - workData.financial.expenditure))}
              {EditableField({ label: "Sanction Date", value: workData.dates.sanctionDate, field: "snctn_dt", type: "date" })}
              {/* Editable only while no contract exists. Once there is a contract, the
                  agreement date belongs to it, and letting someone edit the work's
                  copy here is precisely how the two drifted apart. Change it on the
                  contract instead. */}
              {apiData?.contract?.agreementDate
                ? tile('Agreement Date',
                    <>
                      {formatDate(workData.dates.agreementDate)}
                      <span className="text-slate-400 text-xs ml-1.5 font-normal">from contract</span>
                    </>)
                : EditableField({ label: "Agreement Date", value: workData.dates.agreementDate, field: "agrmnt_dt", type: "date" })}
            </div>
          </div>
          </div>{/* /sec-finance */}
          {/* Current Status */}
          <div id="sec-status" className="scroll-mt-4 bg-white dark:bg-gray-800 rounded-2xl shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)] ring-1 ring-slate-200 dark:ring-gray-700 overflow-hidden">
          {sheetHead({ icon: 'ri-pie-chart-2-fill', label: 'Current Status', tint: 'teal' })}
          {/* Grounding is a lifecycle event, not an edit, so it sits with the status
              rather than in the edit form. Shown only from AWARDED — the single state
              the WORK workflow permits GROUNDED from. */}
          {/* Always shown, whatever the work's state. Clicking it on a work that is
              already grounded says so rather than the control quietly disappearing. */}
          {apiData && (
            <div className="px-4 sm:px-5 pt-4">
              <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${
                apiData?.status?.code === 'GROUNDED'
                  ? 'border-gray-200 bg-gray-50'
                  : 'border-emerald-200 bg-emerald-50/60'
              }`}>
                <i className={`${apiData?.status?.code === 'GROUNDED' ? 'ri-checkbox-circle-line text-gray-500' : 'ri-building-2-line text-emerald-600'}`}></i>
                <div className="flex-1 min-w-[200px]">
                  <div className={`text-sm font-semibold ${apiData?.status?.code === 'GROUNDED' ? 'text-gray-700' : 'text-emerald-900'}`}>
                    {apiData?.status?.code === 'GROUNDED' ? 'This work is grounded' : 'Work has started on site?'}
                  </div>
                  <div className={`text-xs ${apiData?.status?.code === 'GROUNDED' ? 'text-gray-500' : 'text-emerald-700/80'}`}>
                    Grounding records that execution has begun, and grounds this work&apos;s approved designs with it.
                  </div>
                </div>
                <button
                  onClick={handleGroundWork}
                  disabled={groundingWork}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 ${
                    apiData?.status?.code === 'GROUNDED'
                      ? 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                      : 'text-white bg-emerald-600 hover:bg-emerald-700'
                  }`}
                >
                  <i className={apiData?.status?.code === 'GROUNDED' ? 'ri-checkbox-circle-line' : 'ri-building-2-line'}></i>
                  {groundingWork ? 'Grounding…' : apiData?.status?.code === 'GROUNDED' ? 'Grounded' : 'Ground Work'}
                </button>
              </div>
            </div>
          )}
          {groundMsg && (
            <div className="px-4 sm:px-5 pt-3">
              <div className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-start gap-2">
                <i className="ri-information-line text-gray-400 mt-0.5"></i>
                <span className="flex-1">{groundMsg}</span>
                <button onClick={() => setGroundMsg(null)} className="text-gray-400 hover:text-gray-600">
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>
          )}
          <div className="px-4 sm:px-5 py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Financial Progress */}
              <div className="order-last">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 block mb-3">Financial Progress</span>
                {/* Work Completion Progress Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-blue-700">Work Completed vs TCV</span>
                    <span className="text-xs font-bold text-blue-600">
                      {safePercent(workData.financial.workCompleted, workData.financial.tcv)}%
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                      style={{ width: `${safePercent(workData.financial.workCompleted, workData.financial.tcv)}%` }}
                    />
                  </div>
                </div>

                {/* Payment Progress Bar */}
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-green-700">Payment vs Work Completed</span>
                    <span className="text-xs font-bold text-green-600">
                      {safePercent(workData.financial.paidToContractor, workData.financial.workCompleted)}%
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500"
                      style={{ width: `${safePercent(workData.financial.paidToContractor, workData.financial.workCompleted)}%` }}
                    />
                  </div>
                </div>


                {/* Finance Schedule Milestones */}
                {financeEntries.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 flex items-center gap-1 mb-2">
                      <i className="ri-money-rupee-circle-line text-emerald-500 text-sm"></i> Finance Schedule
                    </span>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                      {financeEntries.map((fe: any, fi: number) => {
                        const today = new Date();
                        const targetDt = fe.target_dt ? new Date(fe.target_dt) : null;
                        const isPast = targetDt && targetDt < today;
                        const isComplete = (fe.actl_pct_nu || 0) >= (fe.plnd_pct_nu || 0) && fe.plnd_pct_nu > 0;
                        const isOverdue = isPast && !isComplete;
                        return (
                          <div key={fi} className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${
                            isComplete ? 'bg-green-50 border-green-200' : isOverdue ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                              isComplete ? 'bg-green-500' : isOverdue ? 'bg-red-500' : 'bg-gray-300'
                            }`}>
                              {isComplete ? <i className="ri-check-line text-white text-xs"></i>
                                : isOverdue ? <i className="ri-error-warning-line text-white text-xs"></i>
                                : <span className="text-white text-[10px] font-medium">{fi + 1}</span>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] text-gray-500">
                                  {targetDt ? targetDt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-[10px] font-medium ${isComplete ? 'text-green-600' : isOverdue ? 'text-red-600' : 'text-gray-600'}`}>
                                    Work: {fe.plnd_pct_nu}%
                                  </span>
                                  <span className="text-[10px] font-mono text-gray-500">
                                    ₹{((fe.plnd_at || 0) >= 100000 ? ((fe.plnd_at / 100000).toFixed(1) + 'L') : (fe.plnd_at || 0).toLocaleString('en-IN'))}
                                  </span>
                                </div>
                              </div>
                              <div className="h-1 bg-gray-200 rounded-full overflow-hidden mt-1">
                                <div className={`h-full rounded-full ${isComplete ? 'bg-green-500' : isOverdue ? 'bg-red-400' : 'bg-emerald-300'}`}
                                  style={{ width: `${fe.actl_pct_nu || 0}%` }}></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {/* Physical Progress */}
              <div className="order-first">
              <div className="mb-4 p-3.5 rounded-xl bg-gradient-to-br from-blue-50/60 to-transparent dark:from-blue-900/10 border border-blue-100/70 dark:border-blue-900/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Physical Progress</span>
                  <span className="text-xl font-bold text-blue-600 dark:text-blue-400 tabular-nums leading-none">{workData.physicalProgress}<span className="text-sm font-semibold text-gray-400 ml-0.5">%</span></span>
                </div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-700"
                    style={{ width: `${Math.min(Math.max(workData.physicalProgress, 0), 100)}%` }}
                  />
                </div>
                {/* Started / Target */}
                <div className="flex items-center gap-5 mt-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30"><i className="ri-flag-line text-xs"></i></span>
                    <div className="min-w-0">
                      <span className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">Started</span>
                      <span className="block text-xs font-semibold text-gray-700 dark:text-gray-300 truncate">{formatDate(workData.dates.commencementDate)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600 dark:bg-orange-900/30"><i className="ri-flag-2-line text-xs"></i></span>
                    <div className="min-w-0">
                      <span className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">Target</span>
                      <span className="block text-xs font-semibold text-gray-700 dark:text-gray-300 truncate">{formatDate(workData.dates.revisedCompletion)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Landmarks Progress */}
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {workData.landmarks.map((landmark, index) => (
                  <div
                    key={landmark.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                      landmark.status === 'completed'
                        ? 'bg-green-50 border-green-200'
                        : landmark.status === 'in-progress'
                        ? 'bg-orange-50 border-orange-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    {/* Status Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      landmark.status === 'completed'
                        ? 'bg-green-500'
                        : landmark.status === 'in-progress'
                        ? 'bg-orange-500'
                        : 'bg-gray-300'
                    }`}>
                      {landmark.status === 'completed' ? (
                        <i className="ri-check-line text-white text-sm"></i>
                      ) : landmark.status === 'in-progress' ? (
                        <i className="ri-loader-4-line text-white text-sm animate-spin"></i>
                      ) : (
                        <span className="text-white text-xs font-medium">{index + 1}</span>
                      )}
                    </div>

                    {/* Landmark Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-medium truncate ${
                          landmark.status === 'completed'
                            ? 'text-green-800'
                            : landmark.status === 'in-progress'
                            ? 'text-orange-800'
                            : 'text-gray-600'
                        }`}>
                          {landmark.name}
                        </span>
                        <span className={`text-xs ml-2 flex items-baseline gap-1 ${
                          landmark.status === 'completed'
                            ? 'text-green-600'
                            : landmark.status === 'in-progress'
                            ? 'text-orange-600'
                            : 'text-gray-400'
                        }`}>
                          <span className="font-bold">{landmark.progress}%</span>
                          {landmark.wghtg_pct_nu > 0 && (
                            <span className="text-[10px] font-medium opacity-70">
                              of Wt {landmark.wghtg_pct_nu}%
                            </span>
                          )}
                        </span>
                      </div>
                      {/* Mini Progress Bar */}
                      <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-1.5">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            landmark.status === 'completed'
                              ? 'bg-green-500'
                              : landmark.status === 'in-progress'
                              ? 'bg-orange-500'
                              : 'bg-gray-300'
                          }`}
                          style={{ width: `${landmark.progress}%` }}
                        />
                      </div>
                    </div>

                    {/* Location Link */}
                    <button
                      className="flex-shrink-0 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title={landmark.coordinates}
                    >
                      <i className="ri-map-pin-line text-sm"></i>
                    </button>
                  </div>
                ))}
              </div>

            </div>

          </div>
          {/* Contract value & payment metrics — full-width row spanning the whole
              section so they spread across instead of being cramped on one side. */}
          <div className="px-4 sm:px-5 py-4 border-t border-slate-200 dark:border-gray-700">
            <div className="flex items-center justify-between rounded-xl bg-slate-50 dark:bg-gray-700/40 border border-slate-200 dark:border-gray-600 px-4 py-3 mb-3">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-gray-300">
                <i className="ri-file-list-3-line text-slate-500"></i> Total Contract Value
              </span>
              <span className="text-lg font-bold text-slate-900 dark:text-gray-100 tabular-nums">{formatCurrency(workData.financial.tcv)}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Work Completed', value: workData.financial.workCompleted, icon: 'ri-hammer-line', text: 'text-slate-900' },
                { label: 'Pending Workdone', value: workData.financial.pendingWorkdone, icon: 'ri-loader-line', text: 'text-rose-600' },
                { label: 'Paid to Contractor', value: workData.financial.paidToContractor, icon: 'ri-checkbox-circle-line', text: 'text-emerald-600' },
                { label: 'Pending Payment', value: workData.financial.pendingPayment, icon: 'ri-time-line', text: 'text-amber-600' },
              ].map((m) => (
                <div key={m.label} className="min-w-0 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3.5 py-3 shadow-sm">
                  <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 dark:text-gray-400">
                    <i className={`${m.icon} text-indigo-500`}></i>{m.label}
                  </span>
                  <p className={`text-base font-bold ${m.text} dark:text-gray-100 tabular-nums mt-1 truncate`}>{formatCurrency(m.value)}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200/60 dark:border-gray-700 text-xs">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 bg-green-500 rounded-full"></span>
                  <span className="text-gray-600 dark:text-gray-400">Paid: {safePercent(workData.financial.paidToContractor, workData.financial.tcv)}%</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 bg-orange-500 rounded-full"></span>
                  <span className="text-gray-600 dark:text-gray-400">Pending: {safePercent(workData.financial.pendingPayment, workData.financial.tcv)}%</span>
                </span>
              </div>
              <span className="text-gray-500 dark:text-gray-400">of TCV</span>
            </div>
          </div>
          </div>
          </div>{/* /sec-status */}
        </div>{/* /content sheet */}
        </div>{/* /section-menu flex */}

        {/* Tabs Section — order-last pushes it to the bottom of the column, below the
            detail cards (Work Location, Contractor, Work Details, Financial, etc.). */}
        <div className="order-last bg-white dark:bg-gray-800 rounded-2xl shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)] border border-slate-200 dark:border-gray-700 ring-1 ring-slate-200/60 dark:ring-gray-700 overflow-hidden">
          {/* Tab Headers - Horizontally scrollable on small screens */}
          <div className="py-4 px-4 border-b border-gray-100 dark:border-gray-700">
            <div className="overflow-x-auto scrollbar-hide text-center" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div ref={tabsContainerRef} className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1 relative min-w-max mx-auto">
                {/* Animated sliding indicator */}
                <div
                  className="absolute top-1 bottom-1 rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 shadow-lg transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
                  style={{
                    left: indicatorStyle.left,
                    width: indicatorStyle.width,
                  }}
                />
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    data-tab={tab.id}
                    onClick={(e) => {
                      setActiveTab(tab.id);
                      (e.currentTarget as HTMLElement).scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    }}
                    className={`relative z-10 flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium whitespace-nowrap rounded-lg transition-colors duration-300 ${
                      activeTab === tab.id
                        ? 'text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                    }`}
                  >
                    <i className={`${tab.icon} text-base sm:text-lg`}></i>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-5">
            {activeTab === 'funding' && apiData?.workId && <FundingTab workId={apiData.workId} workCode={apiData.workCode} workName={apiData.workName} sanctionAmount={apiData.sanctionAmount} />}
            {activeTab === 'documents' && <DocumentsTab workId={apiData?.workId} tenderId={apiData?.tender?.tenderId} />}
            {activeTab === 'award' && <AwardTab apiData={apiData} onViewTenderDetails={() => setShowTenderProfile(true)} />}
            {activeTab === 'design_boq' && <DesignBOQTab workId={apiData?.workId} />}
            {activeTab === 'progress' && <ProgressUpdatesTab
              contractor={workData.contractor}
              engineer={workData.engineer}
              landmarks={workData.landmarks}
              scheduleId={scheduleId}
              workId={workId}
              workCode={workData.workId}
              totalProjectCost={Number(apiData?.sanctionAmount || apiData?.as_at || apiData?.snctn_at || 0)}
              onContractorClick={() => setShowContractorModal(true)}
              onEngineerClick={() => setShowEngineerModal(true)}
              onProgressSaved={loadGallery}
            />}
            {activeTab === 'inspections' && <InspectionsTab workId={workId ? parseInt(workId, 10) : undefined} />}
            {activeTab === 'quality' && <QualityTab workId={apiData?.workId} workCode={apiData?.workCode} />}
            {activeTab === 'communication' && <CommunicationTab />}
            {activeTab === 'map' && <MapTab workId={apiData?.workId} workCode={apiData?.workCode} workName={apiData?.workName} workStatus={apiData?.status?.name} workType={apiData?.type?.name} workCategory={apiData?.category?.name} schemeName={apiData?.scheme?.name} sanctionAmount={apiData?.sanctionAmount} workTypeId={apiData?.type?.id} udaId={apiData?.udaId} udaName={apiData?.ulb} sachivalayams={sachivalayams} location={workData.location} />}
            {activeTab === 'workflows' && <WorkflowsTab />}
          </div>
        </div>

        {/* ===== Site Photos — full-width gallery grouped by month. Kept LAST so the
            profile reads top-to-bottom: details → current status → history → photos.
            order-last keeps it below the Tabs section in the flex column. ===== */}
        <div className="order-last bg-white dark:bg-gray-800 rounded-2xl shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)] border border-slate-200 dark:border-gray-700 ring-1 ring-slate-200/60 dark:ring-gray-700 overflow-hidden">
          <div className="px-4 sm:px-5 py-4 border-b border-slate-200 dark:border-gray-700 flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-gray-300">
              <i className="ri-image-2-line text-violet-500"></i> Site Photos
              <span className="text-[11px] font-medium bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-2 py-0.5 rounded-full">{galleryPhotos.length}</span>
            </span>
            <button onClick={() => loadGallery()} disabled={galleryLoading}
              className="text-[11px] text-violet-600 hover:text-violet-800 flex items-center gap-1 disabled:opacity-50">
              <i className={`ri-refresh-line ${galleryLoading ? 'animate-spin' : ''}`}></i> Refresh
            </button>
          </div>
          <div className="px-4 sm:px-5 py-4">
          {(() => {
            const openPhoto = async (p: GalleryPhoto) => {
              try {
                const url = await attachmentApi.fetchBlobUrl(p.id);
                setGalleryPreview({
                  url,
                  caption: `${p.milestone || 'Progress'}${p.dt ? ' • ' + formatDate(p.dt) : ''}${p.remark ? ' — ' + p.remark : ''}`,
                });
              } catch { /* inline thumbnail stays */ }
            };
            if (galleryPhotos.length === 0 && galleryLoading) {
              return (
                <div className="flex gap-3 overflow-hidden py-2">
                  {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} shape="rounded" className="h-32 w-44" />)}
                </div>
              );
            }
            if (galleryPhotos.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center py-10 text-slate-400 dark:text-gray-500">
                  <i className="ri-image-line text-3xl mb-2"></i>
                  <span className="text-xs">No progress photos uploaded yet</span>
                </div>
              );
            }
            // Group every photo by month-year (newest month first); undated photos last.
            type MonthGroup = { key: string; label: string; ts: number; photos: GalleryPhoto[] };
            const byKey = new Map<string, MonthGroup>();
            const ordered: MonthGroup[] = [];
            for (const p of galleryPhotos) {
              const d = p.dt ? new Date(p.dt) : null;
              const valid = !!d && !isNaN(d.getTime());
              const key = valid ? `${d!.getFullYear()}-${d!.getMonth()}` : 'undated';
              const label = valid ? d!.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'Undated';
              const ts = valid ? d!.getTime() : -Infinity;
              let g = byKey.get(key);
              if (!g) { g = { key, label, ts, photos: [] }; byKey.set(key, g); ordered.push(g); }
              g.photos.push(p);
            }
            ordered.sort((a, b) => b.ts - a.ts);
            return (
              <div className="space-y-5">
                {ordered.map((g) => (
                  <div key={g.key}>
                    {/* Month label + count */}
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 text-white text-xs font-semibold shadow-sm">
                        <i className="ri-calendar-event-line"></i> {g.label}
                      </span>
                      <span className="text-[11px] text-slate-400">{g.photos.length} photo{g.photos.length > 1 ? 's' : ''}</span>
                      <div className="flex-1 h-px bg-slate-100 dark:bg-gray-700"></div>
                    </div>
                    {/* Horizontal strip — scrolls sideways when a month has many photos */}
                    <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                      {g.photos.map((p, i) => (
                        <button key={`${p.id}-${i}`} type="button" onClick={() => openPhoto(p)}
                          className="group relative flex-shrink-0 w-44 h-32 rounded-xl overflow-hidden border border-slate-200 dark:border-gray-600 hover:border-violet-400 hover:ring-2 hover:ring-violet-200 dark:hover:ring-violet-900/40 transition">
                          <AuthImage attchmntId={p.id} alt={p.milestone} className="w-full h-full object-cover" />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 pt-5 pb-1.5 pointer-events-none">
                            <span className="block text-[10px] text-white/90 truncate leading-tight">{p.milestone || 'Progress'}</span>
                            {p.dt && <span className="block text-[9px] text-white/60 leading-tight">{formatDate(p.dt)}</span>}
                          </div>
                          <div className="absolute inset-0 group-hover:bg-black/20 flex items-center justify-center pointer-events-none">
                            <i className="ri-zoom-in-line text-white opacity-0 group-hover:opacity-100"></i>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          </div>
        </div>
        </div>
        )}
      </div>

      {/* Contractor Projects Modal */}
      <ContractorProfile
        isOpen={showContractorModal}
        onClose={() => setShowContractorModal(false)}
        contractor={workData.contractor}
        onWorkIdClick={(clickedWorkId) => {
          setShowContractorModal(false);
          // Work ID clicked from contractor profile - just close (already viewing a work)
        }}
      />

      {/* Contractor Selector */}
      <ContractorSelector
        isOpen={showContractorSelector}
        onClose={() => setShowContractorSelector(false)}
        onSelect={async (contractor) => {
          if (!workId) return;
          try {
            await workScheduleApi.assignContractor(workId, contractor.cntrctr_id);
            // Refresh work data to show updated contractor
            const data = await worksApi.getById(workId);
            setApiData(data);
            setShowContractorSelector(false);
          } catch { /* silent */ }
        }}
      />

      {/* Engineer Selector — assign an engineer to this work */}
      {showEngineerSelector && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center z-[10000] p-4 pt-24"
          onClick={() => !assigningEngineer && setShowEngineerSelector(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-visible"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`${WMS_POPUP_HEADER} rounded-t-2xl`}>
              <div className="flex items-center gap-2 min-w-0">
                <i className="ri-user-add-line text-base text-white/90"></i>
                <h3 className={WMS_POPUP_TITLE}>Assign Engineer</h3>
              </div>
              <button
                onClick={() => !assigningEngineer && setShowEngineerSelector(false)}
                className={WMS_POPUP_CLOSE}
                title="Close"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="p-5">
              <EmployeeSelector
                selectedEmployee={null}
                onSelect={handleAssignEngineer}
                onClear={() => {}}
                label="Search and select an engineer"
                placeholder="Search by name, phone, employee no..."
                disabled={assigningEngineer}
              />
              {assigningEngineer && (
                <div className="flex items-center gap-2 mt-3 text-sm text-violet-600">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Assigning engineer...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Engineer Projects Modal */}
      <EngineerProfile
        isOpen={showEngineerModal}
        onClose={() => setShowEngineerModal(false)}
        engineer={workData.engineer}
        onWorkIdClick={(clickedWorkId) => {
          setShowEngineerModal(false);
          // Work ID clicked from engineer profile - just close (already viewing a work)
        }}
      />

      {/* Tender Profile Modal */}
      <TenderProfile
        isOpen={showTenderProfile}
        onClose={() => setShowTenderProfile(false)}
        workId={workId ? parseInt(workId, 10) : 0}
        workName={workData.workName}
        workCode={workData.workId}
      />
      <SachivalayamSelector
        isOpen={showSvmSelector}
        onClose={() => setShowSvmSelector(false)}
        onSave={handleSvmSave}
        udaId={apiData?.udaId || 0}
        udaName={apiData?.ulb}
        workCode={workData.workId}
        workName={workData.workName}
        initialSelectedIds={sachivalayams.map(s => s.svm_id)}
        saving={savingSvm}
      />

      {/* Site Photo lightbox — full-size preview of a gallery photo */}
      {galleryPreview && (
        <div
          className="fixed inset-0 z-[10001] bg-black/90 flex items-center justify-center p-4"
          onClick={() => { URL.revokeObjectURL(galleryPreview.url); setGalleryPreview(null); }}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
            onClick={() => { URL.revokeObjectURL(galleryPreview.url); setGalleryPreview(null); }}
            title="Close"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
          <div className="max-w-5xl max-h-[88vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={galleryPreview.url} alt={galleryPreview.caption} className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl" />
            {galleryPreview.caption && (
              <p className="text-white/90 text-center mt-4 text-sm max-w-2xl">{galleryPreview.caption}</p>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// Funding Tab - Shows CAPEX/OPEX funding source breakdown for the work
function FundingTab({ workId, workCode, workName, sanctionAmount }: { workId: number; workCode: string; workName: string; sanctionAmount?: number }) {
  const [funding, setFunding] = useState<FundingPattern | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<FundingPattern | null>(null);
  const [saving, setSaving] = useState(false);

  const n = (v: any): number => parseFloat(v) || 0;
  /* A recorded zero and an unrecorded amount are different facts, and this used to
   * print both as "-": `num > 0 ? ... : '-'`.
   *
   * That is what made the only funding pattern in the database unreadable. WRK/2026-27/0008
   * HAS a pattern row (fndng_id 1) that was saved with 0.00 in all twenty-two amount
   * columns, so every cell rendered "-" and the card was indistinguishable from a work
   * with no pattern at all — while the "No funding pattern configured yet" empty state
   * stayed hidden, because a row did exist. The two states need different fixes: one is
   * data entry, the other is correcting a bad save.
   *
   * Now: a number, including 0, prints as an amount. Only null/undefined/blank/NaN —
   * genuinely nothing recorded — prints the dash. */
  const fmtCr = (v: any) => {
    if (v === null || v === undefined || v === '') return '-';
    const num = parseFloat(v as any);
    if (!Number.isFinite(num)) return '-';
    return `${num.toFixed(2)} Cr`;
  };
  // snctn_at is stored as raw rupees; funding_pattern.*_at columns are stored as crores.
  const sanctionCr = n(sanctionAmount) / 1e7;

  useEffect(() => {
    setLoading(true);
    fundingPatternApi.getByWorkId(workId)
      .then(data => {
        if (data) {
          const parsed: FundingPattern = {
            ...data,
            capex_goi_at: n(data.capex_goi_at), capex_goap_at: n(data.capex_goap_at),
            capex_ulb_at: n(data.capex_ulb_at), capex_15fc_at: n(data.capex_15fc_at),
            capex_ppp_at: n(data.capex_ppp_at), capex_totl_at: n(data.capex_totl_at),
            opex_goi_at: n(data.opex_goi_at), opex_goap_at: n(data.opex_goap_at),
            opex_ulb_at: n(data.opex_ulb_at), opex_15fc_at: n(data.opex_15fc_at),
            opex_ppp_at: n(data.opex_ppp_at), opex_totl_at: n(data.opex_totl_at),
            totl_prjct_cst_at: n(data.totl_prjct_cst_at),
          };
          setFunding(parsed);
        }
      })
      .catch(err => console.error('Load funding error:', err))
      .finally(() => setLoading(false));
  }, [workId]);

  const recalc = (p: FundingPattern): FundingPattern => {
    p.capex_totl_at = n(p.capex_goi_at) + n(p.capex_goap_at) + n(p.capex_ulb_at) + n(p.capex_15fc_at) + n(p.capex_ppp_at);
    p.opex_totl_at = n(p.opex_goi_at) + n(p.opex_goap_at) + n(p.opex_ulb_at) + n(p.opex_15fc_at) + n(p.opex_ppp_at);
    p.totl_prjct_cst_at = p.capex_totl_at + p.opex_totl_at;
    return p;
  };

  const updateField = (field: string, value: number) => {
    setDraft(prev => prev ? recalc({ ...prev, [field]: value }) : prev);
  };

  const handleSave = async () => {
    if (!draft) return;
    if (sanctionCr > 0 && draft.totl_prjct_cst_at > sanctionCr) {
      alert(`Total (${Number(draft.totl_prjct_cst_at).toFixed(2)} Cr) exceeds AS Amount (${sanctionCr.toFixed(2)} Cr)`);
      return;
    }
    setSaving(true);
    try {
      const saved = await fundingPatternApi.upsert(workId, { ...draft, src_cd: 'MANUAL' });
      const parsed = { ...saved, capex_goi_at: n(saved.capex_goi_at), capex_goap_at: n(saved.capex_goap_at), capex_ulb_at: n(saved.capex_ulb_at), capex_15fc_at: n(saved.capex_15fc_at), capex_ppp_at: n(saved.capex_ppp_at), capex_totl_at: n(saved.capex_totl_at), opex_goi_at: n(saved.opex_goi_at), opex_goap_at: n(saved.opex_goap_at), opex_ulb_at: n(saved.opex_ulb_at), opex_15fc_at: n(saved.opex_15fc_at), opex_ppp_at: n(saved.opex_ppp_at), opex_totl_at: n(saved.opex_totl_at), totl_prjct_cst_at: n(saved.totl_prjct_cst_at) };
      setFunding(parsed);
      setEditing(false);
      setDraft(null);
    } catch (err) {
      console.error('Save funding error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <TableSkeleton />;

  const data = editing ? draft : funding;
  const empty: FundingPattern = { wrk_id: workId, capex_goi_at: 0, capex_goap_at: 0, capex_ulb_at: 0, capex_15fc_at: 0, capex_ppp_at: 0, capex_totl_at: 0, opex_goi_at: 0, opex_goap_at: 0, opex_ulb_at: 0, opex_15fc_at: 0, opex_ppp_at: 0, opex_totl_at: 0, totl_prjct_cst_at: 0 };
  const p = data || empty;
  // Totals are computed from the individual CAPEX/OPEX cells so the table stays internally consistent
  // (CAPEX row + OPEX row = bottom Total). The gap between this and the Sanctioned Amount is surfaced
  // by the under-allocated warning below.
  const capexTotal = n(p.capex_goi_at) + n(p.capex_goap_at) + n(p.capex_ulb_at) + n(p.capex_15fc_at) + n(p.capex_ppp_at);
  const opexTotal = n(p.opex_goi_at) + n(p.opex_goap_at) + n(p.opex_ulb_at) + n(p.opex_15fc_at) + n(p.opex_ppp_at);
  const projectTotal = capexTotal + opexTotal;
  const exceeds = sanctionCr > 0 && projectTotal > sanctionCr;
  // Under-allocated: any positive gap of >= 1 paisa between AS and the funding-source breakdown.
  const underAllocated = sanctionCr > 0 && projectTotal > 0 && (sanctionCr - projectTotal) >= 0.01;

  const SOURCES = [
    { label: 'GoI', capex: 'capex_goi_at', opex: 'opex_goi_at' },
    { label: 'GoAP', capex: 'capex_goap_at', opex: 'opex_goap_at' },
    { label: 'UDA Share', capex: 'capex_ulb_at', opex: 'opex_ulb_at' },
    { label: '15th FC', capex: 'capex_15fc_at', opex: 'opex_15fc_at' },
    { label: 'PPP', capex: 'capex_ppp_at', opex: 'opex_ppp_at' },
  ];

  const renderVal = (field: string) => {
    if (!editing) return <span className="font-mono text-sm text-gray-800 block text-right">{fmtCr((p as any)[field])}</span>;
    return (
      <input type="number" step="0.01" min="0" value={n((p as any)[field]) || ''} onChange={e => updateField(field, parseFloat(e.target.value) || 0)}
        className="w-full px-2 py-1.5 text-sm font-mono text-right border border-gray-300 rounded focus:ring-1 focus:ring-violet-400 focus:border-violet-400 focus:outline-none" placeholder="0.00" />
    );
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-50 to-violet-100 ring-1 ring-violet-200/60">
            <i className="ri-money-rupee-circle-line text-violet-600 text-base"></i>
          </span>
          <h3 className="font-semibold text-gray-800 tracking-tight">Funding Pattern</h3>
          {sanctionCr > 0 && <span className="text-xs text-gray-500">(AS: {fmtCr(sanctionCr)})</span>}
        </div>
        {!editing ? (
          <button onClick={() => { setEditing(true); setDraft(funding ? { ...funding } : { ...empty }); }}
            className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 flex items-center gap-1">
            <i className="ri-edit-line"></i> Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => { setEditing(false); setDraft(null); }} className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 text-xs bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-lg hover:from-violet-600 hover:to-purple-600 shadow-sm shadow-violet-200/60 flex items-center gap-1 disabled:opacity-50">
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>} Save
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <table data-wms-table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 border-b">
              <th className="px-3 py-2 text-left font-semibold text-gray-600 w-24">Source</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-600">GoI</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-600">GoAP</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-600">UDA Share</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-600">15th FC</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-600">PPP</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-700 bg-gray-200 w-28">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b bg-blue-50/40">
              <td className="px-3 py-2.5 font-semibold text-blue-800">CAPEX</td>
              {SOURCES.map(s => <td key={s.capex} className="px-3 py-2.5 text-right">{renderVal(s.capex)}</td>)}
              <td className="px-3 py-2.5 text-right font-mono font-bold text-blue-900 bg-blue-100">{fmtCr(capexTotal)}</td>
            </tr>
            <tr className="border-b bg-orange-50/40">
              <td className="px-3 py-2.5 font-semibold text-orange-800">OPEX</td>
              {SOURCES.map(s => <td key={s.opex} className="px-3 py-2.5 text-right">{renderVal(s.opex)}</td>)}
              <td className="px-3 py-2.5 text-right font-mono font-bold text-orange-900 bg-orange-100">{fmtCr(opexTotal)}</td>
            </tr>
            <tr className="bg-gray-50 font-semibold">
              <td className="px-3 py-2.5 text-gray-800">Total</td>
              {SOURCES.map(s => (
                <td key={`total-${s.label}`} className="px-3 py-2.5 text-right font-mono text-gray-800">
                  {fmtCr(n((p as any)[s.capex]) + n((p as any)[s.opex]))}
                </td>
              ))}
              <td className={`px-3 py-2.5 text-right font-mono font-bold text-sm ${exceeds ? 'text-red-700 bg-red-100' : 'text-amber-900 bg-amber-100'}`}>
                {fmtCr(projectTotal)}
                {exceeds && <i className="ri-error-warning-fill ml-1 text-red-500"></i>}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-gray-400 text-right">All values in Cr.</div>

      {exceeds && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 flex items-center gap-2 text-xs text-red-700">
          <i className="ri-error-warning-fill text-red-500"></i>
          Total Project Cost exceeds Admin Sanction Amount by {(projectTotal - sanctionCr).toFixed(2)} Cr
        </div>
      )}

      {underAllocated && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-center gap-2 text-xs text-amber-800">
          <i className="ri-alert-line text-amber-500"></i>
          Funding sources cover only {fmtCr(projectTotal)} of the {fmtCr(sanctionCr)} sanctioned amount — {(sanctionCr - projectTotal).toFixed(2)} Cr is unallocated across CAPEX/OPEX sources.
        </div>
      )}

      {/* A pattern row that carries no amounts. Distinct from having no row at all:
          somebody saved the form empty, so this needs correcting rather than creating.
          The backend now refuses such a save, but rows written before that guard
          existed are still in the database and must be legible. */}
      {funding && !editing && projectTotal <= 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-800">
          <i className="ri-error-warning-line text-amber-500 mt-0.5"></i>
          <div>
            <div className="font-semibold">Not configured — this funding pattern was saved with no amounts.</div>
            <div className="mt-0.5 text-amber-700">
              Every source reads 0.00{sanctionCr > 0 ? `, against a sanctioned amount of ${fmtCr(sanctionCr)}` : ''}.
              Use Edit to enter the actual GoI / GoAP / UDA Share / 15th FC / PPP split.
            </div>
          </div>
        </div>
      )}

      {!funding && !editing && (
        <div className="text-center py-6 text-gray-400">
          <i className="ri-money-rupee-circle-line text-3xl"></i>
          <p className="mt-2 text-sm">No funding pattern configured yet</p>
          <button onClick={() => { setEditing(true); setDraft({ ...empty }); }}
            className="mt-2 px-4 py-2 text-xs bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-lg hover:from-violet-600 hover:to-purple-600 shadow-sm shadow-violet-200/60 transition-all hover:shadow-md">
            Add Funding Pattern
          </button>
        </div>
      )}
    </div>
  );
}

// Tab Components
function DocumentsTab({ workId, tenderId }: { workId?: number; tenderId?: number }) {
  const [docGroups, setDocGroups] = useState<WorkDocGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const loadDocs = useCallback(async () => {
    if (!workId) return;
    setLoading(true);
    try {
      const groups = await documentConfigApi.getWorkAllDocs(workId, tenderId);
      setDocGroups(groups);
      // Auto-expand groups that have docs
      const expanded: Record<string, boolean> = {};
      groups.forEach(g => { expanded[g.hndlr_cd] = g.docs.length > 0; });
      setExpandedGroups(expanded);
    } catch (e) {
      console.error('Failed to load work documents:', e);
    } finally {
      setLoading(false);
    }
  }, [workId, tenderId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const toggleGroup = (key: string) =>
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const entityLabel = (type: string) => type === 'TENDER' ? 'Tender' : 'Work';

  return (
    <div className="space-y-4">
      {/* Upload section for work-specific documents */}
      {workId && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <i className="ri-upload-cloud-line text-blue-500"></i>
            Upload Work Documents
          </h4>
          <DocumentUploader handlerCd="WORK_DOCS" refId={workId} refNm="work" />
        </div>
      )}

      {/* All related documents (work + tender) grouped by process */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <i className="ri-folder-line text-orange-500"></i>
          All Related Documents
          {tenderId && <span className="text-[10px] text-gray-400 font-normal">(includes tender documents)</span>}
        </h4>

        {loading ? (
          <ListSkeleton rows={3} avatar={false} />
        ) : docGroups.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm">
            <i className="ri-folder-open-line text-3xl mb-2 block"></i>
            No documents found
          </div>
        ) : (
          <div className="space-y-2">
            {docGroups.map((group) => (
              <div key={group.hndlr_cd} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Group header */}
                <button
                  onClick={() => toggleGroup(group.hndlr_cd)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <i className={`${group.icon_cd || 'ri-folder-line'} text-blue-500`}></i>
                    <span className="text-sm font-medium text-gray-700">{group.prcss_nm}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                      group.entity_type_cd === 'TENDER' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'
                    }`}>
                      {entityLabel(group.entity_type_cd)}
                    </span>
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded">
                      {group.docs.length} file{group.docs.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <i className={`ri-arrow-${expandedGroups[group.hndlr_cd] ? 'up' : 'down'}-s-line text-gray-400`}></i>
                </button>

                {/* Group docs */}
                {expandedGroups[group.hndlr_cd] && (
                  <div className="px-3 py-2 space-y-1">
                    {group.docs.map((d) => (
                      <div key={d.doc_upld_id} className="flex items-center justify-between bg-green-50 rounded px-2.5 py-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <i className="ri-file-check-line text-green-600 text-sm shrink-0"></i>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <a
                                href={d.file_url || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-green-700 hover:underline truncate"
                                title={d.orgnl_file_nm || d.file_nm}
                              >
                                {d.orgnl_file_nm || d.file_nm}
                              </a>
                              <span className="text-[10px] text-gray-400 shrink-0">{d.doc_type_nm}</span>
                              {d.file_sz && (
                                <span className="text-[10px] text-gray-400 shrink-0">{formatFileSize(d.file_sz)}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-[10px] text-gray-500">
                              {d.doc_nu && <span>#{d.doc_nu}</span>}
                              {d.issue_dt && <span>Issued: {d.issue_dt}</span>}
                              {d.expiry_dt && (
                                <span className={new Date(d.expiry_dt) < new Date() ? 'text-red-500' : ''}>
                                  Exp: {d.expiry_dt}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <a
                          href={d.file_url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 ml-2 shrink-0"
                          title="View / Download"
                        >
                          <i className="ri-external-link-line text-sm"></i>
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Best-effort string rendering of any captured value from prgrs_updt_flds_jn.
function fmtCstmFldValue(raw: any): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  if (Array.isArray(raw)) return raw.length ? raw.join(', ') : '—';
  if (typeof raw === 'object') {
    if ('address' in raw) return raw.address || '—';
    if ('name' in raw)    return raw.name || '—';
    if ('lat' in raw && 'lng' in raw) return `${raw.lat}, ${raw.lng}`;
    try { return JSON.stringify(raw); } catch { return '—'; }
  }
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  return s;
}

// ─── Milestone custom-field renderer (inline) ─────────────────────────────────
// Compact widget that renders the user-defined milestone fields inside the
// Work Profile's Progress Updates tab.  Mirrors the same type coverage as
// <FormField> in ../ProgressUpdate.tsx, trimmed to what fits in this form.
function MilestoneCstmField({ field, value, onChange }: { field: MilestoneCustomField; value: any; onChange: (val: any) => void }) {
  // Hooks must run in the same order on every render, so this cannot live inside the
  // file/image branch below — the branches above it return early, so on a text field
  // the hook was never reached and React's hook order shifted between renders. Only
  // the upload branch reads it; declaring it here costs one unused state slot and
  // makes the order fixed. (Was an eslint react-hooks/rules-of-hooks error.)
  const [busy, setBusy] = useState(false);
  const type = (field.fld_type_cd || 'text').toLowerCase();
  const placeholder = field.plchldr_tx || `Enter ${field.fld_nm}`;
  const cls = 'w-full px-2 py-1.5 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-violet-300 focus:border-violet-400';
  if (type === 'textarea') return <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={2} className={`${cls} resize-none`} />;
  if (type === 'number')   return <input type="number" value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder="0" className={cls} />;
  if (type === 'date')     return <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} className={cls} />;
  if (type === 'datetime') return <input type="datetime-local" value={value || ''} onChange={e => onChange(e.target.value)} className={cls} />;
  if (type === 'select') {
    const opts = field.optns_jn || [];
    return (
      <select value={value || ''} onChange={e => onChange(e.target.value)} className={cls}>
        <option value="">Select {field.fld_nm}</option>
        {opts.map((o, i) => <option key={i} value={o.value || o.label}>{o.label}</option>)}
      </select>
    );
  }
  if (type === 'multiselect') {
    const opts = field.optns_jn || [];
    const selected: string[] = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-0.5 border rounded p-1.5 max-h-24 overflow-auto">
        {opts.map((o, i) => {
          const v = o.value || o.label;
          const checked = selected.includes(v);
          return (
            <label key={i} className="flex items-center gap-1.5 cursor-pointer text-xs">
              <input type="checkbox" checked={checked}
                onChange={() => onChange(checked ? selected.filter(s => s !== v) : [...selected, v])}
                className="w-3 h-3 rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
              <span className="text-gray-700 dark:text-gray-300">{o.label}</span>
            </label>
          );
        })}
      </div>
    );
  }
  if (type === 'radio') {
    const opts = field.optns_jn || [];
    return (
      <div className="flex flex-wrap gap-2">
        {opts.map((o, i) => {
          const v = o.value || o.label;
          return (
            <label key={i} className="flex items-center gap-1 cursor-pointer text-xs">
              <input type="radio" name={`cf_${field.fld_cd}`} checked={value === v} onChange={() => onChange(v)}
                className="border-gray-300 text-violet-600 focus:ring-violet-500" />
              <span className="text-gray-700 dark:text-gray-300">{o.label}</span>
            </label>
          );
        })}
      </div>
    );
  }
  if (type === 'checkbox') {
    const opts = field.optns_jn || [];
    if (opts.length <= 1) {
      return (
        <label className="flex items-center gap-1.5 cursor-pointer text-xs">
          <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
            className="w-3 h-3 rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
          <span className="text-gray-700 dark:text-gray-300">{opts[0]?.label || field.fld_nm}</span>
        </label>
      );
    }
    const selected: string[] = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-0.5">
        {opts.map((o, i) => {
          const v = o.value || o.label;
          const checked = selected.includes(v);
          return (
            <label key={i} className="flex items-center gap-1.5 cursor-pointer text-xs">
              <input type="checkbox" checked={checked}
                onChange={() => onChange(checked ? selected.filter(s => s !== v) : [...selected, v])}
                className="w-3 h-3 rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
              <span className="text-gray-700 dark:text-gray-300">{o.label}</span>
            </label>
          );
        })}
      </div>
    );
  }
  if (type === 'toggle') {
    return (
      <label className="flex items-center gap-2 cursor-pointer">
        <div className="relative">
          <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} className="sr-only peer" />
          <div className="w-8 h-4 bg-gray-300 peer-checked:bg-orange-500 rounded-full transition-colors"></div>
          <div className={`absolute top-0.5 ${value ? 'left-4' : 'left-0.5'} w-3 h-3 bg-white rounded-full shadow transition-all`}></div>
        </div>
        <span className="text-xs text-gray-700 dark:text-gray-300">{field.fld_nm}</span>
      </label>
    );
  }
  if (type === 'file' || type === 'image') {
    // Upload to ds_wms.wms_attchmnt_t (BYTEA). Field value = metadata only.
    const accept = type === 'image' ? 'image/*' : '*/*';
    const v: any = value && typeof value === 'object' && value.attchmnt_id ? value : null;
    const handleFile = async (file?: File | null) => {
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        alert(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum 10 MB.`);
        return;
      }
      setBusy(true);
      try {
        const meta = await attachmentApi.upload(file, 'mlstn_prgrs_cstm_fld');
        onChange({
          attchmnt_id: meta.attchmnt_id,
          file_nm:     meta.file_nm,
          cntnt_type:  meta.cntnt_type,
          file_sz:     meta.file_sz,
        });
      } catch (e: any) {
        alert(e?.response?.data?.message || e?.message || 'Upload failed');
      } finally {
        setBusy(false);
      }
    };
    const isImg = type === 'image' || (v?.cntnt_type || '').startsWith('image/');
    return (
      <label className="block border border-dashed border-gray-300 hover:border-orange-400 rounded p-2 text-center cursor-pointer transition-colors text-xs">
        <input type="file" accept={accept} className="hidden" disabled={busy}
          onChange={e => handleFile(e.target.files?.[0])} />
        {busy ? (
          <div className="flex items-center justify-center gap-2 py-1">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-500"></div>
            <span className="text-[10px] text-gray-500">Uploading…</span>
          </div>
        ) : v ? (
          <div className="flex items-center gap-2">
            {isImg ? (
              <img src={attachmentApi.url(v.attchmnt_id)} alt={v.file_nm} className="h-10 w-10 object-cover rounded border" />
            ) : (
              <i className="ri-attachment-2 text-orange-500"></i>
            )}
            <div className="text-left flex-1 min-w-0">
              <p className="text-[11px] font-medium text-gray-700 truncate">{v.file_nm}</p>
              <p className="text-[10px] text-gray-400">{((v.file_sz || 0) / 1024).toFixed(1)} KB</p>
            </div>
            <button type="button"
              onClick={(e) => {
                e.preventDefault();
                attachmentApi.remove(v.attchmnt_id).catch(() => {});
                onChange(null);
              }}
              className="text-red-500" title="Remove">
              <i className="ri-close-line"></i>
            </button>
          </div>
        ) : (
          <>
            <i className={`${type === 'image' ? 'ri-image-add-line' : 'ri-upload-2-line'} text-gray-400 text-lg`}></i>
            <p className="text-[10px] text-gray-500">Click to upload {type} (max 10 MB · stored in DB)</p>
          </>
        )}
      </label>
    );
  }
  // Default: text
  return <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={cls} />;
}

interface MilestoneCustomField {
  fld_cd: string;
  fld_nm: string;
  fld_type_cd: string;
  is_rqrd_in?: boolean;
  plchldr_tx?: string;
  hlp_tx?: string;
  optns_jn?: { label: string; value: string }[];
}

interface Landmark {
  id: number;
  name: string;
  status: string;
  progress: number;
  coordinates: string;
  /** Milestone weightage — needed to derive Planned Amount = wt × TPC / 100. */
  wghtg_pct_nu?: number;
  /** Stored override for the auto-derived planned amount. */
  plnd_amt_at?: number | null;
  /** When true, Planned Amount is surfaced in the Progress Update UI. */
  shw_plnd_amt_in_prgrs_in?: boolean;
  cstm_flds_jn?: MilestoneCustomField[];
}

/**
 * <img> for an attachment stored in wms_attchmnt_t. The bytes are fetched via
 * axios (so the JWT bearer token from the shared interceptor travels with the
 * request) and wrapped in a blob URL. A plain `<img src={attachmentApi.url(id)}>`
 * hits the backend WITHOUT auth → 401 → the browser's broken-image icon, which
 * is why progress photos showed as broken. The blob URL is revoked on unmount.
 */
function AuthImage({ attchmntId, alt, className }: { attchmntId: number | string; alt?: string; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    attachmentApi.fetchBlobUrl(attchmntId)
      .then(u => { if (cancelled) { URL.revokeObjectURL(u); return; } url = u; setSrc(u); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [attchmntId]);
  if (failed) {
    return (
      <div className={`${className || ''} flex items-center justify-center bg-gray-100 text-gray-400`} title="Could not load image">
        <i className="ri-image-line"></i>
      </div>
    );
  }
  if (!src) {
    return (
      <Skeleton shape="rect" className={`min-h-[4rem] ${className || ''}`} />
    );
  }
  return <img src={src} alt={alt} className={className} />;
}

function ProgressUpdatesTab({ landmarks, scheduleId, workId, totalProjectCost, onContractorClick, onEngineerClick, onProgressSaved }: {
  contractor: { name: string; contactPerson: string; phone: string; email: string; registrationNo: string; gstNo: string };
  engineer: { name: string; designation: string; department: string; phone: string; email: string };
  landmarks: Landmark[];
  scheduleId: number | null;
  workId?: string;
  workCode?: string;
  totalProjectCost: number;
  onContractorClick: () => void;
  onEngineerClick: () => void;
  // Notifies the parent so the Site Photos gallery refetches after a new upload.
  onProgressSaved?: () => void;
}) {
  // Sub-component custom field state — kept local so it's in scope for all
  // the JSX inside this component (rendering crashes if declared in the parent).
  const [subFieldDefs, setSubFieldDefs] = useState<Record<number, any[]>>({});
  const [subFieldVals, setSubFieldVals] = useState<Record<number, Record<string, string>>>({});
  const [subFieldValsLoaded, setSubFieldValsLoaded] = useState<Record<number, Record<string, string>>>({});

  // Load defs + previously-submitted values on mount / when work changes.
  useEffect(() => {
    const wrkIdNum = Number(workId);
    if (!Number.isFinite(wrkIdNum) || wrkIdNum <= 0) return;
    cstmMleApi.listForWork(wrkIdNum).then((defs: any[]) => {
      try {
        const list = Array.isArray(defs) ? defs : [];
        const norm = (s?: string) => (s || '').trim().toLowerCase();
        const bySub: Record<number, any[]> = {};
        for (const lm of (landmarks || [])) {
          const subs = Array.isArray((lm as any).sub_milestones) ? (lm as any).sub_milestones : [];
          for (const sm of subs) {
            if (!sm?.wrk_schd_sub_mlstn_id) continue;
            const matched = list.filter((d: any) => {
              const byId   = sm.sub_cmpnt_id && d.sub_cmpnt_id === sm.sub_cmpnt_id;
              const byName = !!d.sub_cmpnt_nm && norm(sm.sub_mlstn_nm) === norm(d.sub_cmpnt_nm);
              return byId || byName;
            });
            if (matched.length > 0) bySub[sm.wrk_schd_sub_mlstn_id] = matched;
          }
        }
        setSubFieldDefs(bySub);
        // Now pre-fill values from the latest stored values per sub × field.
        cstmMleApi.latestValues(wrkIdNum).then((rows: any[]) => {
          try {
            const fldCdById = new Map<number, string>();
            Object.values(bySub).forEach(defsList => {
              (defsList || []).forEach((d: any) => fldCdById.set(d.cstm_mle_id, d.fld_cd));
            });
            const next: Record<number, Record<string, string>> = {};
            for (const r of (rows || [])) {
              const cd = fldCdById.get(r.cstm_mle_id);
              if (!cd) continue;
              if (!next[r.wrk_schd_sub_mlstn_id]) next[r.wrk_schd_sub_mlstn_id] = {};
              next[r.wrk_schd_sub_mlstn_id][cd] = String(r.fld_val_tx ?? '');
            }
            setSubFieldValsLoaded(next);
            setSubFieldVals(prev => ({ ...next, ...prev }));
          } catch (e) { console.warn('[cstm-mle] prefill failed', e); }
        }).catch(() => {/* noop */});
      } catch (e) { console.warn('[cstm-mle] mapping failed', e); }
    }).catch((e) => { console.warn('[cstm-mle] list failed', e?.message || e); setSubFieldDefs({}); });
  }, [workId, landmarks]);

  const [previewImage, setPreviewImage] = useState<{ url: string; caption: string } | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Add Update form state
  const [formComment, setFormComment] = useState('');
  const [formProgress, setFormProgress] = useState(65);
  const [formLandmarkName, setFormLandmarkName] = useState('');
  const [formPhotos, setFormPhotos] = useState<{ file: File; preview: string; caption: string }[]>([]);
  const [formGeocode, setFormGeocode] = useState<{ lat: string; lng: string; address: string }>({ lat: '', lng: '', address: '' });
  const [geoLoading, setGeoLoading] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [drawMode, setDrawMode] = useState<'none' | 'marker' | 'polyline' | 'polygon'>('none');
  const [drawnItems, setDrawnItems] = useState<{ type: string; latlngs: any }[]>([]);
  const [formMilestoneUpdates, setFormMilestoneUpdates] = useState<Record<number, number>>(() => {
    const initial: Record<number, number> = {};
    landmarks.forEach(l => { initial[l.id] = l.progress; });
    return initial;
  });
  // Per-milestone custom-field values, keyed by milestone id → { fld_cd: value }
  const [formFieldValues, setFormFieldValues] = useState<Record<number, Record<string, any>>>({});
  // Per-milestone Value of Work Done (INR string from <input>), bounded to Planned Amount
  const [formMilestoneValues, setFormMilestoneValues] = useState<Record<number, string>>({});
  const [submittingProgress, setSubmittingProgress] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState<number | null>(null);
  // Real milestone-progress history (fetched from the backend).  Shows
  // captured custom-field values per submission so users see exactly what
  // was recorded, not just the % progress number.
  const [milestoneHistory, setMilestoneHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Financial-side state — separate flow with its own history + latest cache.
  const [milestoneTab, setMilestoneTab] = useState<Record<number, 'physical' | 'financial'>>({});
  const [fnclForm, setFnclForm] = useState<Record<number, { snctn: string; expnd: string; rmrk: string }>>({});
  const [fnclLatest, setFnclLatest] = useState<Record<number, { plnd_amt_at: number | null; snctn_amt_at: number | null; expndtr_at: number | null; blnc_at: number | null }>>({});
  const [fnclHistory, setFnclHistory] = useState<any[]>([]);
  const [submittingFncl, setSubmittingFncl] = useState<number | null>(null);
  const reloadHistory = useCallback(async () => {
    if (!scheduleId) { setMilestoneHistory([]); setFnclHistory([]); setFnclLatest({}); return; }
    setHistoryLoading(true);
    try {
      const [h, fh, fl] = await Promise.all([
        workScheduleApi.getProgressHistory(scheduleId),
        workScheduleApi.getFinancialHistory(scheduleId),
        workScheduleApi.getLatestFinancialPerMilestone(scheduleId),
      ]);
      const hist = Array.isArray(h) ? h : [];
      // Pull photo attachments per progress row (ref_kind='wrk_prgrs'). One
      // request per row — acceptable for the page's typical row count and
      // keeps the backend query simple. Falls back to [] silently if the
      // listByRef call errors so a single bad row never blanks the feed.
      const withPhotos = await Promise.all(hist.map(async (row: any) => {
        // ref_kind='wrk_prgrs_dtl' / ref_id=prgrs_mlstn_id ties attachments
        // to a specific timeline entry (not the parent wrk_prgrs row which
        // is shared across every entry on the schedule).
        const id = row?.prgrs_mlstn_id;
        if (!id) return { ...row, photos: [] };
        try {
          const atts = await attachmentApi.listByRef('wrk_prgrs_dtl', id);
          return { ...row, photos: Array.isArray(atts) ? atts : [] };
        } catch {
          return { ...row, photos: [] };
        }
      }));
      setMilestoneHistory(withPhotos);
      setFnclHistory(    Array.isArray(fh) ? fh : []);
      const map: Record<number, any> = {};
      for (const r of (fl || [])) map[r.wrk_schd_mlstn_id] = r;
      setFnclLatest(map);
    } catch {
      setMilestoneHistory([]); setFnclHistory([]); setFnclLatest({});
    } finally {
      setHistoryLoading(false);
    }
  }, [scheduleId]);
  useEffect(() => { reloadHistory(); }, [reloadHistory]);

  // Submit a Financial entry for one milestone. Server validates caps and
  // recomputes the work's fncl_prgrs_nu.
  const handleFnclSubmit = async (lm: Landmark) => {
    if (!scheduleId) { alert('No work schedule found'); return; }
    const f = fnclForm[lm.id] || { snctn: '', expnd: '', rmrk: '' };
    const snctn = f.snctn === '' ? null : Number(f.snctn);
    const expnd = f.expnd === '' ? null : Number(f.expnd);
    const stored = lm.plnd_amt_at;
    const milestonePlanned = stored !== null && stored !== undefined
      ? Number(stored)
      : totalProjectCost > 0 ? Math.round(totalProjectCost * (lm.wghtg_pct_nu || 0) / 100) : 0;
    if (snctn !== null && milestonePlanned > 0 && snctn > milestonePlanned) {
      alert(`Sanctioned Amount (₹${snctn.toLocaleString('en-IN')}) cannot exceed Planned Amount (₹${milestonePlanned.toLocaleString('en-IN')})`);
      return;
    }
    if (snctn !== null && expnd !== null && expnd > snctn) {
      alert(`Expenditure (₹${expnd.toLocaleString('en-IN')}) cannot exceed Sanctioned Amount (₹${snctn.toLocaleString('en-IN')})`);
      return;
    }
    setSubmittingFncl(lm.id);
    try {
      await workScheduleApi.updateFinancialProgress(scheduleId, {
        wrk_schd_mlstn_id: lm.id,
        plnd_amt_at:       milestonePlanned > 0 ? milestonePlanned : null,
        snctn_amt_at:      snctn,
        expndtr_at:        expnd,
        rmrk_tx:           f.rmrk || null,
      });
      setFnclForm(prev => ({ ...prev, [lm.id]: { snctn: '', expnd: '', rmrk: '' } }));
      await reloadHistory();
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.response?.data?.error || 'Failed to submit financial progress');
    } finally {
      setSubmittingFncl(null);
    }
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gisMapRef = useRef<L.Map | null>(null);
  const gisMapContainerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Compute overall progress from milestones
  const computedProgress = landmarks.length > 0
    ? Math.round(Object.values(formMilestoneUpdates).reduce((a, b) => a + b, 0) / landmarks.length)
    : formProgress;

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      caption: '',
    }));
    setFormPhotos(prev => [...prev, ...newPhotos]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (index: number) => {
    setFormPhotos(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const updatePhotoCaption = (index: number, caption: string) => {
    setFormPhotos(prev => prev.map((p, i) => i === index ? { ...p, caption } : p));
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude.toFixed(4);
        const lng = position.coords.longitude.toFixed(4);
        setFormGeocode({ lat, lng, address: `${lat}°N, ${lng}°E` });
        // Pan map to location
        if (gisMapRef.current) {
          gisMapRef.current.setView([parseFloat(lat), parseFloat(lng)], 16);
          L.marker([parseFloat(lat), parseFloat(lng)], {
            icon: L.divIcon({
              className: '',
              html: '<div style="background:linear-gradient(135deg,#f97316,#eab308);width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>',
              iconSize: [14, 14],
              iconAnchor: [7, 7],
            }),
          }).addTo(gisMapRef.current).bindPopup('Current Location');
        }
        // Try reverse geocoding
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
          const data = await res.json();
          if (data.display_name) {
            setFormGeocode(prev => ({ ...prev, address: data.display_name }));
          }
        } catch { /* keep coordinate-based address */ }
        setGeoLoading(false);
      },
      (err) => {
        alert(`Unable to get location: ${err.message}`);
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Parse "17.7240°N, 83.2998°E" into [lat, lng]
  const parseCoords = (coords: string): [number, number] | null => {
    const match = coords.match(/([\d.]+)°[NS],?\s*([\d.]+)°[EW]/);
    if (!match) return null;
    return [parseFloat(match[1]), parseFloat(match[2])];
  };

  // GIS Map initialization — show existing landmarks
  const initGisMap = useCallback((container: HTMLDivElement | null) => {
    if (!container || gisMapRef.current) return;
    const defaultCenter: [number, number] = formGeocode.lat && formGeocode.lng
      ? [parseFloat(formGeocode.lat), parseFloat(formGeocode.lng)]
      : [17.385, 78.4867];
    const map = L.map(container, {
      center: defaultCenter,
      zoom: 15,
      zoomControl: false,
    });
    L.tileLayer(TILE_URLS.osm, {
      attribution: '&copy; OSM',
      maxZoom: 19,
    }).addTo(map);
    L.control.zoom({ position: 'topright' }).addTo(map);
    gisMapRef.current = map;

    // Plot existing landmarks
    const latLngs: L.LatLng[] = [];
    landmarks.forEach((lm) => {
      const parsed = parseCoords(lm.coordinates);
      if (!parsed) return;
      const [lat, lng] = parsed;
      latLngs.push(L.latLng(lat, lng));

      const color = lm.status === 'completed' ? '#22c55e' : lm.status === 'in-progress' ? '#f97316' : '#9ca3af';
      const icon = L.divIcon({
        className: '',
        html: `<div style="position:relative">
          <div style="width:16px;height:16px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>
          <div style="position:absolute;top:-6px;left:20px;white-space:nowrap;font-size:10px;font-weight:600;color:${color};background:white;padding:1px 5px;border-radius:4px;box-shadow:0 1px 3px rgba(0,0,0,0.15);border:1px solid ${color}40">${lm.name}</div>
        </div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });

      L.marker([lat, lng], { icon }).addTo(map).bindPopup(
        `<b>${lm.name}</b><br/>Status: ${lm.status}<br/>Progress: ${lm.progress}%`
      );
    });

    // Draw line connecting landmarks
    if (latLngs.length > 1) {
      L.polyline(latLngs, { color: '#f97316', weight: 2, opacity: 0.5, dashArray: '4 6' }).addTo(map);
    }

    // Fit map to landmarks
    if (latLngs.length > 0) {
      map.fitBounds(L.latLngBounds(latLngs).pad(0.2));
    } else if (formGeocode.lat && formGeocode.lng) {
      map.setView([parseFloat(formGeocode.lat), parseFloat(formGeocode.lng)], 16);
    }
  }, [landmarks, formGeocode.lat, formGeocode.lng]);

  // Handle map drawing
  useEffect(() => {
    const map = gisMapRef.current;
    if (!map || drawMode === 'none') return;

    let drawing = false;
    let currentPolyline: L.Polyline | null = null;
    let currentPolygon: L.Polygon | null = null;
    let points: L.LatLng[] = [];

    const handleClick = (e: L.LeafletMouseEvent) => {
      if (drawMode === 'marker') {
        const marker = L.marker(e.latlng, {
          icon: L.divIcon({
            className: '',
            html: '<div style="background:linear-gradient(135deg,#f97316,#eab308);width:12px;height:12px;border-radius:50%;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>',
            iconSize: [12, 12],
            iconAnchor: [6, 6],
          }),
        }).addTo(map);
        marker.bindPopup(`<b>Marker</b><br/>${e.latlng.lat.toFixed(5)}, ${e.latlng.lng.toFixed(5)}`);
        setDrawnItems(prev => [...prev, { type: 'marker', latlngs: e.latlng }]);
        setFormGeocode({ lat: e.latlng.lat.toFixed(4), lng: e.latlng.lng.toFixed(4), address: `${e.latlng.lat.toFixed(4)}°N, ${e.latlng.lng.toFixed(4)}°E` });
        setDrawMode('none');
      } else if (drawMode === 'polyline' || drawMode === 'polygon') {
        drawing = true;
        points.push(e.latlng);
        if (drawMode === 'polyline') {
          if (currentPolyline) map.removeLayer(currentPolyline);
          currentPolyline = L.polyline(points, { color: '#f97316', weight: 3, dashArray: '6 4' }).addTo(map);
        } else {
          if (currentPolygon) map.removeLayer(currentPolygon);
          currentPolygon = L.polygon(points, { color: '#f97316', weight: 2, fillColor: '#f97316', fillOpacity: 0.15 }).addTo(map);
        }
      }
    };

    const handleDblClick = (e: L.LeafletMouseEvent) => {
      L.DomEvent.stopPropagation(e);
      if (!drawing || points.length < 2) return;
      if (drawMode === 'polyline' && currentPolyline) {
        currentPolyline.setStyle({ dashArray: undefined, weight: 3 });
        setDrawnItems(prev => [...prev, { type: 'polyline', latlngs: [...points] }]);
      } else if (drawMode === 'polygon' && currentPolygon) {
        currentPolygon.setStyle({ weight: 2, fillOpacity: 0.2 });
        setDrawnItems(prev => [...prev, { type: 'polygon', latlngs: [...points] }]);
      }
      drawing = false;
      points = [];
      currentPolyline = null;
      currentPolygon = null;
      setDrawMode('none');
    };

    map.on('click', handleClick);
    map.on('dblclick', handleDblClick);
    map.doubleClickZoom.disable();

    return () => {
      map.off('click', handleClick);
      map.off('dblclick', handleDblClick);
      map.doubleClickZoom.enable();
    };
  }, [drawMode]);

  // Invalidate map size on fullscreen toggle
  useEffect(() => {
    if (gisMapRef.current) {
      setTimeout(() => gisMapRef.current?.invalidateSize(), 100);
    }
  }, [mapFullscreen]);

  const clearDrawings = () => {
    if (gisMapRef.current) {
      gisMapRef.current.eachLayer((layer) => {
        if (layer instanceof L.Marker || layer instanceof L.Polyline || layer instanceof L.Polygon) {
          if (!(layer instanceof L.TileLayer)) {
            gisMapRef.current?.removeLayer(layer);
          }
        }
      });
    }
    setDrawnItems([]);
    setFormGeocode({ lat: '', lng: '', address: '' });
  };

  const handleSubmitUpdate = async () => {
    if (!formComment.trim()) {
      alert('Please enter a comment/description');
      return;
    }

    // Validate required custom fields on milestones the user touched
    // (either progress changed, any field filled, or value of work done entered).
    const touchedMilestones = landmarks.filter(lm => {
      const progChanged = (formMilestoneUpdates[lm.id] ?? lm.progress) !== lm.progress;
      const hasFieldVals = !!formFieldValues[lm.id] && Object.keys(formFieldValues[lm.id]).length > 0;
      const hasValue = !!formMilestoneValues[lm.id] && formMilestoneValues[lm.id] !== '';
      return progChanged || hasFieldVals || hasValue;
    });
    for (const lm of touchedMilestones) {
      // Lock once 100% complete — match the server-side guard.
      if ((lm.progress || 0) >= 100) {
        alert(`"${lm.name}" is already at 100% — further progress updates are not allowed.`);
        return;
      }
      const missing = (lm.cstm_flds_jn || [])
        .filter(f => f.is_rqrd_in && !formFieldValues[lm.id]?.[f.fld_cd])
        .map(f => f.fld_nm);
      if (missing.length) {
        alert(`"${lm.name}": please fill required field(s): ${missing.join(', ')}`);
        return;
      }
      // Value of Work Done bound check
      const raw = formMilestoneValues[lm.id];
      if (raw !== undefined && raw !== '') {
        const num = Number(raw);
        if (!Number.isFinite(num) || num < 0) {
          alert(`"${lm.name}": Value of Work Done must be a non-negative number`);
          return;
        }
        const stored = lm.plnd_amt_at;
        const plannedAmt = stored !== null && stored !== undefined
          ? Number(stored)
          : totalProjectCost > 0 ? Math.round(totalProjectCost * (lm.wghtg_pct_nu || 0) / 100) : 0;
        if (plannedAmt > 0 && num > plannedAmt) {
          alert(`"${lm.name}": Value of Work Done (₹${num.toLocaleString('en-IN')}) cannot exceed Planned Amount (₹${plannedAmt.toLocaleString('en-IN')})`);
          return;
        }
      }
    }

    if (!scheduleId) {
      alert('No work schedule found — cannot submit progress.');
      return;
    }

    // Which milestones to write. Normally the ones the user touched. But a
    // comment-/photo-only update (no % change) must STILL create a timeline
    // entry so the note + photos are saved and shown — otherwise the submit
    // silently does nothing. Fall back to the milestone the user is working on:
    // the selected pill, else the first in-progress one, else the first that
    // isn't already complete (the server rejects 100%/ended/future milestones).
    let targets = touchedMilestones;
    if (targets.length === 0) {
      const fallback =
        landmarks.find(l => l.id === selectedMilestone && (l.progress || 0) < 100) ||
        landmarks.find(l => (l.progress || 0) > 0 && (l.progress || 0) < 100) ||
        landmarks.find(l => (l.progress || 0) < 100);
      if (!fallback) {
        // Two distinct dead-ends: no milestones exist at all, vs. every one is
        // already at 100%. A progress update (comment/photo) is always attached
        // to a milestone, so neither can be saved.
        alert(landmarks.length === 0
          ? 'This work has no milestones yet. Add milestones to the work schedule (Design + BOQ tab) before posting progress updates — photos and comments are recorded against a milestone.'
          : 'All milestones are already at 100% — there is no open milestone to attach this update to.');
        return;
      }
      targets = [fallback];
    }

    setSubmittingProgress(true);
    try {
      // First written detail-row id — photos attach here ONCE. A single update's
      // photos belong to that update, not duplicated across every milestone.
      let photoAnchorId: number | null = null;
      // One API call per target milestone — the backend writes the progress
      // row and one typed value row per captured custom field.
      for (const lm of targets) {
        const pct = formMilestoneUpdates[lm.id] ?? lm.progress;
        const fldVals = formFieldValues[lm.id] || {};
        const rawV = formMilestoneValues[lm.id];
        const valueNum = rawV === undefined || rawV === '' ? null : Number(rawV);
        const submission = await workScheduleApi.updateProgress(scheduleId, {
          wrk_schd_mlstn_id: lm.id,
          prgrs_pct_nu: pct,
          rmrks_tx: formComment,
          rprtd_dt: new Date().toISOString(),
          prgrs_updt_flds_jn: Object.keys(fldVals).length > 0 ? fldVals : null,
          vlu_of_wrk_dn_at: valueNum,
        });
        // Persist sub-component custom field VALUES into wms_cstm_mle_val_t.
        const wrkPrgrsId = (submission as any)?.wrk_prgrs_id;
        const subs = ((lm as any).sub_milestones || []) as Array<{ wrk_schd_sub_mlstn_id: number }>;
        if (wrkPrgrsId && subs.length > 0) {
          const valuesPayload: Array<{ cstm_mle_id: number; wrk_schd_sub_mlstn_id: number; fld_val_tx: string }> = [];
          for (const sm of subs) {
            const defs = subFieldDefs[sm.wrk_schd_sub_mlstn_id] || [];
            const vals = subFieldVals[sm.wrk_schd_sub_mlstn_id] || {};
            for (const d of defs) {
              const v = vals[d.fld_cd];
              if (v !== undefined && v !== null && v !== '') {
                valuesPayload.push({
                  cstm_mle_id: d.cstm_mle_id,
                  wrk_schd_sub_mlstn_id: sm.wrk_schd_sub_mlstn_id,
                  fld_val_tx: String(v),
                });
              }
            }
          }
          if (valuesPayload.length > 0) {
            try { await cstmMleApi.saveValues({ wrk_prgrs_id: wrkPrgrsId, values: valuesPayload }); }
            catch (e: any) { console.error('[cstm-mle] Save values failed:', e?.response?.data || e?.message || e); }
          }
        }
        // Remember the FIRST detail-row id so the photos can attach to it once
        // after the loop (ref_kind='wrk_prgrs_dtl', ref_id=prgrs_mlstn_id).
        if (photoAnchorId == null) {
          const id = (submission as any)?.prgrs_mlstn_id;
          if (id != null) photoAnchorId = id;
        }
      }

      // Persist any photos attached in the Add Update form ONCE, tied to the
      // first written progress-detail row, so they surface in that timeline
      // entry's Physical Progress History card (and aren't duplicated across
      // every touched milestone).
      if (photoAnchorId != null && formPhotos.length > 0) {
        for (const p of formPhotos) {
          try {
            await attachmentApi.upload(p.file, 'wrk_prgrs_dtl', photoAnchorId);
          } catch (e: any) {
            console.error('[wrk_prgrs_dtl] photo upload failed:', p.file.name, e?.response?.data || e?.message || e);
          }
        }
      }
      // Promote current sub-field values to the loaded baseline so re-submitting
      // the same data is blocked (until something else changes).
      setSubFieldValsLoaded(subFieldVals);
      // Reset form
      formPhotos.forEach(p => URL.revokeObjectURL(p.preview));
      setFormComment('');
      setFormProgress(65);
      setFormLandmarkName('');
      setFormPhotos([]);
      setFormGeocode({ lat: '', lng: '', address: '' });
      setDrawnItems([]);
      setSelectedMilestone(null);
      setFormFieldValues({});
      setFormMilestoneValues({});
      const resetMilestones: Record<number, number> = {};
      landmarks.forEach(l => { resetMilestones[l.id] = l.progress; });
      setFormMilestoneUpdates(resetMilestones);
      setMapFullscreen(false);
      if (gisMapRef.current) { gisMapRef.current.remove(); gisMapRef.current = null; }
      setShowAddForm(false);
      // Refresh the captured-values feed so the user sees their submission immediately.
      await reloadHistory();
      // Tell the parent so the Current Status "Site Photos" gallery picks up any
      // newly-uploaded photos without needing the modal to be reopened.
      onProgressSaved?.();
    } catch (err: any) {
      console.error('Submit progress failed:', err);
      alert(err?.response?.data?.error || 'Failed to submit progress update');
    } finally {
      setSubmittingProgress(false);
    }
  };

  // Demo progress feeds removed. Real progress + photos come from the
  // Physical / Financial Progress History sections below (DB-backed).
  const mockUpdates: any[] = [];

  return (
    <div className="space-y-4">
      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/80 z-[10000] flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
            <img
              src={previewImage.url}
              alt={previewImage.caption}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                // If the real image 404s, swap to the placeholder SVG we built
                // for this date by looking it up on the matching update entry.
                const el = e.currentTarget as HTMLImageElement;
                const match = mockUpdates
                  .flatMap(u => u.images as any[])
                  .find(img => img?.url === previewImage.url || img?.thumbnail === previewImage.url);
                const fb = match?.fallback;
                if (fb && el.src !== fb) el.src = fb;
              }}
            />
            {previewImage.caption && (
              <p className="text-white text-center mt-4 text-sm">{previewImage.caption}</p>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-800 dark:text-gray-100">Progress Updates</h4>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 active:scale-[0.98] ${
            showAddForm
              ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              : 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-md shadow-violet-500/30 hover:from-violet-600 hover:to-purple-600 hover:shadow-lg hover:shadow-violet-500/40'
          }`}
        >
          <i className={showAddForm ? 'ri-close-line' : 'ri-add-line'}></i>
          {showAddForm ? 'Cancel' : 'Add Update'}
        </button>
      </div>

      {/* Add Update Form */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 border-2 border-violet-200 dark:border-gray-700 rounded-xl shadow-lg shadow-violet-100/30 overflow-hidden">
          {/* Header — title left, user avatar right */}
          <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-gray-700 dark:to-gray-700 border-b border-violet-100 dark:border-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <i className="ri-edit-box-line text-orange-600"></i>
              </div>
              <h5 className="font-semibold text-gray-800 dark:text-gray-100">New Progress Update</h5>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-[10px] font-bold">
                  {(user?.name || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-700 truncate">{user?.name || 'Unknown User'}</p>
                <p className="text-[10px] text-gray-400 capitalize leading-tight">{user?.type || 'User'}</p>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Comment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <i className="ri-chat-3-line mr-1 text-gray-400"></i>
                Comment / Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formComment}
                onChange={(e) => setFormComment(e.target.value)}
                placeholder="Describe the progress, issues, or milestones achieved..."
                rows={2}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-300 focus:border-violet-400 outline-none resize-none transition-shadow"
              />
            </div>

            {/* Milestone-based Progress */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <i className="ri-flag-line mr-1 text-gray-400"></i>
                  Milestone Progress
                </label>
                {landmarks.length > 0 && (
                  <span className="text-sm font-bold text-orange-600">
                    Overall: {computedProgress}%
                  </span>
                )}
              </div>
              {/* No milestones → progress updates have nothing to attach to.
                  Show a clear notice instead of a misleading default bar. */}
              {landmarks.length === 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-800">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  <p className="text-xs leading-snug">
                    This work has no milestones yet. Add milestones to the work schedule
                    (<strong>Design + BOQ</strong> tab) before posting progress updates —
                    comments and photos are recorded against a milestone.
                  </p>
                </div>
              )}
              {/* Overall progress bar */}
              {landmarks.length > 0 && (
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-violet-500 to-purple-400 transition-all duration-500"
                  style={{ width: `${computedProgress}%` }}
                />
              </div>
              )}
              {/* Milestone grid — compact pill cards; clicking opens the detail panel below. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-40 overflow-y-auto pr-1">
                {landmarks.map((lm) => {
                  const currentProg = formMilestoneUpdates[lm.id] ?? lm.progress;
                  const isSelected = selectedMilestone === lm.id;
                  const statusColor = currentProg >= 100 ? 'green' : currentProg > 0 ? 'orange' : 'gray';
                  return (
                    <div
                      key={lm.id}
                      onClick={() => {
                        if ((lm.progress || 0) >= 100) {
                          alert(`"${lm.name}" is already at 100% — further progress updates are not allowed.`);
                          return;
                        }
                        setSelectedMilestone(isSelected ? null : lm.id);
                        setFormLandmarkName(lm.name);
                        // Pan map to this landmark
                        const parsed = parseCoords(lm.coordinates);
                        if (parsed && gisMapRef.current) {
                          gisMapRef.current.setView(parsed, 17, { animate: true });
                        }
                      }}
                      className={`relative border rounded-lg px-3 py-2.5 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-violet-400 bg-violet-50 dark:bg-violet-900/20 ring-2 ring-violet-200 shadow-sm'
                          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-violet-200 hover:bg-violet-50/30'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ background: statusColor === 'green' ? '#22c55e' : statusColor === 'orange' ? '#f97316' : '#9ca3af' }}
                        />
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate flex-1">{lm.name}</span>
                        <span className="text-[11px] font-bold tabular-nums" style={{ color: statusColor === 'green' ? '#22c55e' : statusColor === 'orange' ? '#f97316' : '#9ca3af' }}>
                          {currentProg}%
                        </span>
                        {Array.isArray(lm.cstm_flds_jn) && lm.cstm_flds_jn.length > 0 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold" title={`${lm.cstm_flds_jn.length} custom field(s)`}>
                            {lm.cstm_flds_jn.length}F
                          </span>
                        )}
                        {(lm.progress || 0) >= 100 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold" title="Locked at 100%">
                            <i className="ri-lock-line"></i>
                          </span>
                        )}
                      </div>
                      {/* Wt % + Planned Amount — always shown when the milestone
                          has a weightage (amount uses stored override or derives
                          from weightage × TPC). */}
                      {(lm.wghtg_pct_nu || 0) > 0 && (
                        <div className="flex items-center justify-between text-[10px] text-gray-500 mt-1 tabular-nums">
                          <span>Wt: {lm.wghtg_pct_nu}%</span>
                          {(() => {
                            const stored = lm.plnd_amt_at;
                            const amt = stored !== null && stored !== undefined
                              ? Number(stored)
                              : totalProjectCost > 0 ? Math.round(totalProjectCost * (lm.wghtg_pct_nu || 0) / 100) : null;
                            if (amt === null) return null;
                            return <span className="text-emerald-700 font-mono">&#8377;{amt.toLocaleString('en-IN')}</span>;
                          })()}
                        </div>
                      )}
                      {/* Stacked Physical + Financial bars in the pill so the
                          two flows are visible at a glance even when collapsed. */}
                      {(() => {
                        const latestF = fnclLatest[lm.id];
                        const fnclPct = latestF && latestF.snctn_amt_at && latestF.snctn_amt_at > 0
                          ? Math.min(100, Math.round(((latestF.expndtr_at || 0) / latestF.snctn_amt_at) * 10000) / 100)
                          : 0;
                        return (
                          <div className="mt-1.5 space-y-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[8px] font-semibold text-violet-600 w-7 tabular-nums">PHY</span>
                              <div className="h-1 flex-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all"
                                  style={{ width: `${Math.min(100, currentProg)}%`,
                                           background: statusColor === 'green' ? '#22c55e' : statusColor === 'orange' ? '#f97316' : '#d1d5db' }} />
                              </div>
                              <span className="text-[9px] font-medium text-gray-600 w-8 text-right tabular-nums">{currentProg}%</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[8px] font-semibold text-emerald-600 w-7 tabular-nums">FIN</span>
                              <div className="h-1 flex-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all bg-emerald-500"
                                  style={{ width: `${Math.min(100, fnclPct)}%` }} />
                              </div>
                              <span className="text-[9px] font-medium text-emerald-700 w-8 text-right tabular-nums">{fnclPct}%</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>

              {/* ─── Selected Milestone Detail Panel ───
                  Full-width panel that surfaces the progress slider + the
                  user-defined custom-form for whichever milestone is selected.
                  Keeps the milestone pills compact while giving the form
                  enough room to breathe. */}
              {selectedMilestone !== null && (() => {
                const sel = landmarks.find(l => l.id === selectedMilestone);
                if (!sel) return null;
                const curr = formMilestoneUpdates[sel.id] ?? sel.progress;
                const statusColor = curr >= 100 ? '#22c55e' : curr > 0 ? '#f97316' : '#9ca3af';
                const fields = sel.cstm_flds_jn || [];
                const valuesForMs = formFieldValues[sel.id] || {};
                return (
                  <div className="mt-4 bg-white dark:bg-gray-800 border border-violet-200 dark:border-violet-900/40 rounded-xl shadow-sm overflow-hidden">
                    {/* Panel header */}
                    <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-gray-700 dark:to-gray-700 border-b border-violet-100 dark:border-gray-600">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${statusColor}22` }}>
                          <i className="ri-flag-line text-[13px]" style={{ color: statusColor }}></i>
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{sel.name}</div>
                          <div className="text-[10px] text-gray-500 dark:text-gray-400">Milestone progress &amp; form</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-lg font-bold tabular-nums" style={{ color: statusColor }}>{curr}%</span>
                        <button
                          type="button"
                          onClick={() => setSelectedMilestone(null)}
                          className="w-7 h-7 rounded-md hover:bg-orange-100 dark:hover:bg-gray-600 text-gray-500 flex items-center justify-center transition-colors"
                          title="Close"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    </div>

                    <div className="p-4 space-y-4">
                      {/* Tab toggle: Physical / Financial — colour-coded so the
                          active tab is obviously distinct (orange=physical,
                          emerald=financial). Independent submissions. */}
                      {(() => {
                        const tab = milestoneTab[sel.id] || 'physical';
                        return (
                          <div className="flex items-center gap-1.5 p-1 bg-gradient-to-r from-violet-50 via-white to-emerald-50 border border-gray-200 rounded-lg w-fit shadow-sm">
                            <button type="button"
                              onClick={() => setMilestoneTab(prev => ({ ...prev, [sel.id]: 'physical' }))}
                              className={`px-3.5 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 ${
                                tab === 'physical'
                                  ? 'bg-gradient-to-r from-violet-600 to-purple-500 text-white shadow-md shadow-violet-200'
                                  : 'bg-white text-violet-700 border border-violet-200 hover:bg-violet-50'
                              }`}>
                              <i className="ri-bar-chart-2-line"></i> Physical
                            </button>
                            <button type="button"
                              onClick={() => setMilestoneTab(prev => ({ ...prev, [sel.id]: 'financial' }))}
                              className={`px-3.5 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 ${
                                tab === 'financial'
                                  ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-md shadow-emerald-200'
                                  : 'bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-50'
                              }`}>
                              <i className="ri-money-rupee-circle-line"></i> Financial
                            </button>
                          </div>
                        );
                      })()}

                      {/* PHYSICAL TAB — existing form */}
                      {(milestoneTab[sel.id] || 'physical') === 'physical' && (<>
                      {/* Planned Amount summary — only when the milestone designer
                          opted in via "Show in Progress Update". */}
                      {sel.shw_plnd_amt_in_prgrs_in && (sel.wghtg_pct_nu || 0) > 0 && (() => {
                        const stored = sel.plnd_amt_at;
                        const plannedAmt = stored !== null && stored !== undefined
                          ? Number(stored)
                          : totalProjectCost > 0 ? Math.round(totalProjectCost * (sel.wghtg_pct_nu || 0) / 100) : 0;
                        if (plannedAmt <= 0) return null;
                        const earned = Math.round(plannedAmt * curr / 100);
                        return (
                          <div className="grid grid-cols-3 gap-2 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-100 rounded-lg px-3 py-2">
                            <div className="min-w-0">
                              <div className="text-[10px] text-gray-500 uppercase tracking-wide">Weightage</div>
                              <div className="text-sm font-bold text-gray-800 tabular-nums">{sel.wghtg_pct_nu}%</div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-[10px] text-gray-500 uppercase tracking-wide">Planned Amount</div>
                              <div className="text-sm font-bold text-emerald-700 font-mono truncate" title={`₹${plannedAmt.toLocaleString('en-IN')}`}>
                                &#8377;{plannedAmt.toLocaleString('en-IN')}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-[10px] text-gray-500 uppercase tracking-wide">Earned ({curr}%)</div>
                              <div className="text-sm font-bold text-violet-700 font-mono truncate" title={`₹${earned.toLocaleString('en-IN')}`}>
                                &#8377;{earned.toLocaleString('en-IN')}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Value of Work Done — only when the milestone opted in. */}
                      {sel.shw_plnd_amt_in_prgrs_in && (() => {
                        const stored = sel.plnd_amt_at;
                        const plannedAmt = stored !== null && stored !== undefined
                          ? Number(stored)
                          : totalProjectCost > 0 ? Math.round(totalProjectCost * (sel.wghtg_pct_nu || 0) / 100) : 0;
                        const raw = formMilestoneValues[sel.id] ?? '';
                        const parsed = raw === '' ? null : Number(raw);
                        const overLimit = parsed !== null && plannedAmt > 0 && Number.isFinite(parsed) && parsed > plannedAmt;
                        return (
                          <div>
                            <label className="flex items-center justify-between text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                              <span><i className="ri-money-rupee-circle-line mr-1"></i>Value of Work Done (&#8377;)</span>
                              {plannedAmt > 0 && (
                                <span className="text-[10px] text-gray-400">Max: &#8377;{plannedAmt.toLocaleString('en-IN')}</span>
                              )}
                            </label>
                            <input
                              type="number"
                              min={0}
                              max={plannedAmt > 0 ? plannedAmt : undefined}
                              step="0.01"
                              value={raw}
                              onChange={(e) => {
                                const v = e.target.value;
                                setFormMilestoneValues(prev => ({ ...prev, [sel.id]: v }));
                                // Auto-derive Progress % from value / planned.
                                if (plannedAmt > 0) {
                                  const num = v === '' ? 0 : Number(v);
                                  const pct = Number.isFinite(num)
                                    ? Math.min(100, Math.max(0, Math.round((num / plannedAmt) * 10000) / 100))
                                    : 0;
                                  setFormMilestoneUpdates(prev => ({ ...prev, [sel.id]: pct }));
                                }
                              }}
                              placeholder={plannedAmt > 0 ? `0 - ${plannedAmt}` : '0'}
                              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                                overLimit
                                  ? 'border-red-400 focus:ring-red-300'
                                  : 'border-gray-300 dark:border-gray-600 focus:ring-violet-300 focus:border-violet-400'
                              }`}
                            />
                            {overLimit && (
                              <p className="text-[10px] text-red-600 mt-1">
                                Cannot exceed Planned Amount of &#8377;{plannedAmt.toLocaleString('en-IN')}
                              </p>
                            )}
                          </div>
                        );
                      })()}

                      {/* Progress
                          - Opted-in milestones: auto-derived from Value/Planned (read-only).
                          - Otherwise: manual slider. */}
                      {sel.shw_plnd_amt_in_prgrs_in ? (() => {
                        const stored = sel.plnd_amt_at;
                        const plannedAmt = stored !== null && stored !== undefined
                          ? Number(stored)
                          : totalProjectCost > 0 ? Math.round(totalProjectCost * (sel.wghtg_pct_nu || 0) / 100) : 0;
                        return (
                          <div>
                            <label className="flex items-center justify-between text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                              <span><i className="ri-speed-up-line mr-1"></i>Progress (auto: Value &#247; Planned)</span>
                              <span className="tabular-nums font-bold" style={{ color: statusColor }}>{curr}%</span>
                            </label>
                            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
                              <div className="h-full rounded-lg transition-all" style={{ width: `${Math.min(100, curr)}%`, backgroundColor: statusColor }}></div>
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                              <button type="button"
                                onClick={() => {
                                  setFormMilestoneValues(prev => ({ ...prev, [sel.id]: '' }));
                                  setFormMilestoneUpdates(prev => ({ ...prev, [sel.id]: 0 }));
                                }}
                                className="text-[11px] px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 dark:border-gray-600">
                                <i className="ri-close-circle-line mr-0.5"></i>Reset
                              </button>
                              <button type="button"
                                disabled={plannedAmt <= 0}
                                title={plannedAmt > 0 ? `Sets Value of Work Done to ₹${plannedAmt.toLocaleString('en-IN')}` : 'No Planned Amount set'}
                                onClick={() => {
                                  setFormMilestoneValues(prev => ({ ...prev, [sel.id]: String(plannedAmt) }));
                                  setFormMilestoneUpdates(prev => ({ ...prev, [sel.id]: 100 }));
                                }}
                                className="text-[11px] px-2 py-1 rounded border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50">
                                <i className="ri-checkbox-circle-line mr-0.5"></i>Mark Completed
                              </button>
                            </div>
                          </div>
                        );
                      })() : (
                        <div>
                          <label className="flex items-center justify-between text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                            <span><i className="ri-speed-up-line mr-1"></i>Progress</span>
                            <span className="tabular-nums font-bold" style={{ color: statusColor }}>{curr}%</span>
                          </label>
                          <input type="range" min={0} max={100} step={5} value={curr}
                            onChange={(e) => setFormMilestoneUpdates(prev => ({ ...prev, [sel.id]: Number(e.target.value) }))}
                            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-orange-500" />
                          <div className="flex justify-between text-[10px] text-gray-400 dark:text-gray-500 mt-1 tabular-nums">
                            <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
                          </div>
                        </div>
                      )}

                      {/* Sub-component custom fields (defined in Add Milestone modal,
                          stored in wms_cstm_mle_lst_t). Rendered per picked sub.
                          Number-type fields drive the milestone progress (weighted avg by sub Wt %). */}
                      {(() => {
                        const subs = ((sel as any).sub_milestones || []) as Array<{
                          wrk_schd_sub_mlstn_id: number; sub_mlstn_nm: string; cmpnt_tx?: string;
                          wghtg_pct_nu?: number;
                        }>;
                        const subsWithFields = subs.filter((sm: any) => (subFieldDefs[sm.wrk_schd_sub_mlstn_id] || []).length > 0);
                        if (subsWithFields.length === 0) return null;
                        // Sub contribution = first field whose value parses as a positive
                        // number, capped at sub Wt %. Works for any admin-declared field type.
                        const subContrib = (sm: any, valsOverride?: Record<string, string>) => {
                          const defs = subFieldDefs[sm.wrk_schd_sub_mlstn_id] || [];
                          const vals = valsOverride ?? subFieldVals[sm.wrk_schd_sub_mlstn_id] ?? {};
                          const cap = Number(sm.wghtg_pct_nu) || 0;
                          for (const d of defs) {
                            const raw = vals[(d as any).fld_cd];
                            if (raw === undefined || raw === '') continue;
                            const v = Number(raw);
                            if (!Number.isFinite(v) || v <= 0) continue;
                            return cap > 0 ? Math.min(cap, v) : v;
                          }
                          return 0;
                        };
                        const derivedPct = Math.min(100, Math.round(subs.reduce((s, sm) => s + subContrib(sm), 0)));
                        const recomputeMilestonePct = (changedSmId: number, changedFldCd: string, nextVal: string) => {
                          const sum = subs.reduce((s, sm) => {
                            const overrideVals = sm.wrk_schd_sub_mlstn_id === changedSmId
                              ? { ...(subFieldVals[sm.wrk_schd_sub_mlstn_id] || {}), [changedFldCd]: nextVal }
                              : undefined;
                            return s + subContrib(sm, overrideVals);
                          }, 0);
                          setFormMilestoneUpdates(prev => ({ ...prev, [sel.id]: Math.min(100, Math.round(sum)) }));
                        };
                        return (
                          <div className="mb-3">
                            <div className="flex items-center justify-between mb-2 pb-1 border-b border-dashed border-purple-200">
                              <span className="text-xs font-semibold text-purple-700">
                                <i className="ri-list-check-line mr-1"></i>Sub-Component Fields
                              </span>
                              <span className="text-[10px] text-gray-500">
                                Auto milestone %: <span className="font-bold text-emerald-700">{derivedPct}%</span>
                              </span>
                            </div>
                            {subsWithFields.map((sm: any) => {
                              // Dedupe by fld_cd in case the same field code appears twice.
                              const rawDefs = subFieldDefs[sm.wrk_schd_sub_mlstn_id] || [];
                              const seenCodes = new Set<string>();
                              const defs = rawDefs.filter((d: any) => {
                                const k = String(d.fld_cd || d.fld_nm || '').toUpperCase();
                                if (seenCodes.has(k)) return false;
                                seenCodes.add(k);
                                return true;
                              });
                              const vals = subFieldVals[sm.wrk_schd_sub_mlstn_id] || {};
                              return (
                                <div key={sm.wrk_schd_sub_mlstn_id} className="mb-2 bg-purple-50/40 rounded p-2">
                                  <div className="text-[11px] font-medium text-gray-700 mb-1 flex items-center justify-between">
                                    <span>{sm.cmpnt_tx ? `${sm.cmpnt_tx} → ` : ''}{sm.sub_mlstn_nm}</span>
                                    <span className="text-[10px] text-gray-500">
                                      Wt {Number(sm.wghtg_pct_nu) || 0}% · Contributes: <span className="font-bold text-emerald-700">{subContrib(sm).toFixed(0)}%</span>
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {defs.map((d: any) => (
                                      <div key={d.cstm_mle_id}>
                                        <label className="text-[10px] text-gray-600 flex items-center gap-1">
                                          {d.fld_nm}{d.is_rqrd_in && <span className="text-red-500">*</span>}
                                        </label>
                                        {d.fld_type_cd === 'textarea' ? (
                                          <textarea value={vals[d.fld_cd] || ''}
                                            placeholder={d.plchldr_tx || ''}
                                            onChange={e => setSubFieldVals(prev => ({
                                              ...prev,
                                              [sm.wrk_schd_sub_mlstn_id]: { ...(prev[sm.wrk_schd_sub_mlstn_id] || {}), [d.fld_cd]: e.target.value },
                                            }))}
                                            rows={2}
                                            className="w-full px-2 py-1 border rounded text-[11px] focus:ring-1 focus:ring-purple-300" />
                                        ) : d.fld_type_cd === 'select' ? (
                                          <select value={vals[d.fld_cd] || ''}
                                            onChange={e => setSubFieldVals(prev => ({
                                              ...prev,
                                              [sm.wrk_schd_sub_mlstn_id]: { ...(prev[sm.wrk_schd_sub_mlstn_id] || {}), [d.fld_cd]: e.target.value },
                                            }))}
                                            className="w-full px-2 py-1 border rounded text-[11px] bg-white focus:ring-1 focus:ring-purple-300">
                                            <option value="">— Select —</option>
                                            {(d.optns_jn || []).map((o: any) => (
                                              <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                          </select>
                                        ) : d.fld_type_cd === 'checkbox' ? (
                                          <input type="checkbox"
                                            checked={vals[d.fld_cd] === 'true'}
                                            onChange={e => setSubFieldVals(prev => ({
                                              ...prev,
                                              [sm.wrk_schd_sub_mlstn_id]: { ...(prev[sm.wrk_schd_sub_mlstn_id] || {}), [d.fld_cd]: e.target.checked ? 'true' : 'false' },
                                            }))}
                                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                                        ) : (
                                          (() => {
                                            const isPctField = d.fld_type_cd === 'number' || d.fld_type_cd === 'percentage';
                                            const cap = isPctField ? (Number(sm.wghtg_pct_nu) || 0) : undefined;
                                            const rawVal = vals[d.fld_cd] || '';
                                            const overCap = isPctField && cap !== undefined && cap > 0 && rawVal !== '' && Number(rawVal) > cap;
                                            return (
                                              <>
                                                <input
                                                  type={d.fld_type_cd === 'number' ? 'number' : d.fld_type_cd === 'date' ? 'date' : d.fld_type_cd === 'datetime' ? 'datetime-local' : 'text'}
                                                  min={isPctField ? 0 : undefined}
                                                  value={rawVal}
                                                  placeholder={isPctField && cap ? `0-${cap}` : (d.plchldr_tx || '')}
                                                  title={isPctField && cap ? `Max allowed: ${cap}% (sub Wt %)` : undefined}
                                                  onChange={e => {
                                                    const next = e.target.value;
                                                    setSubFieldVals(prev => ({
                                                      ...prev,
                                                      [sm.wrk_schd_sub_mlstn_id]: { ...(prev[sm.wrk_schd_sub_mlstn_id] || {}), [d.fld_cd]: next },
                                                    }));
                                                    if (isPctField) {
                                                      recomputeMilestonePct(sm.wrk_schd_sub_mlstn_id, d.fld_cd, next);
                                                    }
                                                  }}
                                                  className={`w-full px-2 py-1 border rounded text-[11px] focus:ring-1 ${
                                                    overCap ? 'border-red-500 focus:ring-red-300 bg-red-50' : 'focus:ring-purple-300'
                                                  }`} />
                                                {overCap && (
                                                  <p className="text-[10px] text-red-600 mt-0.5 font-medium">
                                                    Max allowed is {cap}% (sub Wt %)
                                                  </p>
                                                )}
                                              </>
                                            );
                                          })()
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Custom milestone form */}
                      {fields.length > 0 ? (
                        <div>
                          <div className="flex items-center gap-1.5 mb-3 pb-2 border-b border-dashed border-gray-200 dark:border-gray-700">
                            <i className="ri-file-list-3-line text-orange-500"></i>
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Milestone Form</span>
                            <span className="text-[10px] text-gray-400 ml-auto">
                              {fields.filter(f => f.is_rqrd_in).length} required of {fields.length}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {fields.map((fld) => (
                              <div key={fld.fld_cd} className="min-w-0">
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {fld.fld_nm}
                                  {fld.is_rqrd_in && <span className="text-red-500 ml-0.5">*</span>}
                                </label>
                                <MilestoneCstmField
                                  field={fld}
                                  value={valuesForMs[fld.fld_cd]}
                                  onChange={(val) => setFormFieldValues(prev => ({
                                    ...prev,
                                    [sel.id]: { ...(prev[sel.id] || {}), [fld.fld_cd]: val },
                                  }))}
                                />
                                {fld.hlp_tx && <p className="text-[10px] text-gray-400 mt-0.5">{fld.hlp_tx}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-[11px] text-gray-400 italic bg-gray-50 dark:bg-gray-700/30 rounded-md px-3 py-2 border border-dashed border-gray-200 dark:border-gray-700">
                          No custom fields were defined for this milestone. Use the milestone designer to add some.
                        </div>
                      )}
                      </>)}

                      {/* FINANCIAL TAB — independent submission. Backend
                          recomputes Balance and the work's fncl_prgrs_nu. */}
                      {(milestoneTab[sel.id] || 'physical') === 'financial' && (() => {
                        const f = fnclForm[sel.id] || { snctn: '', expnd: '', rmrk: '' };
                        const latest = fnclLatest[sel.id];
                        const stored = sel.plnd_amt_at;
                        const milestonePlanned = stored !== null && stored !== undefined
                          ? Number(stored)
                          : totalProjectCost > 0 ? Math.round(totalProjectCost * (sel.wghtg_pct_nu || 0) / 100) : 0;
                        const snctnNum = f.snctn === '' ? null : Number(f.snctn);
                        const expndNum = f.expnd === '' ? null : Number(f.expnd);
                        const overPlanned = snctnNum !== null && Number.isFinite(snctnNum) && milestonePlanned > 0 && snctnNum > milestonePlanned;
                        const overSnctn   = snctnNum !== null && expndNum !== null && Number.isFinite(snctnNum) && Number.isFinite(expndNum) && expndNum > snctnNum;
                        const balancePreview = snctnNum !== null && expndNum !== null && Number.isFinite(snctnNum) && Number.isFinite(expndNum)
                          ? snctnNum - expndNum : null;
                        return (
                          <div className="space-y-3">
                            {latest && (
                              <div className="grid grid-cols-4 gap-2 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-100 rounded-lg px-3 py-2">
                                <div><div className="text-[10px] text-gray-500 uppercase">Planned</div>
                                  <div className="text-sm font-bold text-gray-800 font-mono">&#8377;{Number(latest.plnd_amt_at || 0).toLocaleString('en-IN')}</div></div>
                                <div><div className="text-[10px] text-gray-500 uppercase">Sanctioned</div>
                                  <div className="text-sm font-bold text-emerald-700 font-mono">&#8377;{Number(latest.snctn_amt_at || 0).toLocaleString('en-IN')}</div></div>
                                <div><div className="text-[10px] text-gray-500 uppercase">Expenditure</div>
                                  <div className="text-sm font-bold text-orange-700 font-mono">&#8377;{Number(latest.expndtr_at || 0).toLocaleString('en-IN')}</div></div>
                                <div><div className="text-[10px] text-gray-500 uppercase">Balance</div>
                                  <div className="text-sm font-bold text-violet-700 font-mono">&#8377;{Number(latest.blnc_at || 0).toLocaleString('en-IN')}</div></div>
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                                  Planned Amount (&#8377;)
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold">FROM MILESTONE</span>
                                </label>
                                <input type="text" readOnly disabled
                                  value={milestonePlanned > 0 ? `₹${milestonePlanned.toLocaleString('en-IN')}` : ''}
                                  placeholder="Set in the milestone designer"
                                  className="w-full px-3 py-2 border rounded-lg text-sm bg-violet-50 text-violet-900 font-mono cursor-not-allowed" />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center justify-between">
                                  <span>Sanctioned Amount (&#8377;)</span>
                                  {milestonePlanned > 0 && <span className="text-[10px] text-gray-400">Max: &#8377;{milestonePlanned.toLocaleString('en-IN')}</span>}
                                </label>
                                <input type="number" min={0} step="0.01"
                                  max={milestonePlanned > 0 ? milestonePlanned : undefined}
                                  value={f.snctn}
                                  onChange={e => setFnclForm(prev => ({ ...prev, [sel.id]: { ...f, snctn: e.target.value } }))}
                                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                                    overPlanned ? 'border-red-400 focus:ring-red-300' : 'focus:ring-emerald-300 focus:border-emerald-400'
                                  }`} />
                                {overPlanned && <p className="text-[10px] text-red-600 mt-1">Cannot exceed Planned Amount</p>}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Expenditure (&#8377;)</label>
                                <input type="number" min={0} step="0.01" value={f.expnd}
                                  onChange={e => setFnclForm(prev => ({ ...prev, [sel.id]: { ...f, expnd: e.target.value } }))}
                                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                                    overSnctn ? 'border-red-400 focus:ring-red-300' : 'focus:ring-emerald-300 focus:border-emerald-400'
                                  }`} />
                                {overSnctn && <p className="text-[10px] text-red-600 mt-1">Cannot exceed Sanctioned Amount</p>}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Balance (auto)</label>
                                <input type="text" readOnly disabled
                                  value={balancePreview !== null ? `₹${balancePreview.toLocaleString('en-IN')}` : ''}
                                  placeholder="Computed by server"
                                  className="w-full px-3 py-2 border rounded-lg text-sm bg-gray-50 text-gray-700 font-mono" />
                                <p className="text-[10px] text-gray-400 mt-0.5">Sanctioned − Expenditure</p>
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-700 mb-1 block">Remarks</label>
                              <textarea value={f.rmrk}
                                onChange={e => setFnclForm(prev => ({ ...prev, [sel.id]: { ...f, rmrk: e.target.value } }))}
                                rows={2} placeholder="Notes on this financial entry..."
                                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none" />
                            </div>
                            <div className="flex items-center justify-end pt-2 border-t">
                              <button type="button" onClick={() => handleFnclSubmit(sel)}
                                disabled={submittingFncl === sel.id || overSnctn || overPlanned}
                                title={overPlanned ? 'Sanctioned exceeds Planned' : overSnctn ? 'Expenditure exceeds Sanctioned' : undefined}
                                className="px-4 py-1.5 text-xs text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50 font-medium flex items-center gap-1.5">
                                {submittingFncl === sel.id
                                  ? <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div> Submitting...</>
                                  : <><i className="ri-send-plane-line"></i> Submit Financial</>}
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Two Column: Photos (left) | GIS Map (right) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* LEFT — Photos Upload */}
              <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-4 bg-gray-50/50 dark:bg-gray-800/50">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    <i className="ri-camera-line mr-1 text-gray-400"></i>
                    Site Photos
                  </label>
                  <div className="flex gap-1">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1 px-2.5 py-1 bg-violet-50 text-violet-600 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors text-xs"
                    >
                      <i className="ri-upload-2-line"></i> Upload
                    </button>
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.capture = 'environment';
                        input.onchange = (e) => {
                          const files = Array.from((e.target as HTMLInputElement).files || []);
                          const newPhotos = files.map(file => ({
                            file,
                            preview: URL.createObjectURL(file),
                            caption: '',
                          }));
                          setFormPhotos(prev => [...prev, ...newPhotos]);
                        };
                        input.click();
                      }}
                      className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors text-xs"
                    >
                      <i className="ri-camera-3-line"></i> Camera
                    </button>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                {formPhotos.length === 0 ? (
                  <div
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center py-10 text-gray-400 hover:border-orange-300 hover:text-orange-400 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <i className="ri-image-add-line text-4xl mb-2"></i>
                    <span className="text-sm">Click or drag photos here</span>
                    <span className="text-[11px] mt-1">Supports JPG, PNG, HEIC</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2 flex-wrap">
                      {formPhotos.map((photo, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={photo.preview}
                            alt={`Upload ${idx + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            onClick={() => removePhoto(idx)}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                          <input
                            type="text"
                            value={photo.caption}
                            onChange={(e) => updatePhotoCaption(idx, e.target.value)}
                            placeholder="Caption"
                            className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-b-lg border-0 outline-none placeholder-white/60"
                          />
                        </div>
                      ))}
                      {/* Add more */}
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-20 h-20 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-orange-400 hover:text-orange-500 transition-colors"
                      >
                        <i className="ri-add-line text-xl"></i>
                        <span className="text-[10px]">Add</span>
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">{formPhotos.length} photo{formPhotos.length > 1 ? 's' : ''} — hover to caption/remove</p>
                  </div>
                )}
              </div>

              {/* RIGHT — GIS Map */}
              <div className={`border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden bg-gray-50/50 dark:bg-gray-800/50 flex flex-col ${mapFullscreen ? 'fixed inset-4 z-[10001] border-2 border-orange-300 shadow-2xl rounded-2xl' : ''}`}>
                {/* Map Toolbar */}
                <div className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    <i className="ri-map-2-line mr-1 text-gray-400"></i>
                    GIS Update
                  </span>
                  <div className="flex items-center gap-1">
                    {/* Draw tools */}
                    <button
                      onClick={() => setDrawMode(drawMode === 'marker' ? 'none' : 'marker')}
                      className={`p-1.5 rounded text-xs transition-colors ${drawMode === 'marker' ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      title="Drop marker"
                    >
                      <i className="ri-map-pin-add-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setDrawMode(drawMode === 'polyline' ? 'none' : 'polyline')}
                      className={`p-1.5 rounded text-xs transition-colors ${drawMode === 'polyline' ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      title="Draw line"
                    >
                      <i className="ri-route-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setDrawMode(drawMode === 'polygon' ? 'none' : 'polygon')}
                      className={`p-1.5 rounded text-xs transition-colors ${drawMode === 'polygon' ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      title="Draw area"
                    >
                      <i className="ri-shape-line text-sm"></i>
                    </button>
                    <div className="w-px h-4 bg-gray-200 mx-0.5"></div>
                    {/* Locate */}
                    <button
                      onClick={handleGetCurrentLocation}
                      disabled={geoLoading}
                      className="p-1.5 rounded text-xs text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                      title="My location"
                    >
                      {geoLoading ? <i className="ri-loader-4-line animate-spin text-sm"></i> : <i className="ri-focus-3-line text-sm"></i>}
                    </button>
                    {/* Clear */}
                    {drawnItems.length > 0 && (
                      <button
                        onClick={clearDrawings}
                        className="p-1.5 rounded text-xs text-red-500 hover:bg-red-50 transition-colors"
                        title="Clear drawings"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    )}
                    <div className="w-px h-4 bg-gray-200 mx-0.5"></div>
                    {/* Fullscreen */}
                    <button
                      onClick={() => setMapFullscreen(!mapFullscreen)}
                      className="p-1.5 rounded text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title={mapFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                    >
                      <i className={`${mapFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-sm`}></i>
                    </button>
                  </div>
                </div>
                {/* Draw mode hint */}
                {drawMode !== 'none' && (
                  <div className="px-3 py-1.5 bg-orange-50 border-b border-orange-100 flex items-center justify-between">
                    <span className="text-[11px] text-orange-700">
                      <i className="ri-information-line mr-1"></i>
                      {drawMode === 'marker' && 'Click on map to drop a marker'}
                      {drawMode === 'polyline' && 'Click points to draw line. Double-click to finish.'}
                      {drawMode === 'polygon' && 'Click points to draw area. Double-click to finish.'}
                    </span>
                    <button onClick={() => setDrawMode('none')} className="text-[11px] text-orange-500 hover:text-orange-700">Cancel</button>
                  </div>
                )}
                {/* Map Container */}
                <div
                  ref={(el) => { gisMapContainerRef.current = el; initGisMap(el); }}
                  className={`w-full ${mapFullscreen ? 'flex-1' : 'h-64'}`}
                  style={{ minHeight: mapFullscreen ? undefined : 256 }}
                />
                {/* Geocode info bar */}
                {formGeocode.lat && (
                  <div className="px-3 py-1.5 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                    <i className="ri-map-pin-2-fill text-orange-500"></i>
                    <span className="truncate">{formGeocode.address || `${formGeocode.lat}°N, ${formGeocode.lng}°E`}</span>
                    {drawnItems.length > 0 && (
                      <span className="ml-auto flex-shrink-0 px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded text-[10px]">
                        {drawnItems.length} drawn
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => {
                  formPhotos.forEach(p => URL.revokeObjectURL(p.preview));
                  setShowAddForm(false);
                  setFormComment('');
                  setFormProgress(65);
                  setFormLandmarkName('');
                  setFormPhotos([]);
                  setFormGeocode({ lat: '', lng: '', address: '' });
                  setSelectedMilestone(null);
                  const reset: Record<number, number> = {};
                  landmarks.forEach(l => { reset[l.id] = l.progress; });
                  setFormMilestoneUpdates(reset);
                  setFormMilestoneValues({});
                  setFormFieldValues({});
                  clearDrawings();
                  if (gisMapRef.current) { gisMapRef.current.remove(); gisMapRef.current = null; }
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Discard
              </button>
              {(() => {
                // Disable Submit if any milestone's Value of Work Done exceeds its Planned Amount
                const anyOver = landmarks.some(lm => {
                  const raw = formMilestoneValues[lm.id];
                  if (raw === undefined || raw === '') return false;
                  const num = Number(raw);
                  if (!Number.isFinite(num)) return false;
                  const stored = lm.plnd_amt_at;
                  const plannedAmt = stored !== null && stored !== undefined
                    ? Number(stored)
                    : totalProjectCost > 0 ? Math.round(totalProjectCost * (lm.wghtg_pct_nu || 0) / 100) : 0;
                  return plannedAmt > 0 && num > plannedAmt;
                });
                // Block if ANY sub-component field's numeric value exceeds its sub Wt %
                // (regardless of declared field type).
                const anySubOver = landmarks.some((lm: any) => {
                  const subs = (lm.sub_milestones || []) as Array<{ wrk_schd_sub_mlstn_id: number; wghtg_pct_nu?: number }>;
                  return subs.some(sm => {
                    const cap = Number(sm.wghtg_pct_nu) || 0;
                    if (cap <= 0) return false;
                    const defs = subFieldDefs[sm.wrk_schd_sub_mlstn_id] || [];
                    const vals = subFieldVals[sm.wrk_schd_sub_mlstn_id] || {};
                    return defs.some((d: any) => {
                      const v = vals[d.fld_cd];
                      if (v === undefined || v === '') return false;
                      const n = Number(v);
                      return Number.isFinite(n) && n > cap;
                    });
                  });
                });
                // Block re-submitting unchanged data. Wrapped in try/catch so a
                // bad shape on any state can't crash the whole form to a white page.
                let anyDirty = true;
                try {
                  anyDirty = (() => {
                    if ((formComment || '').trim() !== '') return true;
                    if (Object.keys(formMilestoneValues || {}).some(k => (formMilestoneValues[Number(k)] || '') !== '')) return true;
                    if (Object.keys(formFieldValues || {}).some(k => Object.keys(formFieldValues[Number(k)] || {}).length > 0)) return true;
                    for (const lm of (landmarks || []) as any[]) {
                      if (!lm) continue;
                      if ((formMilestoneUpdates[lm.id] ?? lm.progress) !== lm.progress) return true;
                      const subs = Array.isArray(lm.sub_milestones) ? lm.sub_milestones : [];
                      for (const sm of subs) {
                        if (!sm?.wrk_schd_sub_mlstn_id) continue;
                        const current = subFieldVals[sm.wrk_schd_sub_mlstn_id] || {};
                        const loaded  = subFieldValsLoaded[sm.wrk_schd_sub_mlstn_id] || {};
                        const allKeys = new Set([...Object.keys(current), ...Object.keys(loaded)]);
                        for (const k of allKeys) {
                          if ((current[k] ?? '') !== (loaded[k] ?? '')) return true;
                        }
                      }
                    }
                    return false;
                  })();
                } catch (e) { console.warn('[anyDirty] check failed', e); anyDirty = true; }
                const blockSubmit = submittingProgress || anyOver || anySubOver || !anyDirty;
                return (
                  <button
                    onClick={handleSubmitUpdate}
                    disabled={blockSubmit}
                    title={!anyDirty ? 'Nothing new to submit' : anySubOver ? 'A sub-component value exceeds its Wt % — fix the red fields' : anyOver ? 'One or more milestones have Value of Work Done above their Planned Amount' : undefined}
                    className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-lg hover:from-violet-600 hover:to-purple-600 transition-all text-sm font-medium shadow-md shadow-violet-200/50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submittingProgress
                      ? <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div> Submitting…</>
                      : <><i className="ri-send-plane-fill"></i> Submit Update</>}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ─── Physical Progress History ─── */}
      {scheduleId && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-gray-700 dark:to-gray-700 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-2">
              <i className="ri-bar-chart-2-line text-violet-600"></i>
              <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Physical Progress History</h5>
              <span className="text-[10px] bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-600">
                {historyLoading ? '…' : milestoneHistory.length}
              </span>
            </div>
            <button onClick={reloadHistory}
              className="text-[11px] text-violet-600 hover:text-violet-800 flex items-center gap-1">
              <i className={`ri-refresh-line ${historyLoading ? 'animate-spin' : ''}`}></i> Refresh
            </button>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-72 overflow-auto">
            {milestoneHistory.length === 0 && !historyLoading && (
              <div className="px-4 py-6 text-center text-xs text-gray-400">No physical progress submissions yet.</div>
            )}
            {milestoneHistory.map((h: any, i: number) => {
              const vals = h.prgrs_updt_flds_jn;
              const valueEntries = vals && typeof vals === 'object'
                ? Object.entries(vals).filter(([, v]) => v !== null && v !== undefined && v !== '')
                : [];
              // Field-name lookup from the matching landmark's cstm_flds_jn.
              const mlstnDef = landmarks.find(l => l.name === h.mlstn_nm);
              const labelOf = (cd: string) => mlstnDef?.cstm_flds_jn?.find(f => f.fld_cd === cd)?.fld_nm || cd;
              return (
                <div key={i} className="px-4 py-3 hover:bg-gray-50/50 dark:hover:bg-gray-700/30">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <i className="ri-flag-line text-violet-500 text-sm flex-shrink-0"></i>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{h.mlstn_nm}</span>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">
                        {h.rprtd_dt ? new Date(h.rprtd_dt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 text-xs">
                      {h.vlu_of_wrk_dn_at !== null && h.vlu_of_wrk_dn_at !== undefined && (
                        <span className="text-emerald-700 font-mono" title="Value of Work Done">
                          &#8377;{Number(h.vlu_of_wrk_dn_at).toLocaleString('en-IN')}
                        </span>
                      )}
                      <span className="text-green-600 font-medium">+{h.prd_prgrs_pct_nu || 0}%</span>
                      <span className="font-bold text-violet-700">{h.cuml_prgrs_pct_nu || 0}%</span>
                    </div>
                  </div>
                  {h.rmrk_tx && (
                    <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-1 ml-6 line-clamp-2">{h.rmrk_tx}</p>
                  )}
                  {valueEntries.length > 0 && (
                    <div className="mt-2 ml-6 flex flex-wrap gap-1.5">
                      {valueEntries.map(([cd, v]) => (
                        <span key={cd}
                          className="inline-flex items-center gap-1 rounded-md bg-purple-50 border border-purple-100 text-purple-700 px-2 py-0.5 text-[11px]"
                          title={`${labelOf(cd)}: ${fmtCstmFldValue(v)}`}>
                          <span className="font-medium text-purple-900/80">{labelOf(cd)}</span>
                          <span className="text-purple-400">›</span>
                          <span className="truncate max-w-[180px]">{fmtCstmFldValue(v)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Attachments (photos saved against this progress row via
                      ref_kind='wrk_prgrs'). Click → full-size preview modal. */}
                  {Array.isArray(h.photos) && h.photos.length > 0 && (
                    <div className="mt-2 ml-6 flex gap-1.5 flex-wrap">
                      {h.photos.map((ph: any) => (
                        <button
                          key={ph.attchmnt_id}
                          type="button"
                          onClick={async () => {
                            // Fetch an authenticated blob URL for the full-size
                            // preview (a plain backend URL would 401 in the modal).
                            try {
                              const url = await attachmentApi.fetchBlobUrl(ph.attchmnt_id);
                              setPreviewImage({ url, caption: '' });
                            } catch { /* ignore — broken image already shown inline */ }
                          }}
                          className="group relative w-14 h-14 rounded-md border border-gray-200 overflow-hidden hover:border-violet-400"
                          title="Progress photo"
                        >
                          <AuthImage
                            attchmntId={ph.attchmnt_id}
                            alt="Progress photo"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center">
                            <i className="ri-zoom-in-line text-white opacity-0 group-hover:opacity-100"></i>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Financial Progress History ─── */}
      {scheduleId && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-gray-700 dark:to-gray-700 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-2">
              <i className="ri-money-rupee-circle-line text-emerald-600"></i>
              <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Financial Progress History</h5>
              <span className="text-[10px] bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-600">
                {historyLoading ? '…' : fnclHistory.length}
              </span>
            </div>
            <button onClick={reloadHistory}
              className="text-[11px] text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
              <i className={`ri-refresh-line ${historyLoading ? 'animate-spin' : ''}`}></i> Refresh
            </button>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-72 overflow-auto">
            {fnclHistory.length === 0 && !historyLoading && (
              <div className="px-4 py-6 text-center text-xs text-gray-400">No financial progress submissions yet.</div>
            )}
            {fnclHistory.map((h: any, i: number) => (
              <div key={i} className="px-4 py-3 hover:bg-gray-50/50 dark:hover:bg-gray-700/30">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <i className="ri-money-rupee-circle-line text-emerald-500 text-sm flex-shrink-0"></i>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{h.mlstn_nm}</span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">
                      {h.rprtd_dt ? new Date(h.rprtd_dt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </span>
                  </div>
                </div>
                <div className="mt-1 ml-6 grid grid-cols-4 gap-2 text-[11px]">
                  <div><span className="text-gray-500">Planned: </span><span className="font-mono text-gray-800">&#8377;{Number(h.plnd_amt_at || 0).toLocaleString('en-IN')}</span></div>
                  <div><span className="text-gray-500">Sanctioned: </span><span className="font-mono text-emerald-700">&#8377;{Number(h.snctn_amt_at || 0).toLocaleString('en-IN')}</span></div>
                  <div><span className="text-gray-500">Expenditure: </span><span className="font-mono text-orange-700">&#8377;{Number(h.expndtr_at || 0).toLocaleString('en-IN')}</span></div>
                  <div><span className="text-gray-500">Balance: </span><span className="font-mono text-violet-700">&#8377;{Number(h.blnc_at || 0).toLocaleString('en-IN')}</span></div>
                </div>
                {h.rmrk_tx && (
                  <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-1 ml-6 line-clamp-2">{h.rmrk_tx}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {mockUpdates.map((update) => (
          <div key={update.id} className="relative pl-8 pb-6 border-l-2 border-gray-200 dark:border-gray-700 last:border-l-0 last:pb-0">
            <div className="absolute left-0 top-0 w-4 h-4 -translate-x-1/2 bg-orange-500 rounded-full border-4 border-white dark:border-gray-900 shadow"></div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    {new Date(update.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    <span className="text-xs font-normal text-gray-400 ml-1.5">
                      {new Date(update.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                    </span>
                  </span>
                  {update.landmark && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">
                      <i className="ri-map-pin-2-fill text-xs"></i>
                      {update.landmark.name}
                    </span>
                  )}
                </div>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                  {update.progress}% Complete
                </span>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{update.description}</p>

              {/* Landmark Details */}
              {update.landmark && (
                <div className="flex items-center gap-2 mb-3 text-xs text-gray-500 dark:text-gray-400">
                  <i className="ri-compass-3-line"></i>
                  <span>Location: {update.landmark.coordinates}</span>
                  <button className="text-blue-600 hover:text-blue-800 hover:underline ml-2">
                    View on Map
                  </button>
                </div>
              )}

              {/* Images Grid */}
              {update.images.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-image-line text-gray-500 text-sm"></i>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{update.images.length} Photo{update.images.length > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {update.images.map((image: any, imgIndex: number) => (
                      <div
                        key={imgIndex}
                        className="relative flex-shrink-0 group cursor-pointer"
                        onClick={() => setPreviewImage({ url: (image as any).fallback && (image as any)._failed ? (image as any).fallback : image.url, caption: image.caption })}
                      >
                        <img
                          src={image.thumbnail}
                          alt={image.caption}
                          onError={(e) => {
                            const fb = (image as any).fallback;
                            const el = e.currentTarget as HTMLImageElement;
                            if (fb && el.src !== fb) {
                              (image as any)._failed = true;
                              el.src = fb;
                            }
                          }}
                          className="w-24 h-24 object-cover rounded-lg border border-gray-200 group-hover:border-orange-400 transition-colors"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-lg transition-colors flex items-center justify-center">
                          <i className="ri-zoom-in-line text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1 rounded-b-lg">
                          <p className="text-white text-[10px] truncate">{image.caption}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <i className={`${update.updatedByType === 'engineer' ? 'ri-user-settings-line' : 'ri-building-line'} mr-0.5`}></i>
                  <span>Updated by:</span>
                  <button
                    onClick={() => update.updatedByType === 'engineer' ? onEngineerClick() : onContractorClick()}
                    className={`font-semibold hover:underline transition-colors ${
                      update.updatedByType === 'engineer'
                        ? 'text-green-700 hover:text-green-800'
                        : 'text-blue-700 hover:text-blue-800'
                    }`}
                  >
                    {update.updatedByName}
                  </button>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    update.updatedByType === 'engineer'
                      ? 'bg-green-50 text-green-600 border border-green-200'
                      : 'bg-blue-50 text-blue-600 border border-blue-200'
                  }`}>
                    {update.updatedByDesignation}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Edit">
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The inspection history of one work.
 *
 * This tab was a hardcoded empty array with a mock card layout behind it: the API
 * call the client already had (inspectionsApi.getByWork) hit a route nothing served,
 * so no work ever showed an inspection whatever had been recorded against it. It now
 * reads GET /works/:workId/inspections, and each visit opens into its full history —
 * the site photographs with the time and GPS point of each, the assignment trail, and
 * who recorded the outcome (InspectionHistory).
 *
 * Inspections are scheduled from Progress > Inspections, which owns the officer
 * picker and the notification that assignment raises; this tab is the read of them.
 */
function InspectionsTab({ workId }: { workId?: number }) {
  const [inspections, setInspections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!workId) { setInspections([]); setLoading(false); return; }
    setLoading(true);
    inspectionsApi.getByWork(workId)
      .then((rows: any) => { if (!cancelled) setInspections(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setInspections([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workId]);

  const getStatusColor = (code: string) => {
    switch (String(code || '').toUpperCase()) {
      case 'COMPLETED': return 'bg-green-100 text-green-700 border-green-200';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'POSTPONED': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'CANCELLED': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getRatingColor = (code: string) => {
    switch (String(code || '').toUpperCase()) {
      case 'EXCELLENT': return 'bg-green-100 text-green-700';
      case 'GOOD': return 'bg-blue-100 text-blue-700';
      case 'SATISFACTORY': return 'bg-cyan-100 text-cyan-700';
      case 'POOR': return 'bg-orange-100 text-orange-700';
      case 'CRITICAL': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeIcon = (code: string) => {
    switch (String(code || '').toUpperCase()) {
      case 'QUALITY': return 'ri-shield-check-line text-blue-600';
      case 'MILESTONE': return 'ri-line-chart-line text-green-600';
      case 'SAFETY': return 'ri-alarm-warning-line text-orange-600';
      case 'THIRD_PARTY': return 'ri-user-star-line text-purple-600';
      case 'FINAL': return 'ri-flag-2-line text-rose-600';
      default: return 'ri-search-eye-line text-gray-600';
    }
  };

  const completed = inspections.filter((i) => String(i.inspctn_sts_cd).toUpperCase() === 'COMPLETED').length;
  const scheduled = inspections.length - completed;
  const critical = inspections.reduce((s, i) => s + Number(i.crtcl_fndng_ct || 0), 0);
  // The list is ordered newest-visit-first by the API, so the first row carrying a
  // date is the most recent inspection this work has had.
  const lastDt = inspections.find((i) => i.actl_dt || i.schdl_dt);

  if (loading) {
    return (
      <ListSkeleton rows={3} avatar={false} />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-800 dark:text-gray-100">Site Inspections</h4>
        <span className="text-xs text-gray-400">Scheduled from Progress &gt; Inspections</span>
      </div>

      {inspections.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <i className="ri-search-eye-line text-4xl mb-2 block"></i>
          <p className="text-sm">No inspections recorded yet.</p>
        </div>
      ) : (<>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-green-50 rounded-lg p-3 border border-green-200 text-center">
          <p className="text-2xl font-bold text-green-700">{completed}</p>
          <span className="text-xs text-green-600">Completed</span>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 text-center">
          <p className="text-2xl font-bold text-blue-700">{scheduled}</p>
          <span className="text-xs text-blue-600">Open / Scheduled</span>
        </div>
        <div className={`rounded-lg p-3 border text-center ${critical > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'}`}>
          <p className={`text-2xl font-bold ${critical > 0 ? 'text-red-700' : 'text-gray-700 dark:text-gray-300'}`}>{critical}</p>
          <span className="text-xs text-gray-600">Critical Findings</span>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600 text-center">
          <p className="text-lg font-bold text-gray-700 dark:text-gray-300 mt-1">
            {lastDt ? (lastDt.actl_dt || lastDt.schdl_dt) : '—'}
          </p>
          <span className="text-xs text-gray-600">Last Inspection</span>
        </div>
      </div>

      {/* Inspections List — one card per visit, expanding to its full history */}
      <div className="space-y-3">
        {inspections.map((insp) => {
          const open = openId === insp.inspctn_id;
          return (
            <div key={insp.inspctn_id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenId(open ? null : insp.inspctn_id)}
                className="w-full text-left p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center shrink-0">
                      <i className={`${getTypeIcon(insp.inspctn_type_cd)} text-xl`}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 font-mono">{insp.inspctn_cd}</span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(insp.inspctn_sts_cd)}`}>
                          {insp.inspctn_sts_nm || insp.inspctn_sts_cd}
                        </span>
                        {insp.ovrl_rtng_cd && (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRatingColor(insp.ovrl_rtng_cd)}`}>
                            {insp.ovrl_rtng_nm || insp.ovrl_rtng_cd}
                          </span>
                        )}
                        {Number(insp.photo_ct) > 0 && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-violet-100 text-violet-700 flex items-center gap-1">
                            <i className="ri-image-line"></i>{insp.photo_ct}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {insp.inspctn_type_nm || insp.inspctn_type_cd}
                        {insp.inspctn_nm ? ` — ${insp.inspctn_nm}` : ''}
                      </span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                      {insp.actl_dt || insp.schdl_dt || '—'}
                    </span>
                    <div className="text-[11px] text-gray-500">
                      {insp.actl_dt ? 'carried out' : 'scheduled'}
                    </div>
                    {insp.lctn_tx && (
                      <div className="flex items-center justify-end gap-1 text-xs text-blue-600 mt-0.5">
                        <i className="ri-map-pin-2-fill"></i>
                        <span className="truncate max-w-[180px]">{insp.lctn_tx}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-3 text-sm flex-wrap">
                  <i className="ri-user-line text-gray-400"></i>
                  <span className="text-gray-700 dark:text-gray-300">{insp.inspctr_nm || '—'}</span>
                  {insp.inspctr_dsgn_nm && (<>
                    <span className="text-gray-400">|</span>
                    <span className="text-gray-500 dark:text-gray-400">{insp.inspctr_dsgn_nm}</span>
                  </>)}
                  <span className="ml-auto text-xs text-violet-600 flex items-center gap-1">
                    <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
                    {open ? 'Hide history' : 'View history & photos'}
                  </span>
                </div>
              </button>

              {open && (
                <div className="px-4 pb-4 pt-1 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  {/* The card already names the dates, inspector and location, so the
                      history renders without repeating them. */}
                  <InspectionHistory inspectionId={insp.inspctn_id} hideSummary />
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>)}
    </div>
  );
}

interface MapTabProps {
  workId?: number;
  workCode?: string;
  workName?: string;
  workStatus?: string;
  workType?: string;
  workCategory?: string;
  schemeName?: string;
  sanctionAmount?: number;
  workTypeId?: number;
  udaId?: number;
  udaName?: string;
  sachivalayams?: { svm_id: number; svm_cd: string; svm_nm: string }[];
  location: { gpsCoordinates: string; address: string; district?: string; municipality?: string; ward?: string };
}

interface GeoDataShape {
  markers: Array<{ lat: number; lng: number; label?: string }>;
  polylines: Array<Array<{ lat: number; lng: number }>>;
  polygons: Array<Array<{ lat: number; lng: number }>>;
}

function MapTab({ workId, workCode, workName, workStatus, workType, workCategory, schemeName, sanctionAmount, workTypeId, udaId, udaName, sachivalayams: workSachivalayams = [], location }: MapTabProps) {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [baseLayerKey, setBaseLayerKey] = useState('osm');
  const [referenceGeoms, setReferenceGeoms] = useState<any[]>([]);
  const [progressEntries, setProgressEntries] = useState<any[]>([]);
  const [deviationGeoms, setDeviationGeoms] = useState<any[]>([]);
  const [typeConfig, setTypeConfig] = useState<any[]>([]);
  const [activeSection, setActiveSection] = useState<'reference' | 'progress' | 'deviation'>('reference');
  const [isDrawing, setIsDrawing] = useState(false);
  // Overlay layers
  const [showLayersPanel, setShowLayersPanel] = useState(false);
  const [overlayLayers, setOverlayLayers] = useState<{ ulbBorder: boolean; sachivalayams: boolean }>({ ulbBorder: true, sachivalayams: false });
  const [layersLoading, setLayersLoading] = useState<Record<string, boolean>>({});
  const [drawingFor, setDrawingFor] = useState<{ type: string; configId?: number; category: string } | null>(null);
  const [editingGeomId, setEditingGeomId] = useState<number | null>(null);
  const [selectedProgressId, setSelectedProgressId] = useState<number | null>(null);
  const [showProgressForm, setShowProgressForm] = useState(false);
  const [showDeviationForm, setShowDeviationForm] = useState(false);
  const [drawnGeoData, setDrawnGeoData] = useState<GeoDataShape | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progressPhotos, setProgressPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const drawnLayersRef = useRef<L.LayerGroup>(L.layerGroup());
  const ulbBoundaryRef = useRef<L.GeoJSON | null>(null);
  const sachivalayamLayerRef = useRef<L.GeoJSON | null>(null);
  const linkedLayerRef = useRef<L.LayerGroup>(L.layerGroup());
  const [linkedMappings, setLinkedMappings] = useState<any[]>([]);
  const [showLinkedGeo, setShowLinkedGeo] = useState(true);
  const [collapsedLinkedCats, setCollapsedLinkedCats] = useState<Set<string>>(new Set());
  const linkedParcelLayersRef = useRef<Map<number, L.Layer>>(new Map());
  const [hoveredLinkedId, setHoveredLinkedId] = useState<number | null>(null);
  const [showGisParcelSelector, setShowGisParcelSelector] = useState(false);
  const [savingParcel, setSavingParcel] = useState(false);
  const [embEntries, setEmbEntries] = useState<any[]>([]);
  const [selectedEmbForProgress, setSelectedEmbForProgress] = useState<number | null>(null);

  // Progress form state
  const [progressForm, setProgressForm] = useState({
    prgrs_dt: todayYmd(),
    sctn_nm: '',
    actvty_nm: '',
    sts_cd: 'IN_PROGRESS',
    cmpltd_ln_nu: 0,
    cmpltd_ar_nu: 0,
    rmrks_tx: '',
  });
  // Deviation detail state
  const [deviationDetails, setDeviationDetails] = useState<any[]>([]);
  const [selectedDeviationId, setSelectedDeviationId] = useState<number | null>(null);
  const [showCloseModal, setShowCloseModal] = useState<number | null>(null); // dvtnDtlId to close
  const [deviationPhotos, setDeviationPhotos] = useState<File[]>([]);
  const [deviationPhotoPreviews, setDeviationPhotoPreviews] = useState<string[]>([]);
  const deviationPhotoInputRef = useRef<HTMLInputElement>(null);
  // Close modal state
  const [closeForm, setCloseForm] = useState({
    clsd_dt: todayYmd(),
    clsd_rmrks_tx: '',
    vrfd_by_nm: '',
    pymnt_ddctn_in: false,
    ddctn_at: 0,
    ddctn_rcvrd_in: false,
    ddctn_rmrks_tx: ''
  });
  const [closePhotos, setClosePhotos] = useState<File[]>([]);
  const [closePhotoPreviews, setClosePhotoPreviews] = useState<string[]>([]);
  const closePhotoInputRef = useRef<HTMLInputElement>(null);
  // Deviation form state (expanded)
  const [deviationForm, setDeviationForm] = useState({
    notes_tx: '', geom_lbl_tx: '',
    idntfd_dt: todayYmd(),
    idntfd_by_nm: '',
    dvtn_type_cd: 'QUALITY',
    dscn_tx: ''
  });

  // Photo handlers
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setProgressPhotos(prev => [...prev, ...files]);
    // Generate previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotoPreviews(prev => [...prev, ev.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });
    // Reset input so same file can be re-selected
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const removePhoto = (index: number) => {
    setProgressPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const clearPhotos = () => {
    setProgressPhotos([]);
    setPhotoPreviews([]);
  };

  // Deviation photo handlers
  const handleDeviationPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setDeviationPhotos(prev => [...prev, ...files]);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setDeviationPhotoPreviews(prev => [...prev, ev.target?.result as string]);
      reader.readAsDataURL(file);
    });
    if (deviationPhotoInputRef.current) deviationPhotoInputRef.current.value = '';
  };
  const removeDeviationPhoto = (index: number) => {
    setDeviationPhotos(prev => prev.filter((_, i) => i !== index));
    setDeviationPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };
  const clearDeviationPhotos = () => { setDeviationPhotos([]); setDeviationPhotoPreviews([]); };

  // Close modal photo handlers
  const handleClosePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setClosePhotos(prev => [...prev, ...files]);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setClosePhotoPreviews(prev => [...prev, ev.target?.result as string]);
      reader.readAsDataURL(file);
    });
    if (closePhotoInputRef.current) closePhotoInputRef.current.value = '';
  };
  const removeClosePhoto = (index: number) => {
    setClosePhotos(prev => prev.filter((_, i) => i !== index));
    setClosePhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };
  const clearClosePhotos = () => { setClosePhotos([]); setClosePhotoPreviews([]); };

  // Parse GPS coordinates string to [lat, lng]
  const parseGps = useCallback((): [number, number] => {
    if (!location.gpsCoordinates) return [17.385, 78.4867]; // default Hyderabad
    const match = location.gpsCoordinates.match(/([\d.]+)\s*°?\s*N?\s*,?\s*([\d.]+)\s*°?\s*E?/i);
    if (match) return [parseFloat(match[1]), parseFloat(match[2])];
    return [17.385, 78.4867];
  }, [location.gpsCoordinates]);

  // Load all data
  const loadAll = useCallback(async () => {
    if (!workId) return;
    setLoading(true);
    try {
      const promises: Promise<any>[] = [
        workGisApi.getGeometries(workId).catch(() => ({ data: [] })),
        workGisApi.getProgress(workId).catch(() => ({ data: [] })),
        workGisApi.getDeviations(workId).catch(() => []),
      ];
      if (workTypeId) promises.push(workGisApi.getTypeConfig(workTypeId).catch(() => ({ data: [] })));

      const results = await Promise.all(promises);
      const allGeoms = results[0]?.data || results[0] || [];
      const progressData = results[1]?.data || results[1] || [];
      const deviationData = results[2]?.data || results[2] || [];
      const configData = results[3]?.data || results[3] || [];

      setReferenceGeoms(Array.isArray(allGeoms) ? allGeoms.filter((g: any) => g.geomCategory === 'REFERENCE' || g.geom_ctgry_cd === 'REFERENCE') : []);
      setDeviationGeoms(Array.isArray(allGeoms) ? allGeoms.filter((g: any) => g.geomCategory === 'DEVIATION' || g.geom_ctgry_cd === 'DEVIATION') : []);
      setProgressEntries(Array.isArray(progressData) ? progressData : []);
      setDeviationDetails(Array.isArray(deviationData) ? deviationData : []);
      setTypeConfig(Array.isArray(configData) ? configData : []);

      // Load MBook entries for this work
      try {
        const embRes = await axiosInstance.get(`ds-wms/emb/work/${workId}`);
        setEmbEntries(embRes.data?.data || embRes.data || []);
      } catch { setEmbEntries([]); }
    } catch (err) {
      console.error('Failed to load GIS data:', err);
    } finally {
      setLoading(false);
    }
  }, [workId, workTypeId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    const [lat, lng] = parseGps();
    const map = L.map(mapContainerRef.current, { center: [lat, lng], zoom: 15, zoomControl: false });
    L.control.zoom({ position: 'topright' }).addTo(map);

    const layer = BASE_LAYERS.find(l => l.key === baseLayerKey) || BASE_LAYERS[0];
    const tileLayer = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: layer.maxZoom || 19 }).addTo(map);
    tileLayerRef.current = tileLayer;
    drawnLayersRef.current.addTo(map);
    linkedLayerRef.current.addTo(map);
    mapRef.current = map;

    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Toggle UDA border layer — fetches only this UDA from server
  useEffect(() => {
    if (!mapRef.current || !udaName) return;
    if (!overlayLayers.ulbBorder) {
      if (ulbBoundaryRef.current) { ulbBoundaryRef.current.remove(); ulbBoundaryRef.current = null; }
      return;
    }
    const loadUlbBorder = async () => {
      setLayersLoading(p => ({ ...p, ulbBorder: true }));
      try {
        const res = await fetch(`/apiv1/ds-wms/gis-layers/ulb-boundary/${encodeURIComponent(udaName)}`);
        const json = await res.json();
        const geojson = json.data;
        if (!geojson?.features?.length || !mapRef.current) return;

        if (ulbBoundaryRef.current) ulbBoundaryRef.current.remove();
        const layer = L.geoJSON(geojson, {
          style: { color: '#0d9488', weight: 2.5, fillColor: '#0d9488', fillOpacity: 0.04, dashArray: '8 4' },
          onEachFeature: (feature: any, lyr: any) => {
            lyr.bindTooltip(feature.properties?.ulb_nm || udaName, { permanent: false, direction: 'center' });
          }
        }).addTo(mapRef.current);
        ulbBoundaryRef.current = layer;
        const bounds = layer.getBounds();
        if (bounds.isValid()) mapRef.current.fitBounds(bounds.pad(0.1));
      } catch (error) {
        console.error('Error loading UDA boundary:', error);
      } finally {
        setLayersLoading(p => ({ ...p, ulbBorder: false }));
      }
    };
    setTimeout(loadUlbBorder, 300);
  }, [udaName, overlayLayers.ulbBorder]);

  // Toggle Sachivalayam layer — fetches only sachivalayams in this UDA from server
  useEffect(() => {
    if (!mapRef.current || !udaName) return;
    if (!overlayLayers.sachivalayams) {
      if (sachivalayamLayerRef.current) { sachivalayamLayerRef.current.remove(); sachivalayamLayerRef.current = null; }
      return;
    }
    const loadSachivalayams = async () => {
      setLayersLoading(p => ({ ...p, sachivalayams: true }));
      try {
        const res = await fetch(`/apiv1/ds-wms/gis-layers/sachivalayams/${encodeURIComponent(udaName)}`);
        const json = await res.json();
        const geojson = json.data;
        if (!geojson?.features?.length || !mapRef.current) return;

        if (sachivalayamLayerRef.current) sachivalayamLayerRef.current.remove();
        const colors = ['#f59e0b', '#10b981', '#ec4899', '#8b5cf6', '#06b6d4', '#ef4444', '#84cc16', '#f97316'];
        let colorIdx = 0;
        const layer = L.geoJSON(geojson, {
          style: () => {
            const c = colors[colorIdx % colors.length];
            colorIdx++;
            return { color: c, weight: 1.5, fillColor: c, fillOpacity: 0.1 };
          },
          onEachFeature: (feature: any, lyr: any) => {
            const p = feature.properties || {};
            const name = p.svm_nm || 'Sachivalayam';
            const code = p.svm_cd || '';
            const tooltipHtml = `<div style="text-align:center;line-height:1.4"><b style="font-size:12px">${name}</b>${code ? '<br/><span style="font-size:10px;color:#6b7280">' + code + '</span>' : ''}</div>`;
            lyr.bindTooltip(tooltipHtml, { permanent: false, direction: 'top', sticky: true, opacity: 0.95 });
            lyr.on('mouseover', () => { lyr.setStyle({ fillOpacity: 0.3, weight: 2.5 }); });
            lyr.on('mouseout', () => { lyr.setStyle({ fillOpacity: 0.1, weight: 1.5 }); });
          }
        }).addTo(mapRef.current);
        sachivalayamLayerRef.current = layer;
      } catch (error) {
        console.error('Error loading sachivalayam layer:', error);
      } finally {
        setLayersLoading(p => ({ ...p, sachivalayams: false }));
      }
    };
    setTimeout(loadSachivalayams, 500);
  }, [udaName, overlayLayers.sachivalayams]);

  // Load & plot linked parcel geographies + sachivalayam boundaries
  useEffect(() => {
    if (!mapRef.current || !workId || !showLinkedGeo) {
      linkedLayerRef.current.clearLayers();
      return;
    }

    const loadLinked = async () => {
      try {
        linkedLayerRef.current.clearLayers();

        // Load parcel mappings
        const mappingsRes = await axiosInstance.get(`ds-wms/parcel-mapping/work/${workId}`);
        const mappings: any[] = mappingsRes.data?.data || [];
        setLinkedMappings(mappings);

        const mappedIds = new Set(mappings.map((m: any) => Number(m.prcl_id)));
        const mappedCategories = new Set(mappings.map((m: any) => m.prcl_ctgry_cd));
        const svmIds = [...new Set(mappings.map((m: any) => Number(m.svm_id)).filter(Boolean))];

        // Load all sachivalayam boundaries for the UDA (always, not just when parcels exist)
        if (udaId) {
          try {
            const svmCodes = new Set(workSachivalayams.map(s => String(s.svm_cd)));
            const mappingSvmIds = new Set(svmIds.map(String));

            const bndRes = await axiosInstance.get(`ds-wms/dashboard/sachivalayam-boundaries`, { params: { udaId } });
            const bndGeo = bndRes.data?.data || bndRes.data;
            if (bndGeo?.features) {
              const svmLayer = L.geoJSON(bndGeo, {
                style: (feature: any) => {
                  const p = feature?.properties || {};
                  const isMapped = svmCodes.has(String(p.svm_cd)) || mappingSvmIds.has(String(p.svm_id));
                  return isMapped
                    ? { color: '#7c3aed', weight: 2.5, fillColor: '#7c3aed', fillOpacity: 0.08, dashArray: '5,5' }
                    : { color: '#94a3b8', weight: 1, fillColor: '#e2e8f0', fillOpacity: 0.03, dashArray: '3,3' };
                },
                onEachFeature: (feature: any, lyr: any) => {
                  const p = feature.properties || {};
                  const isMapped = svmCodes.has(String(p.svm_cd)) || mappingSvmIds.has(String(p.svm_id));
                  lyr.bindTooltip(`${p.svm_nm || ''} - ${p.svm_cd || ''}${isMapped ? ' (Mapped)' : ''}`, { permanent: false, direction: 'center', className: 'text-xs font-medium' });
                },
              });
              linkedLayerRef.current.addLayer(svmLayer);
            }
          } catch (e) { console.error('Failed to load sachivalayam boundaries:', e); }
        }

        if (mappings.length === 0) return;

        // Load and plot road geometries
        linkedParcelLayersRef.current.clear();
        for (const svmId of svmIds) {
          if (mappedCategories.has('roads')) {
            try {
              const res = await axiosInstance.get(`ds-wms/dashboard/road-network`, { params: { svmId } });
              const geojson = res.data?.data || res.data;
              if (geojson?.features) {
                const layer = L.geoJSON(geojson, {
                  filter: (feature: any) => mappedIds.has(Number(feature.properties?.ds_lyr_id || feature.properties?.road_id)),
                  style: () => ({ color: '#3b82f6', weight: 4, fillOpacity: 0.2, fill: true }),
                  onEachFeature: (feature: any, fl: any) => {
                    const p = feature.properties || {};
                    const id = Number(p.ds_lyr_id || p.road_id);
                    linkedParcelLayersRef.current.set(id, fl);
                    fl.bindTooltip(`${p.road_name || 'Road'} · ${p.road_type || ''}`, { direction: 'center', className: 'text-xs font-medium' });
                  },
                });
                linkedLayerRef.current.addLayer(layer);
              }
            } catch (e) { /* skip */ }
          }

          if (mappedCategories.has('land_parcels') || mappedCategories.has('water_bodies') || mappedCategories.has('public_infra')) {
            try {
              const res = await axiosInstance.get(`ds-wms/dashboard/land-parcels`, { params: { svmId } });
              const geojson = res.data?.data || res.data;
              if (geojson?.features) {
                const layer = L.geoJSON(geojson, {
                  filter: (feature: any) => mappedIds.has(Number(feature.properties?.ds_lyr_id || feature.properties?.parcel_id)),
                  style: (feature: any) => {
                    const cid = Number(feature?.properties?.category_id);
                    const colors: Record<number, string> = { 78: '#15803d', 77: '#a16207', 105: '#6d28d9', 98: '#0891b2', 106: '#c2410c' };
                    const c = colors[cid] || '#7c3aed';
                    return { color: c, weight: 2.5, fillColor: c, fillOpacity: 0.3, fill: true };
                  },
                  onEachFeature: (feature: any, fl: any) => {
                    const p = feature.properties || {};
                    const id = Number(p.ds_lyr_id || p.parcel_id);
                    linkedParcelLayersRef.current.set(id, fl);
                    fl.bindTooltip(p.category || p.land_usage || `Parcel ${p.parcel_id}`, { direction: 'center', className: 'text-xs font-medium' });
                  },
                });
                linkedLayerRef.current.addLayer(layer);
              }
            } catch (e) { /* skip */ }
          }
        }

        // Fit bounds to linked layers
        const layers = linkedLayerRef.current.getLayers();
        if (layers.length > 0 && mapRef.current) {
          const group = L.featureGroup(layers);
          const bounds = group.getBounds();
          if (bounds.isValid()) mapRef.current.fitBounds(bounds.pad(0.15));
        }
      } catch (e) {
        console.error('Failed to load linked geographies:', e);
      }
    };

    setTimeout(loadLinked, 400);
  }, [workId, showLinkedGeo, udaId]);

  // Highlight hovered linked parcel on map
  useEffect(() => {
    linkedParcelLayersRef.current.forEach((layer, id) => {
      const path = layer as L.Path;
      if (id === hoveredLinkedId) {
        path.setStyle({ color: '#f59e0b', weight: 5, fillColor: '#fef3c7', fillOpacity: 0.5 });
        (path as any).bringToFront?.();
        (path as any).openTooltip?.();
      } else {
        // Restore original style — reset by re-triggering the layer's default
        (path as any).closeTooltip?.();
      }
    });
    // Reset non-hovered to original colors
    if (hoveredLinkedId === null) {
      linkedParcelLayersRef.current.forEach((layer) => {
        const path = layer as L.Path;
        const feature = (path as any).feature;
        if (!feature) return;
        const p = feature.properties || {};
        if (p.road_name !== undefined) {
          path.setStyle({ color: '#3b82f6', weight: 4, fillOpacity: 0.2 });
        } else {
          const cid = Number(p.category_id);
          const colors: Record<number, string> = { 78: '#15803d', 77: '#a16207', 105: '#6d28d9', 98: '#0891b2', 106: '#c2410c' };
          const c = colors[cid] || '#7c3aed';
          path.setStyle({ color: c, weight: 2.5, fillColor: c, fillOpacity: 0.3 });
        }
      });
    }
  }, [hoveredLinkedId]);

  // Invalidate map size on fullscreen toggle or container resize
  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => mapRef.current?.invalidateSize(), 100);
      setTimeout(() => mapRef.current?.invalidateSize(), 350);
    }
  }, [isFullScreen]);

  // ResizeObserver to handle parent fullscreen changes
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      mapRef.current?.invalidateSize();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Switch base layer
  useEffect(() => {
    if (!mapRef.current) return;
    const layer = BASE_LAYERS.find(l => l.key === baseLayerKey) || BASE_LAYERS[0];
    if (tileLayerRef.current) tileLayerRef.current.remove();
    const newTile = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: layer.maxZoom || 19 }).addTo(mapRef.current);
    tileLayerRef.current = newTile;
  }, [baseLayerKey]);

  // Render geometries on map
  useEffect(() => {
    if (!mapRef.current) return;
    drawnLayersRef.current.clearLayers();
    const allBounds: L.LatLng[] = [];

    const renderGeoData = (geom: GeoDataShape, color: string, label?: string) => {
      if (!geom) return;
      (geom.polygons || []).forEach((ring) => {
        const latlngs = ring.map(p => L.latLng(p.lat, p.lng));
        allBounds.push(...latlngs);
        const poly = L.polygon(latlngs, { color, fillColor: color, fillOpacity: 0.15, weight: 2 });
        if (label) poly.bindPopup(`<b>${label}</b>`);
        drawnLayersRef.current.addLayer(poly);
      });
      (geom.polylines || []).forEach((line) => {
        const latlngs = line.map(p => L.latLng(p.lat, p.lng));
        allBounds.push(...latlngs);
        const pl = L.polyline(latlngs, { color, weight: 3 });
        if (label) pl.bindPopup(`<b>${label}</b>`);
        drawnLayersRef.current.addLayer(pl);
      });
      (geom.markers || []).forEach((m) => {
        const ll = L.latLng(m.lat, m.lng);
        allBounds.push(ll);
        const mk = L.marker(ll, {
          icon: L.divIcon({
            className: '',
            html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>`,
            iconSize: [14, 14], iconAnchor: [7, 7],
          })
        });
        if (m.label || label) mk.bindPopup(`<b>${m.label || label}</b>`);
        drawnLayersRef.current.addLayer(mk);
      });
    };

    // Reference geometries — blue
    referenceGeoms.forEach(g => {
      const geom = g.geomData || g.geom_jn;
      if (geom) renderGeoData(geom, g.geomColor || g.geom_color_tx || '#3b82f6', g.gisElementName || g.notes_tx || 'Reference');
    });
    // Progress geometries — green for existing, purple for newly added (last 5 min)
    progressEntries.forEach(p => {
      const geom = p.geomData || p.geom_jn;
      if (geom) {
        const isSelected = selectedProgressId === (p.gisProgressId || p.gis_prgrs_id);
        const isRecent = p.i_ts && (Date.now() - new Date(p.i_ts).getTime()) < 300000; // last 5 min
        const color = isSelected ? '#16a34a' : isRecent ? '#7c3aed' : '#22c55e'; // purple for recent
        renderGeoData(geom, color, p.sectionName || p.sctn_nm || 'Progress');
      }
    });
    // Deviation geometries — red
    deviationGeoms.forEach(g => {
      const geom = g.geomData || g.geom_jn;
      if (geom) renderGeoData(geom, g.geomColor || g.geom_color_tx || '#ef4444', g.notes_tx || 'Deviation');
    });

    // Fit bounds
    if (allBounds.length > 0) {
      mapRef.current.fitBounds(L.latLngBounds(allBounds).pad(0.2));
    }
  }, [referenceGeoms, progressEntries, deviationGeoms, deviationDetails, selectedProgressId]);

  // Highlight linked parcels when a progress entry is selected
  useEffect(() => {
    linkedParcelLayersRef.current.forEach((layer, id) => {
      const path = layer as L.Path;
      const feature = (path as any).feature;
      if (!feature) return;
      const p = feature.properties || {};

      if (selectedProgressId) {
        // Highlight all linked parcels in bright yellow/orange
        path.setStyle({ color: '#f59e0b', weight: 4, fillColor: '#fef3c7', fillOpacity: 0.5 });
        (path as any).bringToFront?.();
      } else {
        // Restore original colors
        if (p.road_name !== undefined) {
          path.setStyle({ color: '#3b82f6', weight: 4, fillOpacity: 0.2 });
        } else {
          const cid = Number(p.category_id);
          const colors: Record<number, string> = { 78: '#15803d', 77: '#a16207', 105: '#6d28d9', 98: '#0891b2', 106: '#c2410c' };
          const c = colors[cid] || '#7c3aed';
          path.setStyle({ color: c, weight: 2.5, fillColor: c, fillOpacity: 0.3 });
        }
      }
    });
  }, [selectedProgressId]);

  // Geoman drawing mode
  const startDrawing = (geomType: string, category: string, configId?: number) => {
    if (!mapRef.current) return;
    setIsDrawing(true);
    setDrawingFor({ type: geomType, configId, category });
    setDrawnGeoData({ markers: [], polylines: [], polygons: [] });

    const map = mapRef.current;
    // Remove previous controls
    map.pm.removeControls();
    map.pm.addControls({
      position: 'topleft',
      drawCircle: false, drawCircleMarker: false, drawRectangle: false, drawText: false,
      rotateMode: false, cutPolygon: false,
      drawMarker: geomType === 'MARKER',
      drawPolyline: geomType === 'POLYLINE',
      drawPolygon: geomType === 'POLYGON',
      editMode: true, removalMode: true, dragMode: true,
    });

    // Start the appropriate draw mode with purple styling
    const drawStyle = { color: '#7c3aed', fillColor: '#7c3aed', fillOpacity: 0.2, weight: 3, templineStyle: { color: '#7c3aed', weight: 3 }, hintlineStyle: { color: '#7c3aed', dashArray: '5,5', weight: 2 } };
    if (geomType === 'POLYGON') map.pm.enableDraw('Polygon', { pathOptions: drawStyle, ...drawStyle });
    else if (geomType === 'POLYLINE') map.pm.enableDraw('Line', { pathOptions: drawStyle, ...drawStyle });
    else if (geomType === 'MARKER') map.pm.enableDraw('Marker');

    // Listen for created layers
    const onCreated = (e: any) => {
      const layer = e.layer;
      (layer as any)._drawnByPm = true;
      // Style new drawing in purple
      if (layer.setStyle) layer.setStyle({ color: '#7c3aed', fillColor: '#7c3aed', fillOpacity: 0.2, weight: 3 });
      setDrawnGeoData(prev => {
        if (!prev) return prev;
        const next = { ...prev };
        if (layer instanceof L.Polygon && !(layer instanceof L.Rectangle)) {
          const latlngs = (layer.getLatLngs()[0] as L.LatLng[]).map(ll => ({ lat: ll.lat, lng: ll.lng }));
          next.polygons = [...next.polygons, latlngs];
        } else if (layer instanceof L.Polyline) {
          const latlngs = (layer.getLatLngs() as L.LatLng[]).map(ll => ({ lat: ll.lat, lng: ll.lng }));
          next.polylines = [...next.polylines, latlngs];
        } else if (layer instanceof L.Marker) {
          const ll = layer.getLatLng();
          next.markers = [...next.markers, { lat: ll.lat, lng: ll.lng }];
        }
        return next;
      });
    };
    map.on('pm:create', onCreated);
    (map as any)._pmCreateHandler = onCreated;
  };

  const cancelDrawing = () => {
    if (!mapRef.current) return;
    mapRef.current.pm.disableDraw();
    mapRef.current.pm.removeControls();
    if ((mapRef.current as any)._pmCreateHandler) {
      mapRef.current.off('pm:create', (mapRef.current as any)._pmCreateHandler);
    }
    // Remove ONLY layers marked as drawn by Geoman
    const layersToRemove: L.Layer[] = [];
    mapRef.current.eachLayer(layer => {
      if ((layer as any)._drawnByPm) {
        layersToRemove.push(layer);
      }
    });
    layersToRemove.forEach(l => mapRef.current?.removeLayer(l));
    setIsDrawing(false);
    setDrawingFor(null);
    setDrawnGeoData(null);
  };

  const finishDrawing = async () => {
    if (!workId || !drawingFor || !drawnGeoData) return;
    const hasData = drawnGeoData.markers.length > 0 || drawnGeoData.polylines.length > 0 || drawnGeoData.polygons.length > 0;
    if (!hasData) { cancelDrawing(); return; }

    setSaving(true);
    try {
      if (drawingFor.category === 'REFERENCE') {
        await workGisApi.saveGeometry(workId, {
          geom_jn: drawnGeoData,
          geom_ctgry_cd: 'REFERENCE',
          geom_color_tx: '#3b82f6',
          notes_tx: typeConfig.find(c => c.cfgId === drawingFor.configId || c.cfg_id === drawingFor.configId)?.geomLabel || typeConfig.find(c => c.cfgId === drawingFor.configId || c.cfg_id === drawingFor.configId)?.geom_lbl_tx || 'Reference Geometry',
          gis_elmnt_type_cd: drawingFor.type,
        });
      } else if (drawingFor.category === 'PROGRESS') {
        const savedProgress = await workGisApi.saveProgress(workId, {
          ...progressForm,
          geom_jn: drawnGeoData,
        });
        // Upload photos if any
        const progressId = savedProgress?.gisProgressId || savedProgress?.gis_prgrs_id || 'new';
        if (progressPhotos.length > 0) {
          for (const photo of progressPhotos) {
            const fd = new FormData();
            fd.append('photos', photo);
            fd.append('phto_dt', progressForm.prgrs_dt);
            fd.append('phto_type_cd', 'DURING');
            fd.append('cptn_tx', photo.name);
            try { await workGisApi.uploadProgressPhoto(workId, progressId, fd); } catch (e) { console.error('Photo upload failed:', e); }
          }
        }
        setShowProgressForm(false);
        setProgressForm({ prgrs_dt: todayYmd(), sctn_nm: '', actvty_nm: '', sts_cd: 'IN_PROGRESS', cmpltd_ln_nu: 0, cmpltd_ar_nu: 0, rmrks_tx: '' });
        clearPhotos();
      } else if (drawingFor.category === 'DEVIATION') {
        // Step 1: Save geometry
        const geoResult = await workGisApi.saveGeometry(workId, {
          geom_jn: drawnGeoData,
          geom_ctgry_cd: 'DEVIATION',
          geom_color_tx: '#ef4444',
          notes_tx: deviationForm.dscn_tx || deviationForm.notes_tx,
          gis_elmnt_type_cd: drawingFor.type,
        });
        const gisRelId = geoResult?.wrk_gis_rel_id;
        // Step 2: Save deviation detail
        if (gisRelId) {
          const dvtnResult = await workGisApi.saveDeviation(workId, {
            wrk_gis_rel_id: gisRelId,
            idntfd_dt: deviationForm.idntfd_dt,
            idntfd_by_nm: deviationForm.idntfd_by_nm,
            dvtn_type_cd: deviationForm.dvtn_type_cd,
            dscn_tx: deviationForm.dscn_tx || deviationForm.notes_tx,
          });
          // Step 3: Upload identification photos
          const dvtnDtlId = dvtnResult?.dvtn_dtl_id;
          if (dvtnDtlId && deviationPhotos.length > 0) {
            for (const photo of deviationPhotos) {
              const fd = new FormData();
              fd.append('photos', photo);
              fd.append('phto_dt', deviationForm.idntfd_dt);
              fd.append('context', 'IDENT');
              fd.append('cptn_tx', photo.name);
              try { await workGisApi.uploadDeviationPhoto(workId, dvtnDtlId, fd); } catch (e) { console.error('Deviation photo upload failed:', e); }
            }
          }
        }
        setShowDeviationForm(false);
        setDeviationForm({ notes_tx: '', geom_lbl_tx: '', idntfd_dt: todayYmd(), idntfd_by_nm: '', dvtn_type_cd: 'QUALITY', dscn_tx: '' });
        clearDeviationPhotos();
      }
      cancelDrawing();
      // Small delay to let cancelDrawing finish before re-rendering
      await new Promise(r => setTimeout(r, 200));
      await loadAll();
    } catch (err) {
      console.error('Failed to save geometry:', err);
    } finally {
      setSaving(false);
    }
  };

  const deleteGeom = async (gisRelId: number) => {
    if (!workId || !confirm('Delete this geometry?')) return;
    try {
      await workGisApi.deleteGeometry(workId, gisRelId);
      await loadAll();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const deleteProgress = async (progressId: number) => {
    if (!workId || !confirm('Delete this progress entry?')) return;
    try {
      await workGisApi.deleteProgress(workId, progressId);
      await loadAll();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const focusGeometry = (geom: GeoDataShape) => {
    if (!mapRef.current || !geom) return;
    const pts: L.LatLng[] = [];
    (geom.polygons || []).forEach(ring => ring.forEach(p => pts.push(L.latLng(p.lat, p.lng))));
    (geom.polylines || []).forEach(line => line.forEach(p => pts.push(L.latLng(p.lat, p.lng))));
    (geom.markers || []).forEach(m => pts.push(L.latLng(m.lat, m.lng)));
    if (pts.length > 0) mapRef.current.fitBounds(L.latLngBounds(pts).pad(0.3));
  };

  // Check if a config slot has been drawn
  const getGeomForConfig = (cfg: any) => {
    const cfgGeomType = cfg.geomType || cfg.geom_type_cd;
    const cfgLabel = cfg.geomLabel || cfg.geom_lbl_tx;
    return referenceGeoms.find(g => {
      const gType = g.gisElementType || g.gis_elmnt_type_cd;
      const gLabel = g.notes_tx || g.gisElementName;
      return gType === cfgGeomType && gLabel === cfgLabel;
    });
  };

  return (
    <div className={`${isFullScreen ? 'fixed inset-0 z-50 bg-white dark:bg-gray-900 flex flex-col' : 'space-y-4'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between ${isFullScreen ? 'px-4 py-2 border-b border-gray-200 dark:border-gray-700' : ''}`}>
        <h4 className="font-medium text-gray-800 dark:text-gray-100">Work Location Map</h4>
        <div className="flex items-center gap-2">
          {/* Base layer switcher */}
          <select
            value={baseLayerKey}
            onChange={e => setBaseLayerKey(e.target.value)}
            className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          >
            {BASE_LAYERS.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
          </select>
          {/* Legend */}
          <div className="flex items-center gap-3 text-[10px] text-gray-500 mx-2">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-blue-500 rounded-full"></span>Reference</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-green-500 rounded-full"></span>Progress</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-500 rounded-full"></span>Deviation</span>
          </div>
          <button onClick={() => setIsFullScreen(!isFullScreen)}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs">
            <i className={isFullScreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}></i>
            {isFullScreen ? 'Exit' : 'Full Screen'}
          </button>
        </div>
      </div>

      {/* Main content: left panel + map */}
      <div className={`flex ${isFullScreen ? 'flex-1 min-h-0 overflow-hidden' : ''}`} style={{ height: isFullScreen ? undefined : '520px' }}>
        {/* Left Panel */}
        <div className="w-[280px] min-w-[280px] border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-gray-50 dark:bg-gray-800/50 rounded-l-lg">
          {/* Section tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {(['reference', 'progress', 'deviation'] as const).map(s => (
              <button key={s} onClick={() => setActiveSection(s)}
                className={`flex-1 py-2 text-[10px] font-medium transition-colors ${activeSection === s ? 'text-blue-600 border-b-2 border-blue-600 bg-white dark:bg-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
                {s === 'reference' ? 'Reference' : s === 'progress' ? 'Progress' : 'Deviation'}
              </button>
            ))}
          </div>

          <div className="p-3 space-y-2">
            {loading && <ListSkeleton rows={3} avatar={false} />}

            {/* REFERENCE SECTION */}
            {activeSection === 'reference' && !loading && (
              <>
                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Reference Geometries</div>
                {typeConfig.length > 0 ? typeConfig.map((cfg: any) => {
                  const cfgId = cfg.cfgId || cfg.cfg_id;
                  const geomType = cfg.geomType || cfg.geom_type_cd;
                  const label = cfg.geomLabel || cfg.geom_lbl_tx;
                  const isRequired = cfg.isRequired || cfg.is_rqrd_in;
                  const existing = getGeomForConfig(cfg);
                  return (
                    <div key={cfgId} className="bg-white dark:bg-gray-800 rounded-lg p-2.5 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${existing ? 'bg-green-100 text-green-700' : isRequired ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                          {existing ? 'Drawn' : isRequired ? 'Required' : 'Optional'}
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-400 mb-1.5">{geomType}</div>
                      <div className="flex gap-1">
                        {existing ? (
                          <>
                            <button onClick={() => focusGeometry(existing.geomData || existing.geom_jn)} className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100">View</button>
                            <button onClick={() => deleteGeom(existing.gisRelId || existing.gis_rel_id)} className="text-[10px] px-2 py-0.5 rounded bg-red-50 text-red-600 hover:bg-red-100">Delete</button>
                          </>
                        ) : (
                          <button disabled={isDrawing} onClick={() => startDrawing(geomType, 'REFERENCE', cfgId)}
                            className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50">
                            + Draw
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }) : (
                  <div className="text-xs text-gray-400 text-center py-2">
                    {workTypeId ? 'No geometry config for this work type' : 'Work type not set'}
                  </div>
                )}
                {/* Also show custom/unmatched reference geoms */}
                {referenceGeoms.filter(g => !typeConfig.some(cfg => getGeomForConfig(cfg)?.gis_rel_id === g.gis_rel_id || getGeomForConfig(cfg)?.gisRelId === g.gisRelId)).map((g, i) => (
                  <div key={`ref-${i}`} className="bg-white dark:bg-gray-800 rounded-lg p-2.5 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-blue-700">{g.notes_tx || g.gisElementName || 'Custom Geometry'}</span>
                      <span className="text-[9px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">Custom</span>
                    </div>
                    <div className="flex gap-1 mt-1.5">
                      <button onClick={() => focusGeometry(g.geomData || g.geom_jn)} className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100">View</button>
                      <button onClick={() => deleteGeom(g.gisRelId || g.gis_rel_id)} className="text-[10px] px-2 py-0.5 rounded bg-red-50 text-red-600 hover:bg-red-100">Delete</button>
                    </div>
                  </div>
                ))}
                {/* Free-form draw button */}
                <button disabled={isDrawing} onClick={() => startDrawing('POLYGON', 'REFERENCE')}
                  className="w-full text-[10px] px-2 py-1.5 rounded border border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 disabled:opacity-50 mt-2">
                  + Draw Custom Geometry
                </button>

                {/* Linked GIS Assets */}
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-1.5 mb-2">
                    <i className="ri-link text-emerald-500 text-xs" />
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Linked GIS Assets</span>
                    <span className="text-[10px] text-emerald-600 font-medium">{linkedMappings.length}</span>
                    <button onClick={() => setShowGisParcelSelector(true)}
                      className="ml-auto px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/30 transition-colors flex items-center gap-0.5">
                      <i className="ri-road-map-line text-[10px]"></i> {linkedMappings.length > 0 ? 'Edit' : 'Map GIS Assets'}
                    </button>
                  </div>
                  {linkedMappings.length > 0 ? (
                    <>{(['roads', 'water_bodies', 'public_infra', 'land_parcels'] as const).map(cat => {
                      const catMappings = linkedMappings.filter((m: any) => m.prcl_ctgry_cd === cat);
                      if (catMappings.length === 0) return null;
                      const catLabels: Record<string, { label: string; icon: string; color: string }> = {
                        roads: { label: 'Roads', icon: 'ri-route-line', color: 'text-blue-600' },
                        water_bodies: { label: 'Water Bodies', icon: 'ri-water-flash-line', color: 'text-cyan-600' },
                        public_infra: { label: 'Public Infra', icon: 'ri-government-line', color: 'text-amber-600' },
                        land_parcels: { label: 'Land Parcels', icon: 'ri-landscape-line', color: 'text-violet-600' },
                      };
                      const cfg = catLabels[cat];
                      const isCollapsed = collapsedLinkedCats.has(cat);
                      return (
                        <div key={cat} className="bg-emerald-50/50 dark:bg-emerald-900/10 rounded-lg border border-emerald-200 dark:border-emerald-800 mb-1.5 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setCollapsedLinkedCats(prev => {
                              const next = new Set(prev);
                              if (next.has(cat)) next.delete(cat); else next.add(cat);
                              return next;
                            })}
                            className="w-full px-2.5 py-1.5 flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                          >
                            <i className={`ri-arrow-${isCollapsed ? 'right' : 'down'}-s-line text-gray-400 text-xs`} />
                            <i className={`${cfg.icon} ${cfg.color} text-xs`} />
                            <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300">{cfg.label}</span>
                            <span className="text-[10px] text-emerald-600 font-medium ml-auto">{catMappings.length}</span>
                          </button>
                          {!isCollapsed && (
                            <div className="px-1.5 py-1.5 space-y-1">
                              {catMappings.map((m: any) => {
                                const pId = Number(m.prcl_id);
                                const isHov = hoveredLinkedId === pId;
                                return (
                                  <div
                                    key={m.wrk_prcl_rel_id}
                                    onMouseEnter={() => setHoveredLinkedId(pId)}
                                    onMouseLeave={() => setHoveredLinkedId(null)}
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-all cursor-pointer ${
                                      isHov
                                        ? 'bg-amber-50 border-amber-300 shadow-sm dark:bg-amber-900/20 dark:border-amber-700'
                                        : 'bg-white border-gray-100 hover:border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                                    }`}
                                  >
                                    <i className={`ri-map-pin-2-fill text-xs ${isHov ? 'text-amber-500' : 'text-emerald-400'}`} />
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-[10px] font-medium leading-tight truncate ${isHov ? 'text-amber-800 dark:text-amber-200' : 'text-gray-700 dark:text-gray-300'}`}>
                                        {m.prcl_nm || `#${m.prcl_id}`}
                                      </p>
                                      <p className="text-[9px] text-gray-400 truncate">
                                        {[m.prcl_sub_ctgry, m.svm_nm, m.road_mode_cd].filter(Boolean).join(' · ')}
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      title="Zoom to asset"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const layer = linkedParcelLayersRef.current.get(pId);
                                        if (layer && mapRef.current) {
                                          mapRef.current.fitBounds((layer as any).getBounds(), { padding: [40, 40], maxZoom: 18 });
                                        }
                                      }}
                                      className="shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/30 text-gray-400 hover:text-indigo-600 transition-colors"
                                    >
                                      <i className="ri-focus-3-line text-[10px]" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}</>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-4 text-gray-400 gap-1.5">
                      <i className="ri-map-pin-add-line text-xl"></i>
                      <p className="text-[10px]">No GIS assets linked</p>
                      <button onClick={() => setShowGisParcelSelector(true)}
                        className="text-[10px] text-emerald-600 hover:text-emerald-800 font-medium underline">Map GIS Assets</button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* PROGRESS SECTION */}
            {activeSection === 'progress' && !loading && (
              <>
                {/* MBook Entries */}
                {embEntries.length > 0 && (
                  <div className="mb-3">
                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                      <i className="ri-book-2-line text-indigo-500 text-xs"></i> Measurement Books
                    </div>
                    <div className="space-y-1">
                      {embEntries.map((emb: any) => {
                        const isSelected = selectedEmbForProgress === emb.emb_id;
                        const linkedCount = progressEntries.filter((p: any) => p.emb_id === emb.emb_id).length;
                        return (
                          <div key={emb.emb_id}
                            className={`rounded-lg p-2 border cursor-pointer transition-colors ${
                              isSelected ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-300' : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
                            }`}
                            onClick={() => setSelectedEmbForProgress(isSelected ? null : emb.emb_id)}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <i className="ri-ruler-2-line text-indigo-500 text-xs"></i>
                                <span className="text-[10px] font-bold text-indigo-700">{emb.mbook_nu || emb.emb_cd}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                {linkedCount > 0 && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{linkedCount} progress</span>
                                )}
                                <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                                  emb.sts_cd === 'APPROVED' ? 'bg-green-100 text-green-700' :
                                  emb.sts_cd === 'SUBMITTED' ? 'bg-amber-100 text-amber-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>{emb.sts_cd}</span>
                              </div>
                            </div>
                            <div className="text-[9px] text-gray-400 mt-0.5">{emb.msrmt_frm_dt} — {emb.msrmt_to_dt} | {emb.msrd_by_nm}</div>
                            {isSelected && (
                              <button disabled={isDrawing} onClick={(e) => { e.stopPropagation(); setShowProgressForm(true); }}
                                className="mt-1.5 w-full text-[10px] px-2 py-1 rounded border border-dashed border-green-400 text-green-600 hover:bg-green-50 disabled:opacity-50 flex items-center justify-center gap-0.5">
                                <i className="ri-add-line"></i> Add Progress Entry for this MB
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center justify-between">
                  <span>Progress Timeline</span>
                  {selectedEmbForProgress && (
                    <button onClick={() => setSelectedEmbForProgress(null)} className="text-[9px] text-indigo-500 hover:text-indigo-700 font-medium">Show All</button>
                  )}
                </div>
                {progressEntries
                  .filter((p: any) => !selectedEmbForProgress || p.emb_id === selectedEmbForProgress || !p.emb_id)
                  .map((p: any) => {
                  const pid = p.progressId || p.gisProgressId || p.gis_prgrs_id;
                  const isSelected = selectedProgressId === pid;
                  return (
                    <div key={pid}
                      className={`bg-white dark:bg-gray-800 rounded-lg p-2.5 border cursor-pointer transition-colors ${isSelected ? 'border-green-400 ring-1 ring-green-400' : 'border-gray-200 dark:border-gray-700 hover:border-green-300'}`}
                      onClick={() => {
                        setSelectedProgressId(isSelected ? null : pid);
                        const geom = p.geomData || p.geom_jn;
                        if (geom && !isSelected) focusGeometry(geom);
                      }}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-gray-400">{p.updateDate || p.progressDate || p.prgrs_dt}</span>
                          {(p.emb_id || p.mbook_nu) && (
                            <span className="text-[8px] px-1 py-0 rounded bg-indigo-100 text-indigo-600 font-medium">{p.mbook_nu || `MB #${p.emb_id}`}</span>
                          )}
                        </div>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                          (p.status || p.statusCode || p.sts_cd) === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                          (p.status || p.statusCode || p.sts_cd) === 'ON_HOLD' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>{p.status || p.statusCode || p.sts_cd}</span>
                      </div>
                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{p.sectionName || p.sctn_nm || 'Progress Entry'}</div>
                      {(p.activityName || p.actvty_nm) && <div className="text-[10px] text-gray-400 mt-0.5">{p.activityName || p.actvty_nm}</div>}
                      {(p.completedLength || p.cmpltd_ln_nu) > 0 && (
                        <div className="text-[10px] text-green-600 mt-0.5">Completed: {p.completedLength || p.cmpltd_ln_nu}m</div>
                      )}
                      {(p.remarks || p.rmrks_tx) && <div className="text-[10px] text-gray-400 mt-0.5 truncate">{p.remarks || p.rmrks_tx}</div>}
                      {/* Photos */}
                      {(() => {
                        const photos = p.photos || [];
                        const photoIds = p.photoIds || p.phto_ids_jn || [];
                        const photoCount = photos.length || (Array.isArray(photoIds) ? photoIds.length : 0);
                        return photoCount > 0 ? (
                          <div className="mt-1.5">
                            <div className="flex items-center gap-1 mb-1">
                              <i className="ri-camera-line text-[10px] text-gray-400"></i>
                              <span className="text-[9px] text-gray-400">{photoCount} photo{photoCount !== 1 ? 's' : ''}</span>
                            </div>
                            {photos.length > 0 && (
                              <div className="flex gap-1 overflow-x-auto">
                                {photos.slice(0, 4).map((ph: any, pi: number) => (
                                  <img key={pi}
                                    src={ph.thumbnailUrl || ph.thmb_url_tx || ph.fileUrl || ph.s3_url_tx}
                                    alt={ph.caption || ph.cptn_tx || ''}
                                    className="w-10 h-10 object-cover rounded border border-gray-200"
                                    onClick={e => { e.stopPropagation(); window.open(ph.fileUrl || ph.s3_url_tx, '_blank'); }} />
                                ))}
                                {photos.length > 4 && <div className="w-10 h-10 rounded border border-gray-200 bg-gray-100 flex items-center justify-center text-[9px] text-gray-500">+{photos.length - 4}</div>}
                              </div>
                            )}
                          </div>
                        ) : null;
                      })()}
                      <div className="flex gap-1 mt-1.5">
                        <button onClick={e => { e.stopPropagation(); deleteProgress(pid); }} className="text-[10px] px-2 py-0.5 rounded bg-red-50 text-red-600 hover:bg-red-100">Delete</button>
                      </div>
                    </div>
                  );
                })}
                {progressEntries.length === 0 && <div className="text-xs text-gray-400 text-center py-2">No progress entries yet</div>}

                {/* Add progress form */}
                {showProgressForm ? (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 space-y-2">
                    <div className="text-xs font-medium text-green-700 mb-1 flex items-center justify-between">
                      <span>New Progress Entry</span>
                      {selectedEmbForProgress && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-600 font-medium flex items-center gap-0.5">
                          <i className="ri-link text-[9px]"></i> {embEntries.find((e: any) => e.emb_id === selectedEmbForProgress)?.mbook_nu || `MB #${selectedEmbForProgress}`}
                        </span>
                      )}
                    </div>
                    <input type="date" value={progressForm.prgrs_dt} onChange={e => setProgressForm(f => ({ ...f, prgrs_dt: e.target.value }))}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1" />
                    <input placeholder="Section name" value={progressForm.sctn_nm} onChange={e => setProgressForm(f => ({ ...f, sctn_nm: e.target.value }))}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1" />
                    <input placeholder="Activity" value={progressForm.actvty_nm} onChange={e => setProgressForm(f => ({ ...f, actvty_nm: e.target.value }))}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1" />
                    <select value={progressForm.sts_cd} onChange={e => setProgressForm(f => ({ ...f, sts_cd: e.target.value }))}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1">
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="ON_HOLD">On Hold</option>
                    </select>
                    <input type="number" placeholder="Completed length (m)" value={progressForm.cmpltd_ln_nu || ''} onChange={e => setProgressForm(f => ({ ...f, cmpltd_ln_nu: parseFloat(e.target.value) || 0 }))}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1" />
                    <textarea placeholder="Remarks" value={progressForm.rmrks_tx} onChange={e => setProgressForm(f => ({ ...f, rmrks_tx: e.target.value }))}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1" rows={2} />

                    {/* Photo Upload */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-500 font-medium">Photos</span>
                        <button type="button" onClick={() => photoInputRef.current?.click()}
                          className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center gap-0.5">
                          <i className="ri-camera-line text-xs"></i> Add
                        </button>
                        <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoSelect} />
                      </div>
                      {photoPreviews.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {photoPreviews.map((src, i) => (
                            <div key={i} className="relative group w-14 h-14">
                              <img src={src} alt="" className="w-14 h-14 object-cover rounded border border-gray-200" />
                              <button onClick={() => removePhoto(i)}
                                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <i className="ri-close-line"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {photoPreviews.length === 0 && (
                        <div onClick={() => photoInputRef.current?.click()}
                          className="border border-dashed border-gray-300 rounded p-2 text-center cursor-pointer hover:border-green-400 hover:bg-green-50/50 transition-colors">
                          <i className="ri-image-add-line text-gray-300 text-lg"></i>
                          <div className="text-[9px] text-gray-400 mt-0.5">Click or drag photos</div>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-1 flex-wrap">
                      <button disabled={isDrawing} onClick={() => startDrawing('POLYGON', 'PROGRESS')}
                        className="text-[10px] px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50">
                        {isDrawing && drawingFor?.category === 'PROGRESS' ? 'Drawing...' : 'Draw on Map'}
                      </button>
                      <button disabled={saving} onClick={async () => {
                        if (!workId) return;
                        setSaving(true);
                        try {
                          const saved = await workGisApi.saveProgress(workId, {
                            ...progressForm,
                            emb_id: selectedEmbForProgress || null,
                            geom_jn: drawnGeoData || null,
                          });
                          const pid = saved?.gisProgressId || saved?.gis_prgrs_id || 'new';
                          if (progressPhotos.length > 0) {
                            for (const photo of progressPhotos) {
                              const fd = new FormData();
                              fd.append('photos', photo);
                              fd.append('phto_dt', progressForm.prgrs_dt);
                              fd.append('phto_type_cd', 'DURING');
                              fd.append('cptn_tx', photo.name);
                              try { await workGisApi.uploadProgressPhoto(workId, pid, fd); } catch (e) { console.error('Photo upload failed:', e); }
                            }
                          }
                          setShowProgressForm(false);
                          setProgressForm({ prgrs_dt: todayYmd(), sctn_nm: '', actvty_nm: '', sts_cd: 'IN_PROGRESS', cmpltd_ln_nu: 0, cmpltd_ar_nu: 0, rmrks_tx: '' });
                          clearPhotos();
                          await loadAll();
                        } catch (err) { console.error('Save failed:', err); }
                        finally { setSaving(false); }
                      }} className="text-[10px] px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={() => { setShowProgressForm(false); clearPhotos(); }} className="text-[10px] px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button disabled={isDrawing} onClick={() => { setSelectedEmbForProgress(null); setShowProgressForm(true); }}
                    className="w-full text-[10px] px-2 py-1.5 rounded border border-dashed border-green-300 text-green-600 hover:bg-green-50 disabled:opacity-50 mt-1">
                    + Add Independent Progress Entry
                  </button>
                )}
              </>
            )}

            {/* DEVIATION SECTION */}
            {activeSection === 'deviation' && !loading && (
              <>
                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Deviations</div>
                {deviationDetails.map((d: any) => {
                  const did = d.dvtnDtlId || d.dvtn_dtl_id;
                  const isOpen = (d.status || d.sts_cd) === 'OPEN';
                  const isExpanded = selectedDeviationId === did;
                  return (
                    <div key={did} className={`bg-white dark:bg-gray-800 rounded-lg p-2.5 border cursor-pointer transition-colors ${isExpanded ? 'border-red-400 ring-1 ring-red-400' : 'border-gray-200 dark:border-gray-700 hover:border-red-300'}`}
                      onClick={() => {
                        setSelectedDeviationId(isExpanded ? null : did);
                        if (d.geomData && !isExpanded) focusGeometry(d.geomData);
                      }}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-400">{d.identifiedDate || d.idntfd_dt}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${isOpen ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {isOpen ? 'OPEN' : 'CLOSED'}
                        </span>
                      </div>
                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {d.description || d.dscn_tx || d.geomNotes || 'Deviation'}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                          (d.deviationType || d.dvtn_type_cd) === 'QUALITY' ? 'bg-orange-100 text-orange-700' :
                          (d.deviationType || d.dvtn_type_cd) === 'ALIGNMENT' ? 'bg-purple-100 text-purple-700' :
                          (d.deviationType || d.dvtn_type_cd) === 'SPECIFICATION' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{d.deviationType || d.dvtn_type_cd}</span>
                        {(d.identifiedBy || d.idntfd_by_nm) && (
                          <span className="text-[9px] text-gray-400">by {d.identifiedBy || d.idntfd_by_nm}</span>
                        )}
                      </div>

                      {/* Photo thumbnails */}
                      {(() => {
                        const photos = d.identPhotos || [];
                        return photos.length > 0 ? (
                          <div className="flex gap-1 mt-1.5 overflow-x-auto">
                            {photos.slice(0, 3).map((ph: any, pi: number) => (
                              <img key={pi} src={ph.thumbnailUrl || ph.thmb_url_tx || ph.s3Url || ph.s3_url_tx}
                                alt={ph.caption || ''} className="w-8 h-8 object-cover rounded border border-gray-200"
                                onClick={e => { e.stopPropagation(); window.open(ph.s3Url || ph.s3_url_tx, '_blank'); }} />
                            ))}
                            {photos.length > 3 && <div className="w-8 h-8 rounded border border-gray-200 bg-gray-100 flex items-center justify-center text-[8px] text-gray-500">+{photos.length - 3}</div>}
                          </div>
                        ) : null;
                      })()}

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1.5" onClick={e => e.stopPropagation()}>
                          {(d.identifiedBy || d.idntfd_by_nm) && (
                            <div className="text-[10px]"><span className="text-gray-400">Identified by:</span> <span className="text-gray-600">{d.identifiedBy || d.idntfd_by_nm}</span></div>
                          )}
                          {(d.description || d.dscn_tx) && (
                            <div className="text-[10px] text-gray-500">{d.description || d.dscn_tx}</div>
                          )}
                          {/* Closure info */}
                          {!isOpen && (
                            <div className="bg-green-50 dark:bg-green-900/20 rounded p-2 mt-1 space-y-1">
                              <div className="text-[10px] font-medium text-green-700">Closure Details</div>
                              <div className="text-[10px]"><span className="text-gray-400">Closed:</span> <span className="text-gray-600">{d.closedDate || d.clsd_dt}</span></div>
                              {(d.verifiedBy || d.vrfd_by_nm) && <div className="text-[10px]"><span className="text-gray-400">Verified by:</span> <span className="text-gray-600">{d.verifiedBy || d.vrfd_by_nm}</span></div>}
                              {(d.closedRemarks || d.clsd_rmrks_tx) && <div className="text-[10px] text-gray-500">{d.closedRemarks || d.clsd_rmrks_tx}</div>}
                              {(d.paymentDeduction || d.pymnt_ddctn_in) && (
                                <div className="text-[10px]">
                                  <span className="text-gray-400">Deduction:</span>{' '}
                                  <span className="text-red-600 font-medium">₹{(d.deductionAmount || d.ddctn_at || 0).toLocaleString('en-IN')}</span>
                                  {(d.deductionRecovered || d.ddctn_rcvrd_in) && <span className="ml-1 text-green-600">(Recovered)</span>}
                                </div>
                              )}
                              {(d.deductionRemarks || d.ddctn_rmrks_tx) && <div className="text-[10px] text-gray-400">{d.deductionRemarks || d.ddctn_rmrks_tx}</div>}
                              {/* Closure photos */}
                              {(() => {
                                const cPhotos = d.closedPhotos || [];
                                return cPhotos.length > 0 ? (
                                  <div className="flex gap-1 mt-1 overflow-x-auto">
                                    {cPhotos.map((ph: any, pi: number) => (
                                      <img key={pi} src={ph.thumbnailUrl || ph.thmb_url_tx || ph.s3Url || ph.s3_url_tx}
                                        alt={ph.caption || ''} className="w-10 h-10 object-cover rounded border border-gray-200"
                                        onClick={() => window.open(ph.s3Url || ph.s3_url_tx, '_blank')} />
                                    ))}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          )}
                          <div className="flex gap-1 mt-1">
                            <button onClick={() => focusGeometry(d.geomData)} className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100">Focus</button>
                            {isOpen && (
                              <button onClick={() => {
                                setShowCloseModal(did);
                                setCloseForm({ clsd_dt: todayYmd(), clsd_rmrks_tx: '', vrfd_by_nm: '', pymnt_ddctn_in: false, ddctn_at: 0, ddctn_rcvrd_in: false, ddctn_rmrks_tx: '' });
                                clearClosePhotos();
                              }} className="text-[10px] px-2 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200">Close Deviation</button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Also show deviation geoms that don't have a detail record yet */}
                {deviationGeoms.filter(g => !deviationDetails.some(d => (d.gisRelId || d.wrk_gis_rel_id) === (g.gisRelId || g.wrk_gis_rel_id))).map((g: any, i: number) => {
                  const gid = g.gisRelId || g.gis_rel_id;
                  return (
                    <div key={`legacy-${gid || i}`} className="bg-white dark:bg-gray-800 rounded-lg p-2.5 border border-red-200 opacity-70">
                      <div className="text-xs font-medium text-red-700">{g.notes_tx || g.gisElementName || 'Deviation (no detail)'}</div>
                      <div className="flex gap-1 mt-1.5">
                        <button onClick={() => focusGeometry(g.geomData || g.geom_jn)} className="text-[10px] px-2 py-0.5 rounded bg-red-50 text-red-600 hover:bg-red-100">View</button>
                        <button onClick={() => deleteGeom(gid)} className="text-[10px] px-2 py-0.5 rounded bg-red-50 text-red-600 hover:bg-red-100">Delete</button>
                      </div>
                    </div>
                  );
                })}
                {deviationDetails.length === 0 && deviationGeoms.length === 0 && <div className="text-xs text-gray-400 text-center py-2">No deviations marked</div>}

                {showDeviationForm ? (
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 space-y-2">
                    <div className="text-xs font-medium text-red-700 mb-1">New Deviation</div>
                    <div>
                      <label className="text-[10px] text-gray-500">Date of Identification</label>
                      <input type="date" value={deviationForm.idntfd_dt} onChange={e => setDeviationForm(f => ({ ...f, idntfd_dt: e.target.value }))}
                        className="w-full text-xs border border-gray-300 rounded px-2 py-1" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500">Identified by</label>
                      <input placeholder="Name of person" value={deviationForm.idntfd_by_nm} onChange={e => setDeviationForm(f => ({ ...f, idntfd_by_nm: e.target.value }))}
                        className="w-full text-xs border border-gray-300 rounded px-2 py-1" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500">Deviation Type</label>
                      <select value={deviationForm.dvtn_type_cd} onChange={e => setDeviationForm(f => ({ ...f, dvtn_type_cd: e.target.value }))}
                        className="w-full text-xs border border-gray-300 rounded px-2 py-1">
                        <option value="QUALITY">Quality</option>
                        <option value="ALIGNMENT">Alignment</option>
                        <option value="SPECIFICATION">Specification</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500">Description</label>
                      <textarea placeholder="Describe the deviation..." value={deviationForm.dscn_tx} onChange={e => setDeviationForm(f => ({ ...f, dscn_tx: e.target.value }))}
                        className="w-full text-xs border border-gray-300 rounded px-2 py-1" rows={2} />
                    </div>
                    {/* Photo Upload */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-500 font-medium">Photos</span>
                        <button type="button" onClick={() => deviationPhotoInputRef.current?.click()}
                          className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center gap-0.5">
                          <i className="ri-camera-line text-xs"></i> Add
                        </button>
                        <input ref={deviationPhotoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleDeviationPhotoSelect} />
                      </div>
                      {deviationPhotoPreviews.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {deviationPhotoPreviews.map((src, i) => (
                            <div key={i} className="relative group w-12 h-12">
                              <img src={src} alt="" className="w-12 h-12 object-cover rounded border border-gray-200" />
                              <button onClick={() => removeDeviationPhoto(i)}
                                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <i className="ri-close-line"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      <button disabled={isDrawing} onClick={() => startDrawing('POLYGON', 'DEVIATION')}
                        className="text-[10px] px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50">
                        {isDrawing && drawingFor?.category === 'DEVIATION' ? 'Drawing...' : 'Draw on Map'}
                      </button>
                      <button disabled={saving} onClick={async () => {
                        if (!workId) return;
                        setSaving(true);
                        try {
                          // Save geometry first
                          const geoResult = await workGisApi.saveGeometry(workId, {
                            geom_jn: { markers: [], polylines: [], polygons: [] },
                            geom_ctgry_cd: 'DEVIATION',
                            geom_color_tx: '#ef4444',
                            notes_tx: deviationForm.dscn_tx,
                            gis_elmnt_type_cd: 'POLYGON',
                          });
                          const gisRelId = geoResult?.wrk_gis_rel_id;
                          if (gisRelId) {
                            const dvtnResult = await workGisApi.saveDeviation(workId, {
                              wrk_gis_rel_id: gisRelId,
                              idntfd_dt: deviationForm.idntfd_dt,
                              idntfd_by_nm: deviationForm.idntfd_by_nm,
                              dvtn_type_cd: deviationForm.dvtn_type_cd,
                              dscn_tx: deviationForm.dscn_tx,
                            });
                            const dvtnDtlId = dvtnResult?.dvtn_dtl_id;
                            if (dvtnDtlId && deviationPhotos.length > 0) {
                              for (const photo of deviationPhotos) {
                                const fd = new FormData();
                                fd.append('photos', photo);
                                fd.append('phto_dt', deviationForm.idntfd_dt);
                                fd.append('context', 'IDENT');
                                fd.append('cptn_tx', photo.name);
                                try { await workGisApi.uploadDeviationPhoto(workId, dvtnDtlId, fd); } catch (e) { console.error('Photo upload failed:', e); }
                              }
                            }
                          }
                          setShowDeviationForm(false);
                          setDeviationForm({ notes_tx: '', geom_lbl_tx: '', idntfd_dt: todayYmd(), idntfd_by_nm: '', dvtn_type_cd: 'QUALITY', dscn_tx: '' });
                          clearDeviationPhotos();
                          await loadAll();
                        } catch (err) { console.error('Save failed:', err); }
                        finally { setSaving(false); }
                      }} className="text-[10px] px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save (no map)'}
                      </button>
                      <button onClick={() => { setShowDeviationForm(false); clearDeviationPhotos(); }} className="text-[10px] px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button disabled={isDrawing} onClick={() => setShowDeviationForm(true)}
                    className="w-full text-[10px] px-2 py-1.5 rounded border border-dashed border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 mt-1">
                    + Mark Deviation
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 relative">
          <div ref={mapContainerRef} className="absolute inset-0 rounded-r-lg" />
          {/* Drawing toolbar overlay */}
          {isDrawing && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center gap-3">
              <span className="text-xs text-gray-600 dark:text-gray-300">
                Drawing {drawingFor?.type?.toLowerCase()} for {drawingFor?.category?.toLowerCase()}...
              </span>
              <button onClick={finishDrawing} disabled={saving}
                className="text-xs px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={cancelDrawing} className="text-xs px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300">Cancel</button>
            </div>
          )}

          {/* Layers control */}
          <div className="absolute top-2 right-2 z-[1000]">
            <button
              onClick={() => setShowLayersPanel(!showLayersPanel)}
              className={`w-8 h-8 rounded-lg shadow-md flex items-center justify-center transition-colors ${
                showLayersPanel ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
              title="Layers"
            >
              <i className="ri-stack-line text-sm"></i>
            </button>
            {showLayersPanel && (
              <div className="absolute top-10 right-0 w-52 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Map Layers</span>
                </div>
                <div className="p-2 space-y-1">
                  {/* UDA Border */}
                  <label className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overlayLayers.ulbBorder}
                      onChange={e => setOverlayLayers(p => ({ ...p, ulbBorder: e.target.checked }))}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <span className="w-3 h-3 rounded-sm border-2 border-teal-500 bg-teal-500/10 flex-shrink-0"></span>
                      <span className="text-xs text-gray-700 dark:text-gray-300 truncate">UDA Border</span>
                    </div>
                    {layersLoading.ulbBorder && <div className="w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>}
                  </label>
                  {/* Sachivalayams */}
                  <label className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overlayLayers.sachivalayams}
                      onChange={e => setOverlayLayers(p => ({ ...p, sachivalayams: e.target.checked }))}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <span className="w-3 h-3 rounded-sm border-2 border-amber-500 bg-amber-500/10 flex-shrink-0"></span>
                      <span className="text-xs text-gray-700 dark:text-gray-300 truncate">Sachivalayams</span>
                    </div>
                    {layersLoading.sachivalayams && <div className="w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>}
                  </label>
                  {/* Linked Geographies */}
                  <label className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showLinkedGeo}
                      onChange={e => setShowLinkedGeo(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <span className="w-3 h-3 rounded-sm border-2 border-emerald-500 bg-emerald-500/10 flex-shrink-0"></span>
                      <span className="text-xs text-gray-700 dark:text-gray-300 truncate">Linked GIS Assets</span>
                    </div>
                    {linkedMappings.length > 0 && (
                      <span className="text-[10px] text-emerald-600 font-medium">{linkedMappings.length}</span>
                    )}
                  </label>
                </div>
                {udaName && (
                  <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-600">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">UDA: {udaName}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Close Deviation Modal */}
      {showCloseModal !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => setShowCloseModal(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[520px] max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Close Deviation</h3>
              <button onClick={() => setShowCloseModal(null)} className="text-gray-400 hover:text-gray-600"><i className="ri-close-line text-xl"></i></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 font-medium">Closing Date</label>
                <input type="date" value={closeForm.clsd_dt} onChange={e => setCloseForm(f => ({ ...f, clsd_dt: e.target.value }))}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200" />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Closing Remarks</label>
                <textarea value={closeForm.clsd_rmrks_tx} onChange={e => setCloseForm(f => ({ ...f, clsd_rmrks_tx: e.target.value }))}
                  placeholder="Describe how deviation was resolved..."
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200" rows={3} />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Verified By</label>
                <input value={closeForm.vrfd_by_nm} onChange={e => setCloseForm(f => ({ ...f, vrfd_by_nm: e.target.value }))}
                  placeholder="Name of verifying officer"
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200" />
              </div>

              {/* Closing Photos */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-gray-500 font-medium">Closing Photos</label>
                  <button type="button" onClick={() => closePhotoInputRef.current?.click()}
                    className="text-xs px-3 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center gap-1">
                    <i className="ri-camera-line"></i> Add Photos
                  </button>
                  <input ref={closePhotoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleClosePhotoSelect} />
                </div>
                {closePhotoPreviews.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {closePhotoPreviews.map((src, i) => (
                      <div key={i} className="relative group w-16 h-16">
                        <img src={src} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                        <button onClick={() => removeClosePhoto(i)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment Deduction Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={closeForm.pymnt_ddctn_in}
                    onChange={e => setCloseForm(f => ({ ...f, pymnt_ddctn_in: e.target.checked }))}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-200 font-medium">Payment Deduction Applicable</span>
                </label>
                {closeForm.pymnt_ddctn_in && (
                  <div className="mt-2 space-y-2 pl-6">
                    <div>
                      <label className="text-xs text-gray-500">Deduction Amount (₹)</label>
                      <input type="number" value={closeForm.ddctn_at || ''} onChange={e => setCloseForm(f => ({ ...f, ddctn_at: parseFloat(e.target.value) || 0 }))}
                        placeholder="0.00"
                        className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200" />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={closeForm.ddctn_rcvrd_in}
                        onChange={e => setCloseForm(f => ({ ...f, ddctn_rcvrd_in: e.target.checked }))}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-200">Deduction Recovered</span>
                    </label>
                    <div>
                      <label className="text-xs text-gray-500">Deduction Remarks</label>
                      <textarea value={closeForm.ddctn_rmrks_tx} onChange={e => setCloseForm(f => ({ ...f, ddctn_rmrks_tx: e.target.value }))}
                        placeholder="Details about the deduction..."
                        className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200" rows={2} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setShowCloseModal(null)}
                className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">Cancel</button>
              <button disabled={saving}
                onClick={async () => {
                  if (!workId || showCloseModal === null) return;
                  setSaving(true);
                  try {
                    // Upload closing photos first
                    const closingPhotoIds: number[] = [];
                    if (closePhotos.length > 0) {
                      for (const photo of closePhotos) {
                        const fd = new FormData();
                        fd.append('photos', photo);
                        fd.append('phto_dt', closeForm.clsd_dt);
                        fd.append('context', 'CLOSE');
                        fd.append('cptn_tx', photo.name);
                        try {
                          const phResult = await workGisApi.uploadDeviationPhoto(workId, showCloseModal, fd);
                          if (phResult?.prgrs_phto_id) closingPhotoIds.push(phResult.prgrs_phto_id);
                        } catch (e) { console.error('Close photo upload failed:', e); }
                      }
                    }
                    // Close deviation
                    await workGisApi.closeDeviation(workId, showCloseModal, {
                      ...closeForm,
                      clsd_phto_ids_jn: closingPhotoIds
                    });
                    setShowCloseModal(null);
                    clearClosePhotos();
                    await loadAll();
                  } catch (err) { console.error('Close deviation failed:', err); }
                  finally { setSaving(false); }
                }}
                className="px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                {saving ? 'Closing...' : 'Confirm Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GIS Parcel Selector */}
      <GISParcelSelector
        isOpen={showGisParcelSelector}
        onClose={() => setShowGisParcelSelector(false)}
        onSave={async (selection: any) => {
          if (!workId) return;
          setSavingParcel(true);
          try {
            await axiosInstance.post(`ds-wms/parcel-mapping/work/${workId}`, {
              category: selection.category,
              svmId: selection.svmId,
              svmName: selection.svmName,
              roadMode: selection.roadMode || null,
              parcels: selection.items.map((p: any) => ({
                id: p.id, name: p.name || '', category: p.category || '',
              })),
            });
            setShowGisParcelSelector(false);
            // Reload linked geographies
            setTimeout(() => {
              linkedLayerRef.current.clearLayers();
              linkedParcelLayersRef.current.clear();
              // Re-trigger the load effect by toggling showLinkedGeo
              setShowLinkedGeo(false);
              setTimeout(() => setShowLinkedGeo(true), 100);
            }, 200);
          } catch (e) { console.error('Failed to save parcel mapping:', e); }
          finally { setSavingParcel(false); }
        }}
        udaId={udaId || 0}
        udaName={udaName}
        workId={workId}
        workCode={workCode}
        workName={workName}
        workStatus={workStatus}
        workType={workType}
        workCategory={workCategory}
        schemeName={schemeName}
        sanctionAmount={sanctionAmount}
        workSachivalayams={workSachivalayams}
        saving={savingParcel}
      />
    </div>
  );
}

type Invoice = { id: number; invoiceNo: string; amount: number; date: string; status: string; description: string; receiptNo: string | null; paidDate: string | null };

function InvoicesTab() {
  // No invoices API yet — start empty so the work shows its real (empty) state
  // instead of placeholder bills. Wire this to the invoices service once it exists.
  const invoices: Invoice[] = [];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-700';
      case 'Pending': return 'bg-yellow-100 text-yellow-700';
      case 'Overdue': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Calculate totals
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const paidAmount = invoices.filter(inv => inv.status === 'Paid').reduce((sum, inv) => sum + inv.amount, 0);
  const pendingAmount = invoices.filter(inv => inv.status === 'Pending').reduce((sum, inv) => sum + inv.amount, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Invoices & Payments</h4>
        <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-lg shadow-md shadow-violet-500/30 hover:from-violet-600 hover:to-purple-600 hover:shadow-lg hover:shadow-violet-500/40 active:scale-[0.98] transition-all duration-200 font-medium">
          <i className="ri-add-line"></i>
          Create Invoice
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 text-center">
          <p className="text-lg font-bold text-blue-700">{formatCurrency(totalAmount)}</p>
          <span className="text-xs text-blue-600">Total Billed</span>
        </div>
        <div className="bg-green-50 rounded-lg p-3 border border-green-200 text-center">
          <p className="text-lg font-bold text-green-700">{formatCurrency(paidAmount)}</p>
          <span className="text-xs text-green-600">Total Paid</span>
        </div>
        <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200 text-center">
          <p className="text-lg font-bold text-yellow-700">{formatCurrency(pendingAmount)}</p>
          <span className="text-xs text-yellow-600">Pending</span>
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg py-12 flex flex-col items-center justify-center text-center">
          <i className="ri-bill-line text-5xl text-gray-300 dark:text-gray-600 mb-3"></i>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No invoices yet</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 mb-4">Create the first invoice for this work to start tracking billing and payments.</p>
          <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-lg shadow-md shadow-violet-500/30 hover:from-violet-600 hover:to-purple-600 hover:shadow-lg hover:shadow-violet-500/40 active:scale-[0.98] transition-all duration-200 text-sm font-medium">
            <i className="ri-add-line"></i>
            Create Invoice
          </button>
        </div>
      ) : (
      <div className="overflow-x-auto border border-gray-300 dark:border-gray-600 rounded-lg">
        <table data-wms-table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700 border-b-2 border-gray-400 dark:border-gray-600">
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600">S.No</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600">Invoice No.</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600">Description</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600">Amount</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600">Invoice Date</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600">Status</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600">Receipt No.</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600">Paid Date</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice, index) => (
              <tr key={invoice.id} className={`${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'} border-b border-gray-200 dark:border-gray-700`}>
                <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600">{index + 1}</td>
                <td className="px-3 py-2 text-sm font-medium text-blue-600 border-r border-gray-200 dark:border-gray-600">{invoice.invoiceNo}</td>
                <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600">{invoice.description}</td>
                <td className="px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 text-right border-r border-gray-200 dark:border-gray-600">{formatCurrency(invoice.amount)}</td>
                <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-600">{new Date(invoice.date).toLocaleDateString('en-IN')}</td>
                <td className="px-3 py-2 text-center border-r border-gray-200 dark:border-gray-600">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                    {invoice.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-sm border-r border-gray-200 dark:border-gray-600">
                  {invoice.receiptNo ? (
                    <button className="text-blue-600 hover:text-blue-800 hover:underline font-medium flex items-center gap-1">
                      <i className="ri-file-pdf-line"></i>
                      {invoice.receiptNo}
                    </button>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-600">
                  {invoice.paidDate ? new Date(invoice.paidDate).toLocaleDateString('en-IN') : '-'}
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex justify-center gap-2">
                    <button className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="View">
                      <i className="ri-eye-line"></i>
                    </button>
                    <button className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Download">
                      <i className="ri-download-line"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {/* Total Row */}
            <tr className="bg-gray-100 dark:bg-gray-700 font-semibold border-t-2 border-gray-300 dark:border-gray-600">
              <td className="px-3 py-2 text-sm border-r border-gray-200 dark:border-gray-600" colSpan={3}>Total</td>
              <td className="px-3 py-2 text-sm text-right border-r border-gray-200 dark:border-gray-600">{formatCurrency(totalAmount)}</td>
              <td className="px-3 py-2 text-sm border-r border-gray-200 dark:border-gray-600" colSpan={5}></td>
            </tr>
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

// ─── Award Tab ───
function AwardTab({ apiData, onViewTenderDetails }: { apiData: any; onViewTenderDetails?: () => void }) {
  const tender = apiData?.tender;
  const loa = apiData?.loa;
  const contract = apiData?.contract;
  const contractor = apiData?.contractor;

  const fmtCur = (n: number) => n ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n) : '-';
  const fmtDt = (d: string | null | undefined) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const fmtDtTm = (d: string | null | undefined) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  // Inline edit/upload state for award documents
  const [editingDocIdx, setEditingDocIdx] = useState<number | null>(null);
  const [docForm, setDocForm] = useState({ refNo: '', date: '', amount: '', remarks: '', file: null as File | null });

  const openDocEdit = (doc: any) => {
    setEditingDocIdx(doc.sno);
    setDocForm({
      refNo: doc.refNo !== '-' ? doc.refNo : '',
      date: doc.date ? toYmd(doc.date) : '',
      amount: doc.amount ? String(doc.amount) : '',
      remarks: '',
      file: null
    });
  };

  const closeDocEdit = () => { setEditingDocIdx(null); };

  const handleDocSave = () => {
    // TODO: API call to save document details + file upload
    console.log('Save doc:', editingDocIdx, docForm);
    closeDocEdit();
  };

  const Row = ({ label, value, highlight }: { label: string; value: string | React.ReactNode; highlight?: boolean }) => (
    <div className="flex justify-between py-1.5 px-2 even:bg-white dark:even:bg-gray-700/50 even:rounded">
      <span className="text-gray-500 dark:text-gray-400 text-sm">{label}</span>
      <span className={`font-semibold text-sm ${highlight ? 'text-blue-600' : 'text-gray-800 dark:text-gray-100'}`}>{value || '-'}</span>
    </div>
  );

  if (!tender && !loa && !contract) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-trophy-line text-3xl text-gray-300"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-600 mb-1">No Award Details</h3>
          <p className="text-sm text-gray-400">This work has not been tendered or awarded yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Tender Info */}
        {tender && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
              <i className="ri-auction-line text-blue-600"></i> Tender Details
            </h4>
            <div className="space-y-0.5">
              {Row({ label: "Tender Code", value: tender.tenderCode, highlight: true })}
              {Row({ label: "Tender Type", value: tender.tenderType })}
              {Row({ label: "Status", value: <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">{tender.tenderStatus}</span> })}
              {Row({ label: "Tender Published", value: fmtDtTm(tender.bidStartDate) })}
              {Row({ label: "Tender Close", value: fmtDtTm(tender.bidEndDate) })}
              {Row({ label: "Bid Opening", value: fmtDtTm(tender.bidOpenDate) })}
              {Row({ label: "Financial Opening", value: fmtDtTm(tender.financialOpenDate) })}
              {Row({ label: "e-Proc Reference", value: tender.eproc })}
              {Row({ label: "Estimated Amount", value: fmtCur(tender.estimatedAmount) })}
              {Row({ label: "EMD Amount", value: fmtCur(tender.emdAmount) })}
            </div>
          </div>
        )}

        {/* Awarded Contractor - right side of Tender Details */}
        {contractor ? (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-800">
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
              <i className="ri-user-star-line text-emerald-600"></i> Awarded Contractor
            </h4>
            <div className="space-y-0.5">
              {Row({ label: "Organization", value: contractor.name ? <ContractorLink id={contractor.contractorId} name={contractor.name} code={contractor.contractorCode} /> : contractor.name })}
              {Row({ label: "Code", value: contractor.contractorCode, highlight: true })}
              {Row({ label: "Contact Person", value: contractor.contactPerson })}
              {Row({ label: "Phone", value: contractor.phone })}
              {Row({ label: "Email", value: contractor.email })}
              {Row({ label: "Registration No.", value: contractor.registrationNo })}
              {Row({ label: "GST No.", value: contractor.gstNo })}
              {contractor.msme && Row({ label: "MSME", value: <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">MSME Registered</span> })}
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <i className="ri-user-star-line text-2xl mb-1 block opacity-40"></i>
              <p className="text-xs">No contractor awarded yet</p>
            </div>
          </div>
        )}

        {/* Bank Guarantees - full width */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800 lg:col-span-2">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
            <i className="ri-bank-line text-blue-600"></i> Bank Guarantees ({(apiData?.bankGuarantees || []).length})
          </h4>
          {apiData?.bankGuarantees?.length > 0 ? (
            <div className="overflow-x-auto">
              <table data-wms-table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-blue-200 dark:border-blue-700">
                    <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">S.No</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">BG Number</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Bank</th>
                    <th className="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Issue Date</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Valid Till</th>
                    <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {apiData.bankGuarantees.map((bg: any, idx: number) => (
                    <tr key={bg.bg_id} className={`${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : ''} border-b border-blue-100 dark:border-blue-800`}>
                      <td className="py-2 px-2 text-gray-500">{idx + 1}</td>
                      <td className="py-2 px-2"><span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">{bg.bg_type_nm}</span></td>
                      <td className="py-2 px-2 font-mono text-xs text-blue-700">{bg.bg_nu || bg.bg_cd}</td>
                      <td className="py-2 px-2"><span className="text-xs">{bg.bank_nm}</span><br/><span className="text-[10px] text-gray-400">{bg.brnch_nm}</span></td>
                      <td className="py-2 px-2 text-right font-mono font-semibold">{fmtCur(parseFloat(bg.bg_at))}</td>
                      <td className="py-2 px-2 text-xs">{bg.isue_dt || '-'}</td>
                      <td className="py-2 px-2 text-xs">{bg.vldty_dt || '-'}</td>
                      <td className="py-2 px-2 text-center">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                          bg.sts_cd === 'ACTIVE' ? 'bg-green-50 text-green-700' : bg.sts_cd === 'RELEASED' ? 'bg-sky-50 text-sky-700' : bg.sts_cd === 'EXPIRED' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'
                        }`}>{bg.sts_cd}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-400">
              <i className="ri-bank-line text-2xl mb-1 block opacity-40"></i>
              <p className="text-xs">No bank guarantees submitted for this work</p>
            </div>
          )}
        </div>

        {/* Award Documents Table - full width */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 lg:col-span-2">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
            <i className="ri-file-shield-2-line text-purple-600"></i> Award & Agreement Documents
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase w-10">S.No</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Document</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase w-36">Reference No.</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase w-32">Date</th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase w-36">Amount</th>
                  <th className="text-center py-2 px-3 text-xs font-medium text-gray-500 uppercase w-20">Status</th>
                  <th className="text-center py-2 px-3 text-xs font-medium text-gray-500 uppercase w-24">File</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { sno: 1, name: 'Letter of Award (LoA)', icon: 'ri-mail-send-line', refNo: loa?.loaCode || '-', date: loa?.loaDate, amount: loa?.contractAmount, status: loa?.status || (loa ? 'ISSUED' : null), hasData: !!loa },
                  { sno: 2, name: 'Work Order', icon: 'ri-file-paper-2-line', refNo: contract?.contractCode || '-', date: contract?.workOrderDate, amount: null, status: contract?.workOrderDate ? 'ISSUED' : null, hasData: !!contract?.workOrderDate },
                  { sno: 3, name: 'Negotiation Minutes', icon: 'ri-discuss-line', refNo: '-', date: null, amount: null, status: null, hasData: false, amountLabel: 'Negotiated Value' },
                  { sno: 4, name: 'Agreement', icon: 'ri-file-shield-2-line', refNo: contract?.contractCode || '-', date: contract?.agreementDate, amount: contract?.originalContractAmount, status: contract?.agreementDate ? 'SIGNED' : null, hasData: !!contract?.agreementDate, amountLabel: 'Contract Value' },
                  { sno: 5, name: 'Supplementary Agreement', icon: 'ri-file-add-line', refNo: '-', date: null, amount: contract?.currentContractAmount !== contract?.originalContractAmount ? contract?.currentContractAmount : null, status: null, hasData: false, amountLabel: 'Revised Value' },
                ].map((doc, idx) => (
                  <React.Fragment key={doc.sno}>
                  <tr className={`${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : ''} border-b border-gray-200 dark:border-gray-700`}>
                    <td className="py-2.5 px-3 text-gray-500">{doc.sno}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <i className={`${doc.icon} ${doc.hasData ? 'text-purple-500' : 'text-gray-300'}`}></i>
                        <span className={`font-medium ${doc.hasData ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}>{doc.name}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 font-mono text-xs text-blue-600">{doc.refNo}</td>
                    <td className="py-2.5 px-3 text-xs text-gray-700 dark:text-gray-300">{doc.date ? fmtDt(doc.date) : <span className="text-gray-300">-</span>}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-xs font-semibold text-gray-800">{doc.amount ? fmtCur(doc.amount) : <span className="text-gray-300">-</span>}</td>
                    <td className="py-2.5 px-3 text-center">
                      {doc.status ? (
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                          doc.status === 'ACCEPTED' || doc.status === 'SIGNED' ? 'bg-green-50 text-green-700' :
                          doc.status === 'ISSUED' ? 'bg-blue-50 text-blue-700' :
                          'bg-amber-50 text-amber-700'
                        }`}>{doc.status}</span>
                      ) : <span className="text-[11px] text-gray-300">-</span>}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <div className="flex justify-center gap-1">
                        <button onClick={() => editingDocIdx === doc.sno ? closeDocEdit() : openDocEdit(doc)}
                          className={`p-1 rounded text-sm ${editingDocIdx === doc.sno ? 'text-red-500 hover:bg-red-50' : 'text-orange-500 hover:bg-orange-50'}`}
                          title={editingDocIdx === doc.sno ? 'Cancel' : 'Update'}>
                          <i className={editingDocIdx === doc.sno ? 'ri-close-line' : 'ri-edit-line'}></i>
                        </button>
                        {doc.hasData && <button className="p-1 text-blue-500 hover:bg-blue-50 rounded" title="Download"><i className="ri-download-line text-sm"></i></button>}
                      </div>
                    </td>
                  </tr>
                  {/* Inline edit/upload form */}
                  {editingDocIdx === doc.sno && (
                    <tr className="bg-orange-50 dark:bg-orange-900/10">
                      <td colSpan={7} className="px-3 py-3">
                        <div className="grid grid-cols-5 gap-3 items-end">
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 block mb-1">Reference No.</label>
                            <input type="text" value={docForm.refNo} onChange={(e) => setDocForm({ ...docForm, refNo: e.target.value })}
                              className="w-full border rounded px-2 py-1.5 text-sm" placeholder="e.g., LOA/2025/001" />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 block mb-1">Date</label>
                            <input type="date" value={docForm.date} onChange={(e) => setDocForm({ ...docForm, date: e.target.value })}
                              className="w-full border rounded px-2 py-1.5 text-sm" />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 block mb-1">{(doc as any).amountLabel || 'Amount'} (₹)</label>
                            <input type="number" value={docForm.amount} onChange={(e) => setDocForm({ ...docForm, amount: e.target.value })}
                              className="w-full border rounded px-2 py-1.5 text-sm" placeholder="0" />
                            {doc.sno === 3 && <p className="text-[9px] text-amber-600 mt-0.5">If negotiated, this becomes the contract value</p>}
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 block mb-1">Upload Document</label>
                            <input type="file" onChange={(e) => setDocForm({ ...docForm, file: e.target.files?.[0] || null })}
                              className="w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-orange-100 file:text-orange-700 hover:file:bg-orange-200"
                              accept=".pdf,.doc,.docx,.jpg,.png" />
                          </div>
                          <div className="flex gap-2">
                            <button onClick={handleDocSave}
                              className="px-3 py-1.5 bg-orange-600 text-white rounded text-xs font-medium hover:bg-orange-700 flex items-center gap-1">
                              <i className="ri-save-line"></i> Save
                            </button>
                            <button onClick={closeDocEdit}
                              className="px-3 py-1.5 border rounded text-xs hover:bg-gray-100">Cancel</button>
                          </div>
                        </div>
                        <div className="mt-2">
                          <label className="text-[10px] font-medium text-gray-500 block mb-1">Remarks</label>
                          <input type="text" value={docForm.remarks} onChange={(e) => setDocForm({ ...docForm, remarks: e.target.value })}
                            className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Any notes or observations..." />
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View Full Tender Details Button */}
      {onViewTenderDetails && tender && (
        <div className="pt-2">
          <button onClick={onViewTenderDetails}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl hover:from-purple-700 hover:to-violet-700 transition-all shadow-md hover:shadow-lg font-medium text-sm">
            <i className="ri-auction-line text-lg"></i> View Full Tender Details <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Communication Tab ───
function CommunicationTab() {
  const [activeSection, setActiveSection] = useState('notices');

  const sections = [
    { id: 'notices', label: 'Notices', icon: 'ri-notification-3-line', count: 3 },
    { id: 'letters', label: 'Letters', icon: 'ri-mail-line', count: 5 },
    { id: 'contractor_letters', label: 'From Contractor', icon: 'ri-mail-download-line', count: 2 },
  ];

  const notices = [
    { id: 1, subject: 'Show Cause Notice - Delay in Progress', date: '2024-11-15', to: 'M/s. ABC Infrastructure', severity: 'High', status: 'Sent' },
    { id: 2, subject: 'Notice for Quality Improvement - Zone 3', date: '2024-10-20', to: 'M/s. ABC Infrastructure', severity: 'Medium', status: 'Acknowledged' },
    { id: 3, subject: 'Safety Compliance Notice', date: '2024-09-05', to: 'M/s. ABC Infrastructure', severity: 'Low', status: 'Resolved' },
  ];

  const letters = [
    { id: 1, subject: 'Instruction for Additional Works - Drainage Diversion', date: '2024-11-10', from: 'AEE, Division II', to: 'M/s. ABC Infrastructure', type: 'Instruction' },
    { id: 2, subject: 'Rate Revision Approval for Cement', date: '2024-10-25', from: 'SE, Circle III', to: 'AEE, Division II', type: 'Approval' },
    { id: 3, subject: 'Extension of Time Request - Forwarding', date: '2024-10-15', from: 'AEE, Division II', to: 'SE, Circle III', type: 'Forwarding' },
    { id: 4, subject: 'Mobilization Advance Release', date: '2024-03-01', from: 'EE, Division II', to: 'Accounts Section', type: 'Financial' },
    { id: 5, subject: 'Commencement of Work - Intimation', date: '2024-02-15', from: 'AEE, Division II', to: 'M/s. ABC Infrastructure', type: 'Intimation' },
  ];

  const contractorLetters = [
    { id: 1, subject: 'Request for Extension of Time - Heavy Rainfall', date: '2024-10-10', from: 'M/s. ABC Infrastructure', type: 'EOT Request' },
    { id: 2, subject: 'Rate Revision Request - Cement & Steel', date: '2024-09-28', from: 'M/s. ABC Infrastructure', type: 'Rate Revision' },
  ];

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'High': return 'bg-red-100 text-red-700';
      case 'Medium': return 'bg-yellow-100 text-yellow-700';
      case 'Low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getLetterTypeColor = (type: string) => {
    switch (type) {
      case 'Instruction': return 'bg-blue-100 text-blue-700';
      case 'Approval': return 'bg-green-100 text-green-700';
      case 'Forwarding': return 'bg-purple-100 text-purple-700';
      case 'Financial': return 'bg-amber-100 text-amber-700';
      case 'Intimation': return 'bg-teal-100 text-teal-700';
      case 'EOT Request': return 'bg-orange-100 text-orange-700';
      case 'Rate Revision': return 'bg-pink-100 text-pink-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-4">
      {/* Section Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-3">
        {sections.map(sec => (
          <button
            key={sec.id}
            onClick={() => setActiveSection(sec.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              activeSection === sec.id
                ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-md shadow-violet-500/30'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <i className={`${sec.icon} text-sm`}></i>
            {sec.label}
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              activeSection === sec.id ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
            }`}>{sec.count}</span>
          </button>
        ))}
        <div className="flex-1"></div>
        <button className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-lg shadow-md shadow-violet-500/30 hover:from-violet-600 hover:to-purple-600 hover:shadow-lg hover:shadow-violet-500/40 active:scale-[0.98] transition-all duration-200 text-xs font-medium">
          <i className="ri-add-line"></i> Add
        </button>
      </div>

      {/* Notices */}
      {activeSection === 'notices' && (
        <div className="space-y-2">
          {notices.map((notice) => (
            <div key={notice.id} className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 transition-colors">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-notification-3-line text-red-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{notice.subject}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">To: {notice.to} | {formatDate(notice.date)}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getSeverityColor(notice.severity)}`}>
                {notice.severity}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                notice.status === 'Resolved' ? 'bg-green-100 text-green-700' : notice.status === 'Acknowledged' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {notice.status}
              </span>
              <div className="flex gap-1">
                <button className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="View"><i className="ri-eye-line text-sm"></i></button>
                <button className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Download"><i className="ri-download-line text-sm"></i></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Letters */}
      {activeSection === 'letters' && (
        <div className="space-y-2">
          {letters.map((letter) => (
            <div key={letter.id} className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 transition-colors">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-blue-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{letter.subject}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">From: {letter.from} | To: {letter.to} | {formatDate(letter.date)}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getLetterTypeColor(letter.type)}`}>
                {letter.type}
              </span>
              <div className="flex gap-1">
                <button className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="View"><i className="ri-eye-line text-sm"></i></button>
                <button className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Download"><i className="ri-download-line text-sm"></i></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contractor Letters */}
      {activeSection === 'contractor_letters' && (
        <div className="space-y-2">
          {contractorLetters.map((letter) => (
            <div key={letter.id} className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 transition-colors">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-download-line text-amber-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{letter.subject}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">From: {letter.from} | {formatDate(letter.date)}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getLetterTypeColor(letter.type)}`}>
                {letter.type}
              </span>
              <div className="flex gap-1">
                <button className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="View"><i className="ri-eye-line text-sm"></i></button>
                <button className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Download"><i className="ri-download-line text-sm"></i></button>
              </div>
            </div>
          ))}
          {contractorLetters.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <i className="ri-mail-line text-4xl mb-2 block"></i>
              <p>No letters from contractor</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Design + BOQ Tab (loads real data from APIs) ───
const FILE_TYPE_BUTTONS = [
  { type: 'BIM', label: 'BIM Files', icon: 'ri-building-3-line', color: 'blue', accept: '.ifc,.rvt,.nwd,.nwc,.nwf,.dgn' },
  { type: 'CAD', label: 'CAD Files', icon: 'ri-pencil-ruler-2-line', color: 'purple', accept: '.dwg,.dxf,.dwf,.dws' },
  { type: 'GIS', label: 'GIS Files', icon: 'ri-map-2-line', color: 'teal', accept: '.kml,.kmz,.shp,.shx,.dbf,.prj,.geojson,.gpx' },
  { type: 'PDF', label: 'PDF Documents', icon: 'ri-file-pdf-line', color: 'red', accept: '.pdf' },
  { type: 'IMAGE', label: 'Images', icon: 'ri-image-line', color: 'green', accept: '.jpg,.jpeg,.png,.gif,.tiff,.bmp' },
  { type: 'OTHER', label: 'Other', icon: 'ri-file-line', color: 'gray', accept: '.doc,.docx,.xls,.xlsx,.csv,.zip' },
] as const;

function DesignBOQTab({ workId }: { workId?: number }) {
  const [activeSection, setActiveSection] = useState('design');
  const [loading, setLoading] = useState(true);
  const [designData, setDesignData] = useState<any>(null);
  const [drawings, setDrawings] = useState<any[]>([]);
  const [boqItems, setBoqItems] = useState<any[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!workId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      try {
        // Find estimate for this work to get BOQ items
        const [dsgn, drw, estList] = await Promise.allSettled([
          designsApi.getById(workId),
          designsApi.getDrawings(workId),
          estimatesApi.getAll({ workId: String(workId) } as any, 1, 1),
        ]);
        if (cancelled) return;
        setDesignData(dsgn.status === 'fulfilled' ? dsgn.value : null);
        setDrawings(drw.status === 'fulfilled' ? (drw.value || []) : []);

        // If estimate found, load its BOQ items
        const estData = estList.status === 'fulfilled' ? estList.value : null;
        const firstEstimate = estData?.data?.[0];
        if (firstEstimate?.estmt_id) {
          try {
            const items = await estimatesApi.getItems(firstEstimate.estmt_id);
            if (!cancelled) setBoqItems(items || []);
          } catch { if (!cancelled) setBoqItems([]); }
        }
      } catch (e) { console.error('Design+BOQ load error:', e); }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [workId]);

  // Reload drawings after upload
  const reloadDrawings = useCallback(async () => {
    if (!workId) return;
    try {
      const drw = await designsApi.getDrawings(workId);
      setDrawings(drw || []);
    } catch { /* ignore */ }
  }, [workId]);

  // Upload files
  const handleUploadFiles = useCallback(async (files: FileList, fileType: string) => {
    if (!workId || files.length === 0) return;
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('files', files[i]);
        formData.append('drwng_nm', files[i].name);
        formData.append('drwng_type_cd', fileType);
        await designsApi.uploadDrawing(workId, formData);
      }
      await reloadDrawings();
    } catch (e) { console.error('Upload error:', e); }
    finally { setUploading(false); }
  }, [workId, reloadDrawings]);

  // Delete drawing
  const handleDeleteDrawing = useCallback(async (drwngId: number) => {
    if (!workId) return;
    try {
      await designsApi.deleteDrawing(workId, drwngId);
      await reloadDrawings();
    } catch (e) { console.error('Delete error:', e); }
  }, [workId, reloadDrawings]);

  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  const totalBOQ = boqItems.reduce((sum, item) => sum + (parseFloat(item.amount_at) || 0), 0);

  if (loading) {
    return <ListSkeleton />;
  }

  return (
    <div className="space-y-4">
      {/* Section Toggle */}
      <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
        <button onClick={() => setActiveSection('design')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeSection === 'design' ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-md shadow-violet-500/30' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <i className="ri-draft-line text-sm"></i> Drawings ({drawings.length})
        </button>
        <button onClick={() => setActiveSection('boq')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeSection === 'boq' ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-md shadow-violet-500/30' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <i className="ri-table-line text-sm"></i> BOQ ({boqItems.length})
        </button>
      </div>

      {/* Design Section */}
      {activeSection === 'design' && (
        <div className="space-y-4">
          {/* Upload buttons by type */}
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-2 block">Upload Design Documents</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {FILE_TYPE_BUTTONS.map(ft => (
                <button key={ft.type} type="button"
                  disabled={uploading}
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file'; input.multiple = true; input.accept = ft.accept;
                    input.onchange = (e) => {
                      const files = (e.target as HTMLInputElement).files;
                      if (files && files.length > 0) handleUploadFiles(files, ft.type);
                    };
                    input.click();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-colors disabled:opacity-50
                    ${ft.color === 'blue' ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' :
                      ft.color === 'purple' ? 'bg-purple-50 text-purple-700 hover:bg-purple-100' :
                      ft.color === 'teal' ? 'bg-teal-50 text-teal-700 hover:bg-teal-100' :
                      ft.color === 'red' ? 'bg-red-50 text-red-700 hover:bg-red-100' :
                      ft.color === 'green' ? 'bg-green-50 text-green-700 hover:bg-green-100' :
                      'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}>
                  <i className={ft.icon}></i> {ft.label}
                </button>
              ))}
            </div>

            {/* Drag & Drop Zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={(e) => {
                e.preventDefault(); setIsDragging(false);
                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                  handleUploadFiles(e.dataTransfer.files, 'OTHER');
                }
              }}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all mb-3 ${
                isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
              }`}
            >
              <input ref={fileInputRef} type="file" multiple
                accept=".ifc,.rvt,.nwd,.nwc,.nwf,.dgn,.dwg,.dxf,.dwf,.dws,.kml,.kmz,.shp,.shx,.dbf,.prj,.geojson,.gpx,.pdf,.jpg,.jpeg,.png,.gif,.tiff,.bmp,.doc,.docx,.xls,.xlsx,.csv,.zip"
                onChange={(e) => { if (e.target.files && e.target.files.length > 0) { handleUploadFiles(e.target.files, 'OTHER'); e.target.value = ''; } }}
                className="hidden" />
              {uploading ? (
                <><ListSkeleton /><p className="text-sm text-indigo-600">Uploading...</p></>
              ) : (
                <>
                  <i className={`ri-upload-cloud-2-line text-3xl mb-2 block ${isDragging ? 'text-indigo-500' : 'text-gray-400'}`}></i>
                  <p className="text-gray-600 text-sm mb-1"><span className="font-medium text-indigo-600">Click to upload</span> or drag and drop</p>
                  <p className="text-xs text-gray-400">BIM (.ifc, .rvt) | CAD (.dwg, .dxf) | GIS (.kml, .shp) | PDF | Images | Documents</p>
                </>
              )}
            </div>
          </div>

          {/* Drawings list */}
          {drawings.length === 0 ? (
            <div className="bg-gray-50 rounded-lg border p-8 text-center text-gray-400">
              <i className="ri-image-line text-3xl mb-2 block opacity-40"></i>
              <p className="text-sm">No drawings uploaded for this work</p>
            </div>
          ) : (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <i className="ri-image-line text-orange-600"></i> Design Drawings ({drawings.length})
              </h4>
              {drawings.map((d: any) => {
                const typeColors: Record<string, { icon: string; bg: string }> = {
                  BIM: { icon: 'ri-building-3-line text-blue-600', bg: 'bg-blue-100 text-blue-700' },
                  CAD: { icon: 'ri-pencil-ruler-2-line text-purple-600', bg: 'bg-purple-100 text-purple-700' },
                  GIS: { icon: 'ri-map-2-line text-teal-600', bg: 'bg-teal-100 text-teal-700' },
                  PDF: { icon: 'ri-file-pdf-line text-red-600', bg: 'bg-red-100 text-red-700' },
                  IMAGE: { icon: 'ri-image-line text-green-600', bg: 'bg-green-100 text-green-700' },
                };
                const tc = typeColors[d.drwng_type_cd] || { icon: 'ri-file-line text-gray-600', bg: 'bg-gray-100 text-gray-700' };
                const sizeStr = d.file_sz ? (d.file_sz > 1048576 ? `${(d.file_sz / 1048576).toFixed(1)} MB` : `${(d.file_sz / 1024).toFixed(0)} KB`) : '';
                return (
                  <div key={d.drwng_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border hover:bg-white transition-colors">
                    <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={tc.icon}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{d.drwng_nm || d.file_nm}</p>
                      <p className="text-xs text-gray-500">{d.drwng_cd} {d.drwng_type_cd ? `| ` : ''}<span className={`px-1 py-0.5 rounded text-[10px] ${tc.bg}`}>{d.drwng_type_cd}</span> {sizeStr ? `| ${sizeStr}` : ''}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${d.sts_cd === 'APPROVED' || d.drwng_sts_cd === 'APPROVED' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {d.sts_cd || d.drwng_sts_cd || 'Draft'}
                    </span>
                    <button onClick={() => handleDeleteDrawing(d.drwng_id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* BOQ Section */}
      {activeSection === 'boq' && (
        <div>
          {boqItems.length === 0 ? (
            <div className="bg-gray-50 rounded-lg border p-8 text-center text-gray-400">
              <i className="ri-file-list-3-line text-3xl mb-2 block opacity-40"></i>
              <p className="text-sm">No BOQ items found for this work</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <DataGrid
                dataSource={boqItems.map((item: any, i: number) => ({ ...item, sno: i + 1, amount_at: item.amount_at || ((item.qty_nu || 0) * (item.rate_at || 0)) }))}
                columns={[
                  { field: 'sno', columnName: 'S.No', columnType: 'number' as const, width: '60px', alignment: 'center' as const },
                  { field: 'itm_cd', columnName: 'Item Code', columnType: 'text' as const, width: '120px',
                    cellRenderer: (row: any) => <span className="font-mono text-xs text-gray-600">{row.itm_cd}</span>
                  },
                  { field: 'itm_nm', columnName: 'Description', columnType: 'text' as const, width: '280px' },
                  { field: 'itm_type_cd', columnName: 'Type', columnType: 'text' as const, width: '80px', alignment: 'center' as const,
                    cellRenderer: (row: any) => (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${row.itm_type_cd === 'SOR' ? 'bg-blue-100 text-blue-700' : row.itm_type_cd === 'RATE_CONTRACT' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                        {row.itm_type_cd === 'RATE_CONTRACT' ? 'RC' : row.itm_type_cd || '—'}
                      </span>
                    )
                  },
                  { field: 'uom_nm', columnName: 'UOM', columnType: 'text' as const, width: '80px', alignment: 'center' as const,
                    cellRenderer: (row: any) => <span>{row.uom_nm || row.uom_cd || '—'}</span>
                  },
                  { field: 'qty_nu', columnName: 'Qty', columnType: 'number' as const, width: '100px', alignment: 'right' as const,
                    cellRenderer: (row: any) => <span className="font-medium">{(row.qty_nu || 0).toLocaleString('en-IN')}</span>
                  },
                  { field: 'rate_at', columnName: 'Rate', columnType: 'number' as const, width: '120px', alignment: 'right' as const,
                    cellRenderer: (row: any) => <span className="font-mono text-sm">{formatCurrency(row.rate_at || 0)}</span>
                  },
                  { field: 'amount_at', columnName: 'Amount', columnType: 'number' as const, width: '140px', alignment: 'right' as const,
                    cellRenderer: (row: any) => <span className="font-mono text-sm font-semibold">{formatCurrency(row.amount_at || 0)}</span>
                  },
                ]}
                gridConfig={{
                  title: `BOQ Items (${boqItems.length})`,
                  pagination: true,
                  minPageSizes: [10, 25, 50],
                  enableSearchPanel: true,
                  enableColumnResizing: true,
                }}
              />
              <div className="border-t bg-gray-50 px-6 py-3 flex items-center justify-end gap-8">
                <div className="text-sm text-gray-500">{boqItems.length} item{boqItems.length !== 1 ? 's' : ''}</div>
                <div className="text-sm font-bold text-green-700">Grand Total: {formatCurrency(totalBOQ)}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WorkflowsTab() {
  const workflows = [
    { id: 1, name: 'Administrative Sanction', status: 'Completed', date: '2024-01-15', approvedBy: 'Director, UDA' },
    { id: 2, name: 'Technical Sanction', status: 'Completed', date: '2024-01-25', approvedBy: 'Chief Engineer' },
    { id: 3, name: 'Tender Process', status: 'Completed', date: '2024-02-01', approvedBy: 'Tender Committee' },
    { id: 4, name: 'Agreement Execution', status: 'Completed', date: '2024-02-01', approvedBy: 'Executive Director' },
    { id: 5, name: 'First Running Bill', status: 'Completed', date: '2024-03-15', approvedBy: 'AEE/EE' },
    { id: 6, name: 'Second Running Bill', status: 'Completed', date: '2024-05-20', approvedBy: 'AEE/EE' },
    { id: 7, name: 'Third Running Bill', status: 'Completed', date: '2024-08-10', approvedBy: 'AEE/EE' },
    { id: 8, name: 'Fourth Running Bill', status: 'Pending', date: null, approvedBy: null },
    { id: 9, name: 'Final Bill', status: 'Not Started', date: null, approvedBy: null },
    { id: 10, name: 'Project Completion', status: 'Not Started', date: null, approvedBy: null },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed': return 'ri-checkbox-circle-fill text-green-500';
      case 'Pending': return 'ri-time-fill text-yellow-500';
      default: return 'ri-checkbox-blank-circle-line text-gray-300';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-800 dark:text-gray-100">Project Workflows</h4>
      </div>
      <div className="space-y-2">
        {workflows.map((workflow, index) => (
          <div key={workflow.id} className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors">
            <i className={`${getStatusIcon(workflow.status)} text-xl`}></i>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{workflow.name}</p>
              {workflow.approvedBy && (
                <p className="text-xs text-gray-500 dark:text-gray-400">Approved by: {workflow.approvedBy}</p>
              )}
            </div>
            <div className="text-right">
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                workflow.status === 'Completed' ? 'bg-green-100 text-green-700' :
                workflow.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-500'
              }`}>
                {workflow.status}
              </span>
              {workflow.date && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {new Date(workflow.date).toLocaleDateString('en-IN')}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Quality (QC / NCR) tab of the work profile.
 *
 * Quality tests are stored against the CONTRACT (dswms_qlty_tst_dtl_t.cntrct_id),
 * never against the work directly — the work is two hops away via
 * dswms_cntrct_lst_t.wrk_id. That is why a test recorded on the Quality Control
 * screen never appeared anywhere on the work it belongs to: nothing read it back.
 * GET /works/:workId/quality-control has existed all along and had no caller.
 *
 * NCRs are keyed the same way, so both need the work's contract(s) resolved first.
 * The empty state distinguishes the two reasons a tab can be blank — no contract yet
 * (nothing CAN be recorded) versus a contract with no tests (nothing HAS been) —
 * because those call for different actions.
 */
function QualityTab({ workId, workCode }: { workId?: number; workCode?: string }) {
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState<any[]>([]);
  const [ncrs, setNcrs] = useState<any[]>([]);
  const [contracts, setContracts] = useState<any[]>([]);

  const loadQuality = useCallback(async () => {
    if (!workId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Contracts first: NCRs can only be queried per contract, and whether any
      // contract exists is what separates the two empty states below.
      const cntrcts = await contractsApi.getByWork(workId).catch(() => []);
      const cntrctList = Array.isArray(cntrcts) ? cntrcts : [];
      setContracts(cntrctList);

      const [qcRes, ...ncrRes] = await Promise.allSettled([
        qcApi.getByWork(workId),
        ...cntrctList.map((c: any) => ncrApi.getAll({ cntrct_id: c.cntrct_id }, 1, 100)),
      ]);

      setTests(qcRes.status === 'fulfilled' ? (qcRes.value || []) : []);
      setNcrs(
        ncrRes
          .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled')
          .flatMap((r) => r.value?.data || [])
      );
    } catch (e) {
      console.error('Quality tab load error:', e);
    } finally {
      setLoading(false);
    }
  }, [workId]);

  useEffect(() => { loadQuality(); }, [loadQuality]);

  const passed = tests.filter((t) => t.passd_in === true).length;
  const failed = tests.filter((t) => t.passd_in === false).length;
  const pending = tests.filter((t) => t.sts_cd !== 'COMPLETED').length;
  const openNcrs = ncrs.filter((n) => !['CLOSED', 'VERIFIED'].includes(String(n.sts_cd || '').toUpperCase())).length;

  // Pass rate counts only tests that reached a verdict — a scheduled test is not a
  // failure, and counting it as one would understate quality on an active work.
  const verdicts = passed + failed;
  const passRate = verdicts ? Math.round((passed / verdicts) * 100) : null;

  const statusPill = (sts: string) => {
    const map: Record<string, string> = {
      SCHEDULED: 'bg-slate-100 text-slate-600',
      SAMPLED: 'bg-sky-100 text-sky-700',
      TESTING: 'bg-amber-100 text-amber-700',
      COMPLETED: 'bg-emerald-100 text-emerald-700',
    };
    return map[String(sts || '').toUpperCase()] || 'bg-gray-100 text-gray-600';
  };

  const ncrPill = (sts: string) => {
    const map: Record<string, string> = {
      OPEN: 'bg-red-100 text-red-700',
      IN_PROGRESS: 'bg-amber-100 text-amber-700',
      COMPLETED: 'bg-sky-100 text-sky-700',
      VERIFIED: 'bg-violet-100 text-violet-700',
      CLOSED: 'bg-emerald-100 text-emerald-700',
    };
    return map[String(sts || '').toUpperCase()] || 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <StatsSkeleton count={4} />
        <TableSkeleton rows={4} showToolbar={false} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { label: 'Tests', value: tests.length, icon: 'ri-flask-line', cls: 'text-slate-800' },
          { label: 'Passed', value: passed, icon: 'ri-checkbox-circle-line', cls: 'text-emerald-600' },
          { label: 'Failed', value: failed, icon: 'ri-close-circle-line', cls: 'text-red-600' },
          { label: 'Awaiting Result', value: pending, icon: 'ri-time-line', cls: 'text-amber-600' },
          { label: 'Open NCRs', value: openNcrs, icon: 'ri-error-warning-line', cls: 'text-rose-600' },
        ].map((s) => (
          <div key={s.label} className="border border-gray-200 rounded-lg px-3 py-2 bg-white">
            <div className="flex items-center gap-1.5 text-[10px] text-gray-500 mb-0.5">
              <i className={s.icon}></i> {s.label}
            </div>
            <div className={`text-lg font-semibold ${s.cls}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {passRate !== null && (
        <div className="text-[11px] text-gray-500">
          Pass rate <span className="font-semibold text-gray-700">{passRate}%</span> across {verdicts} test
          {verdicts !== 1 ? 's' : ''} with a recorded verdict
          {pending > 0 ? ` · ${pending} still awaiting a result` : ''}
        </div>
      )}

      {/* Quality tests */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <i className="ri-flask-line text-purple-500"></i>
          Quality Tests
          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded">
            {tests.length}
          </span>
          <button
            onClick={loadQuality}
            className="ml-auto text-[11px] text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <i className="ri-refresh-line"></i> Refresh
          </button>
        </h4>

        {tests.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
            <i className="ri-flask-line text-3xl mb-2 block"></i>
            {contracts.length === 0 ? (
              <>
                No contract awarded for {workCode || 'this work'} yet.
                <div className="text-[11px] text-gray-400 mt-1">
                  Quality tests are recorded against the contract, so none can be raised until the work is awarded.
                </div>
              </>
            ) : (
              <>
                No quality tests recorded for this work.
                <div className="text-[11px] text-gray-400 mt-1">
                  Raise one from the Quality Control screen against contract{' '}
                  {contracts.map((c: any) => c.cntrct_cd).filter(Boolean).join(', ')}.
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table data-wms-table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left font-medium px-3 py-2">QC No</th>
                  <th className="text-left font-medium px-3 py-2">Test Type</th>
                  <th className="text-left font-medium px-3 py-2">Test Date</th>
                  <th className="text-left font-medium px-3 py-2">Sample Location</th>
                  <th className="text-left font-medium px-3 py-2">Lab</th>
                  <th className="text-left font-medium px-3 py-2">Specified</th>
                  <th className="text-left font-medium px-3 py-2">Actual</th>
                  <th className="text-left font-medium px-3 py-2">Result</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tests.map((t: any) => (
                  <tr key={t.qlty_tst_id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-[11px] text-purple-700">{t.qlty_tst_cd}</td>
                    <td className="px-3 py-2 text-gray-700">{t.tst_type_nm || t.tst_type_cd || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{t.tst_dt || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{t.smpl_lctn_tx || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{t.lab_nm || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{t.spcfd_val_tx || '—'}</td>
                    <td className="px-3 py-2 text-gray-800 font-medium">{t.rslt_val_tx || '—'}</td>
                    <td className="px-3 py-2">
                      {t.passd_in === true ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">Pass</span>
                      ) : t.passd_in === false ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">Fail</span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusPill(t.sts_cd)}`}>
                        {t.sts_cd || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* NCRs — only worth a section once a contract exists to hang them off */}
      {contracts.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <i className="ri-error-warning-line text-rose-500"></i>
            Non-Conformance Reports
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded">
              {ncrs.length}
            </span>
          </h4>

          {ncrs.length === 0 ? (
            <div className="text-center py-5 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
              <i className="ri-shield-check-line text-2xl mb-1 block text-emerald-400"></i>
              No non-conformances raised against this work.
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table data-wms-table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">NCR No</th>
                    <th className="text-left font-medium px-3 py-2">Reported</th>
                    <th className="text-left font-medium px-3 py-2">Location</th>
                    <th className="text-left font-medium px-3 py-2">Non-Conformance</th>
                    <th className="text-left font-medium px-3 py-2">Severity</th>
                    <th className="text-left font-medium px-3 py-2">Due</th>
                    <th className="text-left font-medium px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {ncrs.map((n: any) => (
                    <tr key={n.ncr_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-[11px] text-rose-700">{n.ncr_cd}</td>
                      <td className="px-3 py-2 text-gray-600">{n.rptd_dt || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{n.lctn_tx || '—'}</td>
                      <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={n.nc_dscn_tx || ''}>
                        {n.nc_dscn_tx || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{n.ncr_svty_nm || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{n.due_dt || '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${ncrPill(n.sts_cd)}`}>
                          {n.sts_cd || '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
